import { EzLogger } from '/public/javascript/common/ezclocker-logger.js';

import {
    EzBadParamException,
    EzBadStateException,
    EzException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzChar,
    EzString,
    EzArray,
    EzFunction,
    EzJson
} from '/ezlibrary/helpers/EzHelpers.js';
import { EzClass } from '/ezlibrary/EzClass.js';
import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';
import { EzTimeZone } from '/ezlibrary/date-time/EzTimeZone.js';
import { EzEmployerOption } from '/ezlibrary/EzEmployerOption.js';
import { EzRegistrationState } from '/ezlibrary/enums/EzRegistrationState.js';
import { EzContextEventName } from '/ezlibrary/EzClockerContext/EzContextEventName.js';
import { EzLocale } from '/ezlibrary/locales/EzLocale.js';
import { EzUx } from '/ezlibrary/ux/EzUx.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Represents a DateTime for ezclocker
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzDateTime, ezDateTime } from '/public/javascript/common/ez-date-time.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     globalThis.ezApi.ezclocker?.[EzDateTime.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     EzClass.ezWaitReady(
 *         EzDateTime.ezEventNames.onReady,
 *         {listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzDateTime extends EzClass {
    /**
     * @static
     * @public @readonly @property
     * Gets the default active timezone value of: 'AUTOMATIC'
     */
    static get DEFAULT_ACTIVE_TIMEZONE() {
        return 'AUTOMATIC';
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Date Formatting Constants
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @static
     * @public @readonly @property
     * Gets the default moment.js ISO date time format string (does not include seconds or milliseconds): 'YYYY-MM-DD'
     */
    static get DEFAULT_ISO_DATE_FORMAT() {
        return 'YYYY-MM-DD';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the moment.js format string: 'YYYY-MM-DD\'T\'HH:mm:ssZ'
     */
    static get DEFAULT_ISO_DATE_TIME_WITH_MIN_SEC_ZONE_FORMAT() {
        return 'YYYY-MM-DD\'T\'HH:mm:ssZ';
    }


    /**
     * @static
     * @public @readonly @property
     * Gets the default moment.js ISO date time format string (does not include seconds or milliseconds): 'YYYY-MM-DDTHH:mmZ'
     */
    static get DEFAULT_ISO_DATE_TIME_FORMAT() {
        return 'YYYY-MM-DDTHH:mmZ';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets date format string: 'MM/DD/YYYY'
     */
    static get DTFORMAT_MM_DD_YYYY() {
        return 'MM/DD/YYYY';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets date format string: 'ddd MM/DD/YYYY'
     */
    static get DTFORMAT_ddd_MM_DD_YYYY() {
        return 'ddd MM/DD/YYYY';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets time format string: 'hh:mm a'
     */
    static get DTFORMAT_hh_mm_a() {
        return 'hh:mm a';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default moment.js ISO date time format string that includes seconds as: 'YYYY-MM-DDTHH:mm:ssZ'
     */
    static get DEFAULT_ISO_DATE_TIME_WITH_SECONDS_FORMAT() {
        return 'YYYY-MM-DDTHH:mm:ssZ';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default moment.js ISO date time format string that includes milliseconds as: 'YYYY-MM-DDTHH:mm:ss.999Z'
     */
    static get DEFAULT_ISO_DATE_TIME_WITH_MILLISECONDS_FORMAT() {
        return 'YYYY-MM-DDTHH:mm:ss.999Z';
    }

    /**
     * @static
     * @public @function
     * Evaluates the provided timeZoneId to try and determine if it is a valid supported time zone id.
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @returns {boolean}
     * True if the provied timeZoneId is considered a valid and supported time zone id. False otherwise.
     */
    static isValidTimeZone(timeZoneId = null) {
        if (!EzString.hasText(timeZoneId)) {
            return false;
        }

        // See if we can parse the timeZoneId into a time zone instance
        try {
            const timeZoneInstance = moment.tz.zone(timeZoneId);

            timeZoneId = EzObject.isValid(timeZoneInstance) && timeZoneId?.toUpperCase() === timeZoneInstance?.name.toUpperCase()
                ? timeZoneInstance.name
                : null;

            if (!EzString.hasText(timeZoneId)) {
                return false;
            }

            // Verify the time zone instance's time zone id is supported by moment:
            return moment().tz(timeZoneId);
        } catch (err) {
            if (!EzString.hasText(err?.message)) {
                err.message = `Unexpected error while getting now for timeZoneId=${timeZoneId}`;
            }

            const em = EzString.em`
                The provided timeZoneId of "${timeZoneId}" is either not a valid time zone id or the time zone id is currently not supported.
                [Error reported: ${err.message}]`;

            if (globalThis?.ezApi?.ezclocker?.ezLogger) {
                globalThis.ezApi.ezclocker.ezLogger.error(em, err);
            } else {
                globalThis?.console?.error(em, err);
            }
        }

        return false;
    }


    /**
     * @static
     * @public @function
     * Determines if the provided timeZoneId is considered a valid date time zone id.
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidTimeZone(timeZoneId)
     */
    static ezIsValidTimeZone = EzDateTime.isValidTimeZone;

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: 8:30 PM
     * @returns {string}
     */
    static get SHORT_TIME_FORMAT() {
        return 'LT';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: 8:30:25 PM
     * @returns {string}
     */
    static get SHORT_TIME_WITH_SECONDS_FORMAT() {
        return 'LTS';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: 09/04/1986
     * @returns {string}
     */
    static get TWO_DIGIT_SHORT_DATE() {
        return EzChar.L;
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: 9/4/1986
     * @returns {string}
     */
    static get SHORT_DATE() {
        return EzChar.l;
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: September 4, 1986
     * @returns {string}
     */
    static get LONG_MONTH_DATE_FORMAT() {
        return 'LL';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: Sep 4, 1986
     * @returns {string}
     */
    static get SHORT_MONTH_DATE_FORMAT() {
        return 'll';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: September 4, 1986 8:30 PM
     * @returns {string}
     */
    static get LONG_MONTH_DATE_SHORT_TIME_FORMAT() {
        return 'LLL';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: Sep 4, 1986 8:30 PM
     * @returns {string}
     */
    static get SHORT_MONTH_DATE_SHORT_TIME_FORMAT() {
        return 'lll';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: Thursday, September 4, 1986 8:30 PM
     * @returns {string}
     */
    static LONG_MONTH_DAY_DATE_SHORT_TIME_FORMAT() {
        return 'LLLL';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: Thu, Sep 4, 1986 8:30 PM
     * @returns {string}
     */
    static SHORT_MONTH_DAY_DATE_SHORT_TIME_FORMAT() {
        return 'llll';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the default display date and time format string for moment.js
     * Format string: 'L LT'
     * Returns in localized format: 09/04/1986 8:30 PM
     * @returns {string}
     */
    static get DEFAULT_DISPLAY_DATE_TIME() {
        return 'L LT';
    }

    /**
     * @public @readonly @property
     * Returns the default display date and time with seconds format string for moment.js
     * Format string: 'L LTS'
     * Returns in localized format: 09/04/1986 8:30:25 PM
     */
    static get DEFAULT_DISPLAY_DATE_TIME_WITH_SECONDS() {
        return 'L LTS';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the default display date format string for moment.js
     * Format string: EzChar.L
     * Returns in localized format: 09/04/1986
     * @returns {string}
     */
    static get DEFAULT_MOMENT_DATE_FORMAT() {
        return EzChar.L;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default momentjs time-stanp display format string.
     * @returns {string}
     */
    static get DEFAULT_MOMENT_TIMESTAMP_DISPLAY_FORMAT() {
        return 'MM/DD/YYYY hh:mm:ss';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the format string for displaying the date as: MM/YYYY
     * @returns {string}
     */
    static get DEFAULT_MONTH_YEAR_DISPLAY_FORMAT() {
        return 'MM/YYYY';
    }

    /**
     * @static
     * @public @readonly @property
     * Default hour and minuit display time format
     * @returns {string}
     */
    static get DEFAULT_HH_MM_DISPLAYFORMAT() {
        return 'hh:mm a';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the default display day and date format string for moment.js Format string: 'dddd L'
     * Returns in localized format: Thursday 09/04/1986
     */
    static get DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT() {
        return 'ddd L';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the default time display without seconds format string for moment.js Format string: 'LT'
     * Returns in localized format: 8:30 PM
     * @returns {string}
     */
    static get DEFAULT_MOMENT_HH_MM_TIME_FORMAT() {
        return 'LT';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default time display format string for moment.js
     * Format string: 'LTS'
     * Result is in localized format: 8:30:25 PM
     * @returns {string}
     */
    static get DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT() {
        return 'LTS';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the REGEX value for validation ISO dates.
     * @returns {regex}
     */
    static get ISO_VALIDATION_REGEX() {
        // Possible Alternative RegEx: /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/
        // eslint-disable-next-line no-useless-escape
        return /^([+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24\:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets date format string: 'MM/DD/YYYY'
     * @returns {string}
     */
    static get DEFAULT_MM_DD_YYYY_FORMAT() {
        return 'MM/DD/YYYY';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default date picker display format string.
     * @returns {string}
     */
    static get DEFAULT_DATE_PICKER_FORMAT() {
        return 'mm/dd/yy';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default date and time display format string for moment.js.
     * @returns {regex}
     */
    static get DEFAULT_MOMENT_DATE_TIME_FORMAT() {
        return `${EzDateTime.DEFAULT_MOMENT_DATE_FORMAT}[ at ]${EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT}`.trim();
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default date and time display format (with seconds) string for moment.js.
     * @returns {regex}
     */
    static get DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT() {
        return `${EzDateTime.DEFAULT_MOMENT_DATE_FORMAT}[ at ]${EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT}`.trim();
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default date picker display format string.
     * @returns {string}
     */
    static get DEFAULT_MONTH_DAY_YEAR_FORMAT() {
        return EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    static #timeZoneIds = {
        // Universal Time Zone
        UTC: 'UTC',
        // Hawaiian Standard Time
        HST: 'HST',
        // US Hawaiian Time (Honolulu)'
        'Pacific_Honolulu': 'Pacific/Honolulu',
        // Alaskan Standard Time
        AKDT: 'AKDT',
        // 'US Alaskan Time (Anchorage)
        'America_Anchorage': 'America/Anchorage',
        // Pacific Daylight Time
        PDT: 'PDT',
        // 'US Pacific Time (Los Ange
        // Pacific Standard Time
        PST: 'PST',
        'America_Los_Angeles': 'America/Los_Angeles',
        // US Mountain Standard Time
        UMST: 'UMST',
        // Mountain Standard Time
        MDT: 'MDT',
        // 'US Mountain Time (Denver)'),
        'America_Denver': 'America/Denver',
        // 'US Mountain Standard Time (Ph
        'America_Phoenix': 'America/Phoenix',
        // Canada Central Standard Time
        CCST: 'CCST',
        // Central Standard Time
        CDT: 'CDT',
        // 'US Central Time (Chicago)'),
        'America_Chicago': 'America/Chicago',
        // Eastern Daylight Time
        EDT: 'EDT',
        // 'US Eastern Time (New York)')
        // US Eastern Standard Time
        UEDT: 'UEDT',
        'America_New_York': 'America/New_York',
        // West Pacific Standard Time
        WPST: 'WPST',
        // 'Chamorro Standard Time (Guam)')
        'Pacific_Guam': 'Pacific/Guam'
    };
    static get timeZoneIds() {
        return this.#timeZoneIds;
    }

    /**
     * @public @function
     * Builds a Time zone info instance from the provided time zone id.
     * @param {string} timeZoneId
     * @returns {object}
     * Example: {
     *      value: `Automatic (${fullZoneId})`,
     *      text: fullZoneId,
     *      zoneId: {
     *          full: fullZoneId,
     *          abbr: abbrZoneId
     *      },
     *      abbr: abbrZoneId,
     *      offset: zoneOffset,
     *      isdst: isDst,
     *      isAutomatic: true,
     *      utc: [
     *          EzDateTime.DEFAULT_ACTIVE_TIMEZONE,
     *          fullZoneId,
     *          abbrZoneId
     *      ]
     *  };
     */
    static buildTimeZoneInfoFromTimeZoneId(timeZoneId, displayNameOverride = null, isAutomatic = false) {
        if (!EzString.hasText(timeZoneId)) {
            throw new EzBadParamException(
                { timeZoneId },
                EzDateTime,
                EzDateTime.buildTimeZoneInfoFromTimeZOneId);
        }

        if (!EzBoolean.isBoolean(isAutomatic)) {
            isAutomatic = false;
        }

        const momentTimeZoneInfo = moment.tz.zone(timeZoneId);

        if (!EzObject.isValid(momentTimeZoneInfo)) {
            throw new EzBadParamException(
                { timeZoneId },
                EzDateTime,
                EzDateTime.buildTimeZoneInfoFromTimeZOneId);
        }

        let fullZoneId = momentTimeZoneInfo.name;

        let abbrZoneId = moment.tz(fullZoneId).format(EzChar.z);

        let zoneOffset = moment.tz(fullZoneId).utcOffset()

        let isDst = moment().tz(fullZoneId).isDST();

        let displayValue;

        let utcArray;

        if (isAutomatic) {
            displayValue = `Automatic (${EzString.stringWithTextOrDefault(displayNameOverride, fullZoneId)})`;

            utcArray = [
                EzDateTime.DEFAULT_ACTIVE_TIMEZONE,
                fullZoneId,
                abbrZoneId
            ];
        } else {
            displayValue = EzString.stringWithTextOrDefault(displayNameOverride, fullZoneId);

            utcArray = [
                fullZoneId,
                abbrZoneId
            ];
        }

        return {
            value: displayValue,
            text: fullZoneId,
            zoneId: {
                full: fullZoneId,
                abbr: abbrZoneId
            },
            abbr: abbrZoneId,
            offset: zoneOffset,
            isdst: isDst,
            isAutomatic: isAutomatic,
            utc: utcArray
        };
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default supported time zones array constant
     * @returns {array}
     */
    static get DEFAULT_SUPPORTED_TIME_ZONES() {
        return [
            EzDateTime.AUTOMATIC_TIME_ZONE_INFO,
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.Pacific_Honolulu, 'US Hawaiian Time (Honolulu)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Anchorage, 'US Alaskan Time (Anchorage)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Los_Angeles, 'US Pacific Time (Los Angeles)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Denver, 'US Mountain Time (Denver)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Phoenix, 'US Mountain Standard Time (Phoenix)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Chicago, 'US Central Time (Chicago)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_New_York, 'US Eastern Time (New York)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.Pacific_Guam, 'Chamorro Standard Time (Guam)')
            // "American/Samoa" (UTC-11)
            // Chamorro Standard Time (UTC+10)
            // Atlantic Standard Time (Puerto Rico) (UTC-4)
            // Hawaii-Aleutian Standard Time (UTC-10)

            /*
            {
                value: 'US Hawaiian Time (Honolulu)',
                abbr: 'HST',
                offset: EzNumber.MINUS_10,
                isdst: false,
                text: '(UTC-10:00) Hawaii',
                utc: [
                    'Pacific/Honolulu'
                ]
            },
            {
                value: 'US Alaskan Time (Anchorage)',
                abbr: 'AKDT',
                offset: EzNumber.MINUS_8,
                isdst: true,
                text: '(UTC-09:00) Alaska',
                utc: [
                    'America/Anchorage'
                ]
            },
            {
                value: 'US Pacific Time (Los Angeles)',
                abbr: 'PDT',
                offset: EzNumber.MINUS_7,
                isdst: true,
                text: '(UTC-07:00) Pacific Time (US & Canada)',
                utc: [
                    'America/Los_Angeles'
                ]
            },
            {
                value: 'US Mountain Time (Denver)',
                abbr: 'MDT',
                offset: EzNumber.MINUS_6,
                isdst: true,
                text: '(UTC-07:00) Mountain Time (US & Canada)',
                utc: [
                    'America/Denver'
                ]
            },
            {
                value: 'US Mountain Standard Time (Phoenix)',
                abbr: 'MDT',
                offset: EzNumber.MINUS_6,
                isdst: true,
                text: '(UTC-07:00) Mountain Standard Time (US & Canada)',
                utc: [
                    'America/Phoenix'
                ]
            },
            {
                value: 'US Central Time (Chicago)',
                abbr: 'CDT',
                offset: EzNumber.MINUS_5,
                isdst: true,
                text: '(UTC-06:00) Central Time (US & Canada)',
                utc: [
                    'America/Chicago'
                ]
            },
            {
                value: 'US Eastern Time (New York)',
                abbr: 'EDT',
                offset: EzNumber.MINUS_4,
                isdst: true,
                text: '(UTC-05:00) Eastern Time (US & Canada)',
                utc: [
                    'America/New_York'
                ]
            },
            {
                value: 'Chamorro Standard Time (Guam)',
                abbr: 'WPST',
                offset: EzNumber.TEN,
                isdst: false,
                text: '(UTC+10:00) Guam, Port Moresby',
                utc: [
                    'Pacific/Guam'
                ]
            }
            */
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of ezClocker supported time zone abbreviations.
     * From timezones.json, object:
     *      {
     *          "value": <string>,
     *          "abbr": <string>, (value stored in this array)
     *          "offset": <number>,
     *          "isdst": <boolean>,
     *          "text": <string>,
     *          "utc": [
     *              <string>,
     *              ...
     *              <string>
     *          ]
     *     }
     * @returns {array}
     */
    static get EZCLOCKER_SUPPORTED_TIMEZONES_ABBR() {
        return [
            // Hawaiian Standard Time
            EzDateTime.timeZoneIds.HST,
            // Alaskan Standard Time
            EzDateTime.timeZoneIds.AKDT,
            // Pacific Daylight Time
            EzDateTime.timeZoneIds.PDT,
            // Pacific Standard Time
            EzDateTime.timeZoneIds.PST,
            // US Mountain Standard Time
            EzDateTime.timeZoneIds.UMST,
            // Mountain Standard Time
            EzDateTime.timeZoneIds.MDT,
            // Central Standard Time
            EzDateTime.timeZoneIds.CDT,
            // Canada Central Standard Time
            EzDateTime.timeZoneIds.CCST,
            // Eastern Daylight Time
            EzDateTime.timeZoneIds.EDT,
            // US Eastern Standard Time
            EzDateTime.timeZoneIds.UEDT,
            // West Pacific Standard Time
            EzDateTime.timeZoneIds.WPST
        ];
    }

    /**
     * @static
     * @public @function
     * Patches an isoDateTime that was URL decoded, replacing the + with a space. Returns the plus to the ISO date.
     * If the provided isoDateTime is undefined or empty, an EzString.EMPTY is returned.
     * Does not validate if the provided isoDateTime is a valid iso date!
     * @param {string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {string}
     */
    static patchBadDecodedIso(isoDateTime = EzString.EMPTY) {
        if (!EzString.hasText(isoDateTime)) {
            return EzString.EMPTY;
        }

        if (!isoDateTime.includes(EzChar.SPACE)) {
            // If no spaces exist, then no patching needed
            return isoDateTime;
        }

        // Plus characters in a iso date time string sometimes get replaced by a space due to HTML URL encoding issues.
        // Replace all spaces with the + character
        return !isoDateTime.includes(EzChar.FORWARD_SLASH) && !isoDateTime.includes(EzChar.BACK_SLASH)
            ? isoDateTime.replaceAll(EzChar.SPACE, EzChar.PLUS)
            : isoDateTime;
    }

    /**@static
     * @private @field
     * Stores the preferred display date time foramt
     * Default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME
     * @type {string}
     */
    static #ezPreferredDisplayDateTimeFormat = EzDateTime.DEFAULT_DISPLAY_DATE_TIME;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred display date time foramt
     * @returns {string}
     */
    static get ezPreferredDisplayDateTimeFormat() {
        return this.#ezPreferredDisplayDateTimeFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred display date time foramt
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME
     */
    static set ezPreferredDisplayDateTimeFormat(preferredDisplayDateTimeFormat = EzDateTime.DEFAULT_DISPLAY_DATE_TIME) {
        this.#ezPreferredDisplayDateTimeFormat = EzString.stringWithLengthOrDefault(
            preferredDisplayDateTimeFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred display date time foramt
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDisplayDateTimeFormat
     */
    get ezPreferredDisplayDateTimeFormat() {
        return EzDateTime.ezPreferredDisplayDateTimeFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred display date time foramt
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDisplayDateTimeFormat
     */
    set ezPreferredDisplayDateTimeFormat(preferredDisplayDateTimeFormat = EzDateTime.DEFAULT_DISPLAY_DATE_TIME) {
        EzDateTime.ezPreferredDisplayDateTimeFormat = preferredDisplayDateTimeFormat;
    }

    /**
     * @private @field
     * Stores the preferred display time format
     * Default: EzDateTime.DEFAULT_HH_MM_DISPLAYFORMAT
     * @type {string}
     */
    static #ezPreferredTimeFormat = EzDateTime.DEFAULT_HH_MM_DISPLAYFORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred date time format string
     * @returns {string}
     */
    static get ezPreferredTimeFormat() {
        return this.#ezPreferredTimeFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date time format string
     * @param {string} ezPreferredTimeFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     */
    static set ezPreferredTimeFormat(ezPreferredTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        this.#ezPreferredTimeFormat = EzString.stringWithLengthOrDefault(
            ezPreferredTimeFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_FORMAT);
    }

    /**
     * @static
     * @private @field
     * Stores
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     * @type {string}
     */
    static #ezPreferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred date time format string
     * @returns {string}
     */
    static get ezPreferredDateFormat() {
        return this.#ezPreferredDateFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date time format string
     * @param {string} preferredDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     */
    static set ezPreferredDateFormat(preferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        this.#ezPreferredDateFormat = EzString.stringWithLengthOrDefault(
            preferredDateFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred date time format string
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    get ezPreferredDateFormat() {
        return EzDateTime.ezPreferredDateFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred date time format string
     * @param {string} preferredDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    set ezPreferredDateFormat(preferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        EzDateTime.ezPreferredDateFormat = preferredDateFormat;
    }
    /**
     * @public @function
     * Sets the preferred display date format for moment objects
     * @param {string} ezPreferredDateFormat
     * Dfeault: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    ezSetPreferredDisplayDateFormat(ezPreferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        EzDateTime.ezPreferredDateFormat = ezPreferredDateFormat;
    }
    /**
     * @public @function
     * Gets the preferred display date format for moment objects
     * Default: 'MM/DD/YYYY'
     * Example: '06/07/2021'
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    ezGetPreferredDisplayDateFormat() {
        return EzDateTime.ezPreferredDateFormat;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred date time format string
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     * @type {string}
     */
    static #ezPreferredDateTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred date time format string
     * @returns {string}
     */
    static get ezPreferredDateTimeFormat() {
        return this.#ezPreferredDateTimeFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date time format string
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     */
    static set ezPreferredDateTimeFormat(preferredDateTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT) {
        this.#ezPreferredDateTimeFormat = EzString.stringWithLengthOrDefault(
            preferredDateTimeFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred date time format string
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateTimeFormat
     */
    get ezPreferredDateTimeFormat() {
        return EzDateTime.ezPreferredDateTimeFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred date time format string
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateTimeFormat
     */
    set ezPreferredDateTimeFormat(preferredDateTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT) {
        EzDateTime.ezPreferredDateTimeFormat = preferredDateTimeFormat;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred date time with seconds format
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT
     * @type {string}
     */
    static #ezPreferredDateTimeWithSecondsFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred date time with seconds format
     * @returns {string}
     */
    static get ezPreferredDateTimeWithSecondsFormat() {
        return this.#ezPreferredDateTimeWithSecondsFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date time with seconds format
     * @param {string} ezPreferredDateTimeWithSecondsFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT
     */
    static set ezPreferredDateTimeWithSecondsFormat(preferredDateTimeWithSecondsFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT) {
        this.#ezPreferredDateTimeWithSecondsFormat = EzString.stringWithLengthOrDefault(
            preferredDateTimeWithSecondsFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred date time with seconds format
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     */
    get ezPreferredDateTimeWithSecondsFormat() {
        return EzDateTime.#ezPreferredDateTimeWithSecondsFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred date time with seconds format
     * @param {string} ezPreferredDateTimeWithSecondsFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT
     */
    set ezPreferredDateTimeWithSecondsFormat(preferredDateTimeWithSecondsFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT) {
        EzDateTime.ezPreferredDateTimeWithSecondsFormat = preferredDateTimeWithSecondsFormat;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred date picker format
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     * @type {string}
     */
    static #ezPreferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred date picker format
     * @returns {string}
     */
    static get ezPreferredDatePickerFormat() {
        return this.#ezPreferredDatePickerFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date picker format
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     */
    static set ezPreferredDatePickerFormat(preferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT) {
        this.#ezPreferredDatePickerFormat = EzString.stringWithLengthOrDefault(
            preferredDatePickerFormat,
            EzDateTime.DEFAULT_DATE_PICKER_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred date picker format
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    get ezPreferredDatePickerFormat() {
        return EzDateTime.ezPreferredDatePickerFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date picker format
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    set ezPreferredDatePickerFormat(preferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT) {
        EzDateTime.ezPreferredDatePickerFormat = preferredDatePickerFormat;
    }
    /**
    * @public @function
    * Gets the preferred date picker display format string.
    * Default: 'mm/dd/yy'
    * Example: '06/07/2021'
    *
    * @returns {string}
    *
    * @deprecated
    * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
    */
    ezGetPreferredDatePickerFormat() {
        return EzDateTime.ezPreferredDatePickerFormat;
    }

    /**
     * @public @function
     * Sets the preferred date picker display format
     * @param {string} preferredDatePickerFormat
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    ezSetPreferredDatePickerFormat(preferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT) {
        EzDateTime.ezPreferredDatePickerFormat = EzString.hasLength(preferredDatePickerFormat)
            ? preferredDatePickerFormat
            : EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred day name and date format
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     * @type {string}
     */
    static #ezPreferredMomentDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred day name and date for
     * @returns {string}
     */
    static get ezPreferredMomentDayNameAndDateFormat() {
        return this.#ezPreferredMomentDayNameAndDateFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred day name and date for
     * @param {string} preferredDayNameAndDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     */
    static set ezPreferredMomentDayNameAndDateFormat(preferredDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT) {
        this.#ezPreferredMomentDayNameAndDateFormat = EzString.stringWithLengthOrDefault(
            preferredDayNameAndDateFormat,
            EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred day name and date for
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    get ezPreferredMomentDayNameAndDateFormat() {
        return EzDateTime.ezPreferredMomentDayNameAndDateFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred day name and date for
     * @param {string} preferredDayNameAndDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    set ezPreferredMomentDayNameAndDateFormat(preferredDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT) {
        EzDateTime.ezPreferredMomentDayNameAndDateFormat = preferredDayNameAndDateFormat;
    }
    /**
     * @public @function
     * Returns the preferred day name plus date format string for moment objects.
     * Default format: 'dddd MM/DD/YYYY'
     * Example: Monday '06/07/2021'
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    ezGetPreferredMomentDayNameAndDateFormat() {
        return EzDateTime.ezPreferredMomentDayNameAndDateFormat;
    }

    /**
     * @public @function
     * Sets the preferred moment Day Name and Date format string.
     * @param {string} preferredDayNameAndDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    ezSetPreferredMomentDayNameAndDateFormat(preferredDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT) {
        EzDateTime.ezPreferredMomentDayNameAndDateFormat = preferredDayNameAndDateFormat;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred locale to use
     * Dfeault: EzLocale.DEFAULT_LOCAL
     * @type {string}
     * A valid enum property value from EzLocale
     */
    static #ezPreferredLocale = EzLocale.DEFAULT_LOCALE;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred locale to use
     * @returns {string}
     * A valid enum property value from EzLocale
     */
    static get ezPreferredLocale() {
        return this.#ezPreferredLocale;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred locale to use
     * @param {string} ezLocale
     * A valid enum property value from EzLocale
     * Default: EzLocale.DEFAULT_LOCALE
     */
    static set ezPreferredLocale(ezLocale = EzLocale.DEFAULT_LOCAL) {
        this.#ezPreferredLocale = EzString.stringWithLengthOrDefault(
            ezLocale,
            EzDateTime.DEFAULT_LOCAL);

        /* TODO: Finish adding locale support
        // Inject the locale into the browser document
        this.#ezInjectMomentLocaleIntoDocument();

        // Set the moment.js locale to the preferred
        moment.locale(this.#ezPreferredLocale);
        */
    }
    /**
     * @public @property @getter
     * Gets the preferred locale to us
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredLocale
     */
    get ezPreferredLocale() {
        return EzDateTime.ezPreferredLocale;
    }
    /**
     * @public @property @setter
     * Sets the preferred locale to us
     * @param {string} ezLocale
     * A valid enum property value from EzLocale
     * Default: EzLocale.DEFAULT_LOCAL
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredLocale
     */
    set ezPreferredLocale(ezLocale = EzLocale.DEFAULT_LOCAL) {
        EzDateTime.ezPreferredLocale = ezLocale;
    }

    /**
     * @static
     * @private @field
     * Stores if the perferred time zone was loaded from options or not
     * Default: false
     * @type {boolean}
     */
    static #ezLoadedPreferredTimeZoneOption = false;
    /**
     * @static
     * @public @property @getter
     * Gets if the perferred time zone was loaded from options or not
     * @returns {boolean}
     */
    static get ezLoadedPreferredTimeZoneOption() {
        return this.#ezLoadedPreferredTimeZoneOption;
    }
    /**
     * @static
     * @public @property @setter
     * Sets if the perferred time zone was loaded from options or not
     * @param {boolean} loadedPreferredTimeZoneOption
     * Default: false
     */
    static set ezLoadedPreferredTimeZoneOption(loadedPreferredTimeZoneOption = false) {
        this.#ezLoadedPreferredTimeZoneOption = EzBoolean.booleanOrFalse(loadedPreferredTimeZoneOption);
    }
    /**
     * @public @property @getter
     * Gets if the perferred time zone was loaded from options or not
     * @returns {boolean}
     * @deprecated
     * Migrate to static property: EzDateTime.ezLoadedPreferredTimeZoneOption
     */
    get ezLoadedPreferredTimeZoneOption() {
        return EzDateTime.ezLoadedPreferredTimeZoneOption;
    }
    /**
     * @public @property @setter
     * Sets if the perferred time zone was loaded from options or not
     * @param {boolean} loadedPreferredTimeZoneOption
     * Default: false
     * @deprecated
     * Migrate to static property: EzDateTime.ezLoadedPreferredTimeZoneOption
     */
    set ezLoadedPreferredTimeZoneOption(loadedPreferredTimeZoneOption = false) {
        EzDateTime.ezLoadedPreferredTimeZoneOption = EzBoolean.booleanOrFalse(loadedPreferredTimeZoneOption);
    }
    /**
     * @static
     * @public @function
     * Sets the active time zone to the active employer's preffered timezone option value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Selected Employer is only available when user is an Employer,
     * a Payroll Manager signing in as a employer, or a manager signing in as a
     * employer.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
    static ezLoadPreferredTimezoneOption(triggerEvents = true) {
        if (EzBoolean.isTrue(EzDateTime.ezLoadedPreferredTimeZoneOption)) {
            EzLogger.info(
                EzString.msg`
                    Skipped loading the preferred time zone option: Option is already loaded.`);

            return;
        }

        if (!globalThis?.ezApi?.ezclocker) {
            EzLogger.warn(
                EzString.em`
                    Unable to load the preferred time zone at this time:
                    Requried dependency globalThis.ezApi.ezclocker is not available yet.`);

            return;
        }

        if (!globalThis.ezApi.ezclocker?.ezClockerContext) {
            EzLogger.warn(
                EzString.em`
                    Unable to load the preferred time zone at this time:
                    Required dependency globalThis.ezApi.ezclocker.ezClockerContext is not available yet.`);

            return;
        }

        let loadedActiveTimeZone;

        if (EzObject.isValid(globalThis.ezApi.ezclocker.ezClockerContext?.ezSelectedEmployerAccount)) {
            // Load from the selected employer account
            loadedActiveTimeZone = globalThis.ezApi.ezclocker.ezClockerContext.ezReadSelectedEmployerAccountOption(
                EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE,
                EzDateTime.DEFAULT_ACTIVE_TIMEZONE);
        } else if (EzObject.isValid(globalThis.ezApi.ezclocker.ezClockerContext?.activeEmployer)) {
            // Load from the active employer account
            loadedActiveTimeZone = globalThis.ezApi.ezclocker.ezClockerContext.ezReadActiveEmployerOption(
                EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE,
                EzDateTime.DEFAULT_ACTIVE_TIMEZONE);
        } else {
            EzLogger.warn(
                EzString.em`
                    Unable to load the preferred time zone at this time due to the following state:
                    The neither the globalThis.ezApi.ezclocker.ezClockerContext.ezSelectedEmployerAccount or the
                    globalThis.ezApi.ezclocker.ezClockerContext?.activeEmployer account is available at this time.`);

            return;
        }

        if (triggerEvents) {
            globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                EzDateTime.ezEventNames.onSupportedTimeZonesReady,
                globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                    EzDateTime.ezApiName,
                    'Supported time zones read.',
                    EzDateTime.ezSupportedTimeZones));
        }

        EzDateTime.ezActiveTimeZoneId = EzDateTime.isValidTimeZone(loadedActiveTimeZone)
            ? loadedActiveTimeZone
            : EzDateTime.DEFAULT_ACTIVE_TIMEZONE;

        EzDateTime.ezLoadedPreferredTimeZoneOption = true;

        if (triggerEvents) {
            globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                EzDateTime.ezEventNames.onActiveTimeZoneLoaded,
                globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                    EzDateTime.ezApiName,
                    'Active time zone loaded from options.',
                    EzDateTime.ezActiveTimeZoneId));

            globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                EzDateTime.ezEventNames.onActiveTimeZoneChanged,
                globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                    EzDateTime.ezApiName,
                    `Set EzDateTime's active time zone id and library moment.js's active time zone to: ${EzDateTime.ezActiveTimeZoneId}`,
                    EzDateTime.ezActiveTimeZoneId));
        }
    }
    /**
     * @public @function
     * Sets the active time zone to the active employer's preffered timezone option value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Selected Employer is only available when user is an Employer,
     * a Payroll Manager signing in as a employer, or a manager signing in as a
     * employer.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @deprecated
     * Migrate to static method: EzDateTime.ezLoadPreferredTimezoneOption
     */
    ezLoadPreferredTimezoneOption() {
        EzDateTime.ezLoadPreferredTimezoneOption();
    }

    /**
     * @static
     * @private @field
     * Stores if the preferred locale option was loaded yet
     * Default: false
     * @type {boolean}
     */
    static #ezLoadedPreferredLocaleOption = false;
    /**
     * @static
     * @public @property @getter
     * Gets if the preferred locale option was loaded yet
     * @returns {boolean}
     */
    static get ezLoadedPreferredLocaleOption() {
        return this.#ezLoadedPreferredLocaleOption;
    }
    /**
     * @static
     * @public @property @setter
     * Sets if the preferred locale option was loaded yet
     * @param {boolean} loadedPreferredLocaleOption
     * Default: false
     */
    static set ezLoadedPreferredLocaleOption(loadedPreferredLocaleOption = false) {
        this.#ezLoadedPreferredLocaleOption = EzBoolean.booleanOrFalse(loadedPreferredLocaleOption);
    }
    /**
     * @public @property @getter
     * Gets if the preferred locale option was loaded yet
     * @returns {boolean}
     * @deprecated
     * Migrate to static property: EzDateTimeZone.ezLoadedPreferredLocaleOption
     */
    get ezLoadedPreferredLocaleOption() {
        return EzDateTime.ezLoadedPreferredLocaleOption;
    }
    /**
     * @public @property @setter
     * Sets if the preferred locale option was loaded yet
     * @param {boolean} loadedPreferredLocaleOption
     * Default: false
     * @deprecated
     * Migrate to static property: EzDateTimeZone.ezLoadedPreferredLocaleOption
     */
    set ezLoadedPreferredLocaleOption(loadedPreferredLocaleOption = false) {
        EzDateTime.ezLoadedPreferredLocaleOption = loadedPreferredLocaleOption;
    }

    /**
     * @static
     * @public @function
     * Guesses the local time zone
     * @returns {string}
     */
    static ezGuessLocalTimeZone() {
        return moment.tz.guess(true);
    }
    /**
     * @public @function
     * Guesses the local time zone
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTimeZone.ezGuessLocalTimeZone
     */
    ezGuessLocalTimeZone = EzDateTime.ezGuessLocalTimeZone;

    /**
     * @static
     * @private @function
     * Sets the default time zone id for the moment.js library.
     * @param {string} momentJsDefaultTimeZone
     * Default: EzDateTime.ezGuessLocalTimeZone()
     * @returns {boolean}
     * True if the moment.js default time zone id was set. False otherwise.
     */
    static #setMomentJsDefaultTimeZone(momentJsDefaultTimeZone = EzDateTime.ezGuessLocalTimeZone()) {
        const activeTimeZoneIdToUse = !EzString.hasLength(momentJsDefaultTimeZone) ||
            EzChar.ZERO === momentJsDefaultTimeZone ||
            EzChar.SPACE === momentJsDefaultTimeZone ||
            EzDateTime.DEFAULT_ACTIVE_TIMEZONE === momentJsDefaultTimeZone
            ? EzDateTime.ezGuessLocalTimeZone()
            : EzDateTime.timeZoneIdOrDefault(
                momentJsDefaultTimeZone,
                EzDateTime.ezGuessLocalTimeZone());

        const FAILED_LOG_MSG = `Failed to set the default time zone id of ${activeTimeZoneIdToUse} for momentJs.`;

        try {
            moment.tz.setDefault(activeTimeZoneIdToUse);
        } catch (err) {
            try {
                EzLogger.warn(
                    EzString.em`
                        ${FAILED_LOG_MSG}
                        Error reported: ${err.message}.
                        Falling back to guessed local time zone from browser.`);

                moment.tz.setDefault(EzDateTime.ezGuessLocalTimeZone());

                EzLogger.info(`Set moment.js library's active time zone to: ${activeTimeZoneIdToUse}`);
                return true;
            } catch (err2) {
                EzLogger.error(
                    EzString.em`
                        ${FAILED_LOG_MSG}
                        Error reported: ${err2.message}.`);
            }
        }

        return false;
    }

    /**
     * @private @field
     * Stores the current active time zone id
     * @returns {string}
     */
    static #ezActiveTimeZoneId = null;
    /**
     * @public @property
     * Gets the current active time zone id
     * @returns {string}
     */
    static get ezActiveTimeZoneId() {
        if (null == this.#ezActiveTimeZoneId) {
            this.#ezActiveTimeZoneId = EzDateTime.ezGuessLocalTimeZone();
        }

        return this.#ezActiveTimeZoneId;
    }
    /**
     * @public @property
     * Sets the current active time zone id
     * @param {string} activeTimeZoneId
     * Default: EzDateTime.ezGuessLocalTimeZone()
     */
    static set ezActiveTimeZoneId(activeTimeZoneId = EzDateTime.ezGuessLocalTimeZone()) {
        const activeTimeZoneIdToUse = !EzString.hasText(activeTimeZoneId) || EzChar.ZERO === activeTimeZoneId
            || EzDateTime.DEFAULT_ACTIVE_TIMEZONE === activeTimeZoneId
            ? EzDateTime.ezGuessLocalTimeZone()
            : EzDateTime.timeZoneIdOrDefault(
                activeTimeZoneId,
                EzDateTime.ezGuessLocalTimeZone());

        if (this.#ezActiveTimeZoneId !== activeTimeZoneIdToUse) {
            this.#ezActiveTimeZoneId = activeTimeZoneIdToUse;

            if (this.#setMomentJsDefaultTimeZone(this.#ezActiveTimeZoneId) && globalThis?.ezApi?.ezclocker?.ezEventEngine) {
                globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                    EzDateTime.ezEventNames.onActiveTimeZoneChanged,
                    globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                        EzDateTime.ezApiName,
                        `Active time zone id changed to ${this.#ezActiveTimeZoneId}.`,
                        this.#ezActiveTimeZoneId));
            }
        }
    }
    /**
     * @public @property
     * Gets the current active time zone id
     * @returns {string}
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    get activeTimeZone() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public @property
     * Sets the current active time zone id
     * @param {string} activeTimeZoneId
     * Default: EzDateTime.ezGuessLocalTimeZone()
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    set activeTimeZone(activeTimeZoneId = EzDateTime.ezGuessLocalTimeZone()) {
        EzDateTime.ezActiveTimeZoneId = activeTimeZoneId;
    }
    /**
     * @public @function
     * Sets the active timezone id
     * @param {string} activeTimeZoneId
     * Default: EzDateTime.ezGuessLocalTimeZone()
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezSetActiveTimezone(activeTimeZoneId = EzDateTime.ezGuessLocalTimeZone()) {
        EzDateTime.ezActiveTimeZoneId = activeTimeZoneId;
    }
    /**
     * @public @function
     * Gets the active timezone for the context
     * @returns {string}
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezGetActiveTimeZone() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public @function
     * Gets the active timezone for the context
     * @returns {string}
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezGetActiveTimeZoneId() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public @function
     * Gets the active timezone for the context
     * @returns {string}
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezGetActiveTimezone() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public @function
     * Preferred method to use to obtain the active time zone id.
     * Gets the active timezone for the context
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezActiveTimeZoneId
     */
    ezGetActiveTimezoneId() {
        return EzDateTime.ezActiveTimeZoneId;
    }

    /**
     * @public @readonly @property
     * Returns the event names triggered by EzDateTime
     * @returns {object}
     * @deprecated
     * Migrate to static property: EzDateTime.ezEventNames property
     */
    get ezEventNames() {
        return EzDateTime.ezEventNames;
    }

    /**
     * @public @readonly @property
     * Returns the browser's default language (locale)
     * @returns {string}
     */
    get ezBrowserLanguage() {
        const lang = navigator.language;

        return EzString.hasLength(lang)
            ? lang
            : EzLocale.DEFAULT_LOCALE;
    }

    /**
     * @public @readonly @property
     * Returns EzLocale based upon the browser default language
     * @returns {string}
     * A valid enum property value from EzLocale
     */
    get ezBrowserLocale() {
        const browserLang = EzDateTime.ezInstance.ezBrowserLanguage;

        return EzLocale.ezLocaleForValue(
            2 <= browserLang.length
                ? browserLang.slice(0, 2).toUpperCase()
                : browserLang.toUpperCase());
    }

    /**
     * @private @field
     * Stores the all the known time zones
     * Default: EzArray.EMPTY
     * @type {array}
     */
    static #ezAllKnownTimeZones = EzArray.EMPTY;
    /**
     * @static
     * @public @property @getter
     * Gets the all the known time zones
     * @returns {array}
     */
    static get ezAllKnownTimeZones() {
        return this.#ezAllKnownTimeZones;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the all the known time zones
     * @param {array} allKnownTimeZones
     * Default: EzArray.EMPTY
     */
    static set ezAllKnownTimeZones(allKnownTimeZones = EzArray.EMPTY) {
        this.#ezAllKnownTimeZones = EzArray.arrayOrEmpty(allKnownTimeZones);
    }
    /**
     * @public @property @getter
     * Gets the all the known time zones
     * @returns {array}
     * @deprecated
     * Migrate to static property: EzDeateTime.ezAllKnownTimeZones
     */
    get ezAllKnownTimeZones() {
        return EzDateTime.ezAllKnownTimeZones;
    }
    /**
     * @public @property @setter
     * Sets the all the known time zones
     * @param {array} allKnownTimeZones
     * Default: EzArray.EMPTY
     * @deprecated
     * Migrate to static property: EzDeateTime.ezAllKnownTimeZones
     */
    set ezAllKnownTimeZones(allKnownTimeZones = EzArray.EMPTY) {
        EzDateTime.ezAllKnownTimeZones = EzArray.arrayOrEmpty(allKnownTimeZones);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the time zone info object for the UTC time zone.
     * @returns {object}
     */
    static get utcTimeZoneInfo() {
        return EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.UTC);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the local zone info object for ezClocker's AUTOMATIC time zone.
     * @returns {object}
     */
    static get AUTOMATIC_TIME_ZONE_INFO() {
        try {
            return EzDateTime.buildTimeZoneInfoFromTimeZoneId(
                moment.tz.guess(true),
                EzString.EMPTY,
                true);
        } catch (err) {
            EzLogger.error(
                EzString.em`
                    Failed to guess the local time zone due to the following error: ${err.message}.
                    Automatic time zone will default to UTC instead.`);
        }

        return EzDateTime.utcTimeZoneInfo;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default supported time zones array constant
     * @returns {array}
     */
    static get DEFAULT_SUPPORTED_TIME_ZONES() {
        return [
            EzDateTime.AUTOMATIC_TIME_ZONE_INFO,
            EzDateTime.buildTimeZoneInfoFromTimeZoneId('Pacific/Honolulu', 'US Hawaiian Time (Honolulu)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId('America/Anchorage', 'US Alaskan Time (Anchorage)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId('America/Los_Angeles', 'US Pacific Time (Los Angeles)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId('America/Denver', 'US Mountain Time (Denver)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId('America/Phoenix', 'US Mountain Standard Time (Phoenix)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId('America/Chicago', 'US Central Time (Chicago)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId('America/New_York', 'US Eastern Time (New York)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId('Pacific/Guam', 'Chamorro Standard Time (Guam)')
            // "American/Samoa" (UTC-11)
            // Chamorro Standard Time (UTC+10)
            // Atlantic Standard Time (Puerto Rico) (UTC-4)
            // Hawaii-Aleutian Standard Time (UTC-10)

            /*
            {
                value: 'US Hawaiian Time (Honolulu)',
                abbr: 'HST',
                offset: EzNumber.MINUS_10,
                isdst: false,
                text: '(UTC-10:00) Hawaii',
                utc: [
                    'Pacific/Honolulu'
                ]
            },
            {
                value: 'US Alaskan Time (Anchorage)',
                abbr: 'AKDT',
                offset: EzNumber.MINUS_8,
                isdst: true,
                text: '(UTC-09:00) Alaska',
                utc: [
                    'America/Anchorage'
                ]
            },
            {
                value: 'US Pacific Time (Los Angeles)',
                abbr: 'PDT',
                offset: EzNumber.MINUS_7,
                isdst: true,
                text: '(UTC-07:00) Pacific Time (US & Canada)',
                utc: [
                    'America/Los_Angeles'
                ]
            },
            {
                value: 'US Mountain Time (Denver)',
                abbr: 'MDT',
                offset: EzNumber.MINUS_6,
                isdst: true,
                text: '(UTC-07:00) Mountain Time (US & Canada)',
                utc: [
                    'America/Denver'
                ]
            },
            {
                value: 'US Mountain Standard Time (Phoenix)',
                abbr: 'MDT',
                offset: EzNumber.MINUS_6,
                isdst: true,
                text: '(UTC-07:00) Mountain Standard Time (US & Canada)',
                utc: [
                    'America/Phoenix'
                ]
            },
            {
                value: 'US Central Time (Chicago)',
                abbr: 'CDT',
                offset: EzNumber.MINUS_5,
                isdst: true,
                text: '(UTC-06:00) Central Time (US & Canada)',
                utc: [
                    'America/Chicago'
                ]
            },
            {
                value: 'Central Standard Time (Mexico)',
                abbr: 'CST',
                offset: -6,
                isdst: false,
                text: '(UTC-06:00) Central Standard Time (Mexico)',
                utc: [
                    'America/Regina'
                ]
            },
            {
                value: 'US Eastern Time (New York)',
                abbr: 'EDT',
                offset: EzNumber.MINUS_4,
                isdst: true,
                text: '(UTC-05:00) Eastern Time (US & Canada)',
                utc: [
                    'America/New_York'
                ]
            },
            {
                value: 'Chamorro Standard Time (Guam)',
                abbr: 'WPST',
                offset: EzNumber.TEN,
                isdst: false,
                text: '(UTC+10:00) Guam, Port Moresby',
                utc: [
                    'Pacific/Guam'
                ]
            }
            */
        ];
    }

    /**
     * @private @field
     * Stores the supported time zones
     * Default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES
     * @type {array}
     */
    static #ezSupportedTimeZones = EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES;
    /**
     * @static
     * @public @property @getter
     * Gets the supported time zones
     * @returns {array}
     */
    static get ezSupportedTimeZones() {
        return this.#ezSupportedTimeZones;
    }
    /**
     * @static
     * @public @property @getter
     * Sets the supported time zones
     * @param {array} supportedTimeZones
     * Default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES
     */
    static set ezSupportedTimeZones(supportedTimeZones = EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES) {
        this.#ezSupportedTimeZones = EzArray.arrayOrDefault(
            supportedTimeZones,
            EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES);
    }
    /**
     * @public @property @getter
     * Gets the supported time zones
     * @returns {array}
     * @deprecated
     * Migrate to static property: EzDateTime.ezSupportedTimeZones
     */
    get ezSupportedTimeZones() {
        return EzDateTime.ezSupportedTimeZones;
    }
    /**
     * @public @property @getter
     * Sets the supported time zones
     * @param {array} supportedTimeZones
     * Default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES
     * @deprecated
     * Migrate to static property: EzDateTime.ezSupportedTimeZones
     */
    set ezSupportedTimeZones(supportedTimeZones = EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES) {
        EzDateTime.ezSupportedTimeZones = EzArray.arrayOrDefault(
            supportedTimeZones,
            EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES);
    }

    /**
     * @static
     * @public @function
     * Gets the time zone info for a supported time zone id. Returns null if the timeZoneId is not supported.
     * @param {string} timeZoneId
     * @param {boolean} [ignoreAutomatic=false]
     *     When true, skips any time zone entry in DEFAULT_SUPPORTED_TIME_ZONES where
     *     isAutomatic === true (i.e. the synthetic "Automatic (detected)" entry built by
     *     buildTimeZoneInfoFromTimeZoneId with isAutomatic=true).
     *     Use this when you need a real, named IANA time zone match and must not resolve
     *     the caller's local/browser-detected zone as a valid result.
     *     When false (default), the Automatic entry is included in the search and can be
     *     returned if its utc array contains the requested timeZoneId or its offset matches.
     * @returns {object}
     */
    static getSupportedTimeZoneInfo(timeZoneId, ignoreAutomatic = false) {
        const tzUpperCase = timeZoneId.toUpperCase();

        for (let supportedTimeZoneInfo of EzDateTime.ezSupportedTimeZones) {
            if(ignoreAutomatic && supportedTimeZoneInfo.isAutomatic) {
                continue;
            }
            if (supportedTimeZoneInfo.zoneId.full.toUpperCase() === tzUpperCase ||
                supportedTimeZoneInfo.zoneId.abbr.toUpperCase() === tzUpperCase) {
                return supportedTimeZoneInfo;
            }

            if (supportedTimeZoneInfo.name === timeZoneId) {
                return supportedTimeZoneInfo;
            }

            for (const tzId of supportedTimeZoneInfo.utc) {
                if (tzId.toUpperCase() === tzUpperCase) {
                    return supportedTimeZoneInfo;
                }
            }

            if(timeZoneId !== EzDateTime.DEFAULT_ACTIVE_TIMEZONE) {
                const timeZoneInfo = EzDateTime.buildTimeZoneInfoFromTimeZoneId(timeZoneId, EzString.EMPTY);
                if(timeZoneInfo.offset === supportedTimeZoneInfo.offset) {
                    return supportedTimeZoneInfo;
                }
            }
        }

        return null;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets if the now date time is within a leap year or not.
     * @returns {boolean}
     */
    static get nowIsLeapYear() {
        return EzDateTime.now(EzDateTime.ezActiveTimeZoneId).isLeapYear();
    }

    /**
     * @static
     * @public @readonly @property
     * Gets if the provided aMoment is within a leap year or not.
     * @param {moment} aMoment
     * @returns {boolean}
     */
    static isLeapYear(aMoment = null) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime,
                `get ${EzDateTime.isLeapYear.toString()}`);
        }

        return aMoment.isLeapYear();
    }

    /**
     * @public @function
     * Initializes EzDateTime
     * @returns {EzDateTime}
     */
    ezInit() {
        EzDateTime.ezInstance.ezRegisterEvents();

        EzDateTime.ezInstance.ezRegisterWantEvents();

        return EzDateTime.ezInstance;
    }

    /**
     * @public @function
     * Registers events triggered by this class
     */
    ezRegisterEvents() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzDateTime.ezApiName,
            EzDateTime.ezEventNames.onActiveTimeZoneChanged);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzDateTime.ezApiName,
            EzDateTime.ezEventNames.onActiveTimeZoneLoaded);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzDateTime.ezApiName,
            EzDateTime.ezEventNames.onSupportedTimeZonesReady);
    }

    /**
     * @public @function
     * Registers want events for initialization
     */
    ezRegisterWantEvents() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzDateTime.ezEventNames.onActiveTimeZoneChanged,
            EzDateTime.ezApiName,
            EzDateTime.ezInstance.ezDisplayActiveTimezone);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzContextEventName.onSelectedEmployerAccountReady,
            EzDateTime.ezApiName,
            EzDateTime.ezInstance.ezHandleOnSelectedEmployerReady,
            true);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzContextEventName.onUserContextReady,
            EzDateTime.ezApiName,
            EzDateTime.ezInstance.ezHandleOnUserContextReady,
            true);
    }

    /**
     * @public @function
     * Loads the authenticated users's preferred locale option
     */
    ezLoadAuthenticatedUserPreferredLocaleOption() {
        if (EzBoolean.isTrue(EzDateTime.ezLoadedPreferredLocaleOption)) {
            // Already loaded
            return;
        }

        // Sets the locale from the browser's navigator.language property
        EzDateTime.ezPreferredLocale = EzDateTime.ezInstance.ezBrowserLocale;

        if (EzBoolean.isTrue(globalThis.ezApi.ezclocker['ezClockerContext']?.ready) &&
            EzBoolean.isTrue(globalThis.ezApi.ezclocker.ezClockerContext.ezGetUserContext().ready)) {

            // TODO: Finishe locale support: Add user level options and ready the preferred locale value

            EzDateTime.ezLoadedPreferredLocaleOption = true;
        }
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default option object for the JQuery Date Picker Input
     * @returns {Object}
     */
    static get defaultDatePickerOptions() {
        return {
            dateFormat: globalThis.ezApi.ezclocker.ezDateTime.ezGetPreferredDatePickerFormat(),
            defaultDate: globalThis.ezApi.ezclocker.ezDateTime.ezToJsDate(EzDateTime.nowWithStartOfDay()),
            constrainInput: false,
            changeMonth: true,
            changeYear: true
        };
    }

    /**
     * @public @function
     * Returns the default option object for the JQuery Date Picker Input
     * @returns {Object}
     * @deprecated
     * Migrate to static property: EzDateTime.defaultDatePickerOptions
     */
    ezGetDefaultDatePickerOptions() {
        return EzDateTime.defaultDatePickerOptions;
    }

    /**
     * @public @function
     * Applies the jQuery DatePicker initialization to the provided elementId.
     * Component details: https://jqueryui.com/datepicker/
     * @param {string} dateTimePickerId
     * @param {Object|null} eventHandlers
     * @param {Object|null} propertyOverrides
     * @param {moment|null} aInitialMoment
     * @returns {Object}
     * Returns the jQuery reference to the datepicker.
     * @deprecated
     * Migrate to static method: EzDateTime.initDatePicker(dateTimePickerId, eventHandlers, propertyOverrides, aInitialMoment)
     */
    ezInitDateTimePicker(dateTimePickerId, eventHandlers, propertyOverrides, aInitialMoment) {
        return EzDateTime.initDatePicker(
            dateTimePickerId,
            eventHandlers,
            propertyOverrides,
            aInitialMoment);
    }

    /**
     * @static`
     * @public @function
     * Applies the jQuery DatePicker initialization to the provided elementId.
     * Component details: https://jqueryui.com/datepicker/
     * @param {string} datePickerId
     * @param {undefined|null|object} eventHandlers
     * Default: null
     * @param {undefined|null|object} propertyOverrides
     * Default: null
     * @param {undefined|null|moment} aInitialMoment
     * Default: null
     * @param {undefined|null|boolean} isEndPeriod
     * Default: false
     * @returns {object}
     * Returns the jQuery reference to the datepicker.
     */
    static initDatePicker(datePickerId, eventHandlers = null, propertyOverrides = null, aInitialMoment = null, isEndPeriod = false) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.initDatePicker);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            throw new EzBadStateException(
                `Expected HTML element id="${datePickerId}" to reference a valid date picker HTML input element.`,
                `Date picker HTML input element with id="${datePickerId}" does not exist.`,
                EzDateTime,
                EzDateTime.initDatePicker,
                `Failed to initialize a date picker HTML input element with id="${datePickerId}".`);
        }

        const datePickerOptions = EzDateTime.defaultDatePickerOptions;

        if (EzObject.isValid(eventHandlers)) {
            for (const eHandlers in eventHandlers) {
                if (EzFunction.isFunction(eventHandlers[eHandlers])) {
                    datePickerOptions[eHandlers] = eventHandlers[eHandlers];
                }
            }
        }

        if (EzObject.isValid(propertyOverrides)) {
            for (const pOverrides in propertyOverrides) {
                datePickerOptions[pOverrides] = propertyOverrides[pOverrides];
            }
        }

        // Initialize the datepicker with the options.
        const updateJQueryDatePicker = datePickerHTMLElement.datepicker(datePickerOptions);

        // Assign the initial value (if any)
        aInitialMoment = EzDateTime.isValidMoment(aInitialMoment)
            ? aInitialMoment
            : null;

        if (EzBoolean.isTrue(isEndPeriod)) {
            EzDateTime.setDatePickerValueEndOfDay(
                datePickerId,
                aInitialMoment);
        } else {
            EzDateTime.setDatePickerValueStartOfDay(
                datePickerId,
                aInitialMoment);
        }

        return updateJQueryDatePicker;
    }

    /**
     * @static`
     * @public @function
     * Applies the jQuery DatePicker initialization to the provided elementId.
     * Component details: https://jqueryui.com/datepicker/
     * @param {string} datePickerId
     * @param {undefined|null|object} eventHandlers
     * Default: null
     * @param {undefined|null|object} propertyOverrides
     * Default: null
     * @param {undefined|null|moment} aInitialMoment
     * Default: null
     * @param {undefined|null|boolean} isEndPeriod
     * Default: false
     * @returns {object}
     * Returns the jQuery reference to the datepicker.
     * @deprecated
     * Migrate to static method: EzDateTime.initDatePicker(datePickerId, eventHandlers, propertyOverrides, aInitialMoment, isEndPeriod)
     */
    ezInitDatePicker = EzDateTime.initDatePicker;

    /**
     * @public @function
     * Creates a moment instance from the provided iso8601DateTime value
     * @param {undefined|null|string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.fromIso(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIso(isoDateTime)
     */
    ezFromIso8601(isoDateTime = EzString.EMPTY) {
        return EzDateTime.fromIso(isoDateTime);
    }

    /**
     * @public @function
     * Creates a new moment with the provided ISO string.
     * If the iso string is empty, null is returned. If the iso string is not valid, ezNowWithStartOfDay is returned.
     * @param {string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.fromIso(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIso(isoDateTime)
     */
    ezCreateFromIso = EzDateTime.fromIso;

    /**
     * @public @function
     * Creates a new moment with the provided ISO string.
     * If the provided isoString is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {moment|string}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.ezInstance.ezFromIsoOrEmpty(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIsoOrEmpty(isoDateTime)
     */
    ezCreateFromIsoOrEmpty(isoDateTime = EzString.EMPTY) {
        return EzDateTime.ezInstance.ezFromIsoOrEmpty(isoDateTime);
    }

    /**
     * @public @function
     * Creates a new moment with the provided ISO string.
     * If the provided isoString is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * @returns {moment|string}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.fromIsoOrNull(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIsoOrNull(isoDateTime)
     */
    ezCreateFromIsoOrNull(isoDateTime) {
        return EzDateTime.ezInstance.ezFromIsoOrEmpty(isoDateTime);
    }

    /**
     * @public @function
     * Creates a moment instance from the provided iso string OR returns the provided defaultMoment if the iso string is not valid.
     * @param {string} isoDateTime
     * @param {moment} defaultMoment
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.ezInstance.ezFromIsoOrDefault(isoDateTime, defaultMoment)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIsoOrDefault(isoDateTime, defaultMoment)
     */
    ezCreateFromIsoOrDefaultMoment(isoDateTime, defaultMoment) {
        return EzDateTime.ezInstance.ezFromIsoOrDefault(
            isoDateTime,
            defaultMoment);
    }

    /**
     * TODO: Finish locale work
     * @static
     * @public @function
     * Creates a moment instance from the provided iso8601DateTime value.
     * Sets the timezone to the preferred time zone.
     * @param {undefined|null|string} isoDateTime
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Default: EzDateTime.ezPreferredLocale
     * @returns {moment}
     */
    static fromIso(isoDateTime = null, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezPreferredLocale*/) {
        return EzDateTime.fromIsoOrDefault(
            isoDateTime,
            timeZoneId,
            EzDateTime.now(timeZoneId));
    }

    /**
     * @static
     * @public @function
     * Creates a moment instance from the provided iso8601DateTime value.
     * Sets the timezone to the preferred time zone.
     * @param {undefined|null|string} isoDateTime
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Default: EzDateTime.ezPreferredLocale
     * @returns {moment}
     * @deprecated
     * Migrate to: EzDateTime.fromIso(isoDateTime, timeZoneId, ezLocale)
     */
    ezFromIso = EzDateTime.fromIso;

    /**
     * @static
     * @public @function
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {string|moment}
     */
    static fromIsoOrEmpty(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        return EzDateTime.fromIsoOrDefault(
            isoDateTime,
            timeZoneId,
            EzString.EMPTY);
    }

    /**
     * @public @function
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {string|moment}
     * @deprecated
     * Migrate to static method: EzDateTime.fromIsoOrEmpty(isoDateTime, timeZoneId, ezLocale)
     */
    ezFromIsoOrEmpty = EzDateTime.fromIsoOrEmpty;

    /**
     * TODO: Finish locale work
     * @public @function
     * Creates a moment instance from the provided isoDateTime value
     * @param {undefiend|null|string} isoDateTime
     * Default: null
     * @param {undefiend|null|any} defaultResult
     * IMPORTANT:
     * 1) The defaultResult can be anything, does not necessarly need to be a valid date time instance.
     * 2) Caller has responsibility to make sure the defaultResult is something they can handle properly.
     * 3) If the defaultResult is a valid date time (moment) instance the time zone IS NOT set to the provided
     * timeZoneId (if any). It is returned as it was provided.
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment|any}
     */
    static fromIsoOrDefault(isoDateTime = null, defaultMoment = null, timeZoneId = EzDateTime.ezActiveTimeZoneId
        /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        if (!EzString.hasText(isoDateTime)) {
            return defaultMoment;
        }

        isoDateTime = EzDateTime.patchBadDecodedIso(isoDateTime);

        if (!EzDateTime.validateIso(isoDateTime)) {
            return defaultMoment;
        }

        /* TODO: Finish locale work
        if (!EzString.hasLength(ezLocale)) {
            ezLocale = EzDateTime.ezInstance.ezPreferredLocale;
        }

        let momentInstance = moment(isoString).locale(ezLocale);
        */

        try {
            const momentInstance = moment(isoDateTime).tz(EzDateTime.safeTimeZoneId(timeZoneId));

            return EzDateTime.isValidMoment(momentInstance)
                ? momentInstance
                : defaultMoment;
        } catch (err) {
            if (!EzString.hasText(err.message)) {
                err.message = EzString.em`
                    Unexpected exception when attempting to create a date time (moment) instance
                    from the provided isoDateTime of ${isoDateTime} with time zone id ${timeZoneId}`;
            }

            const em = EzString.em`
                Failed to transform the provided isoDateTime of '${isoDateTime}' to a moment instance
                due to the following error: ${err.message}`;

            if (globalThis?.ezApi?.ezclocker?.ezLogger) {
                globalThis.ezApi.ezclocker.ezLogger.error(em, err);
            } else {
                globalThis?.console?.error(em, err);
            }
        }

        return defaultMoment;
    }

    /**
     * @public @function
     * Creates a moment instance from the provided isoDateTime value
     * @param {string} isoDateTime
     * @param {any} defaultMoment
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.fromIsoOrDefault(isoDateTime, defaultMoment, timeZoneId, ezLocale)
     */
    ezFromIsoOrDefault = EzDateTime.fromIsoOrDefault;

    /**
     * TODO: Finish locale work
     * @public @function
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @returns {null|moment}
     */
    ezTimeStampFromIsoOrNull(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        if (!EzString.hasText(isoDateTime)) {
            return null;
        }

        isoDateTime = EzDateTime.patchBadDecodedIso(isoDateTime);

        if (!EzDateTime.validateIso(isoDateTime)) {
            return null;
        }

        try {
            const momentInstance = moment(isoDateTime)
                .tz(EzDateTime.safeTimeZoneId(timeZoneId));

            return EzDateTime.isValidMoment(momentInstance)
                ? momentInstance
                : null;
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided isoDateTime of '${isoDateTime}' to a moment instance
                    due to the following error: ${err.message}`);

            return null;
        }
    }

    /**
     * TODO: Finish locale work
     * @public @function
     * Creates a moment instance from the provided isoDateTime value
     * @param {string} isoDateTime
     * @param {any} defaultMoment
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     */
    ezTimeStampFromIsoOrDefault(isoDateTime, defaultMoment, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        const momentFromIso = EzDateTime.ezInstance.ezTimeStampFromIsoOrNull(
            isoDateTime,
            timeZoneId
            /*, ezLocale */);

        return null != momentFromIso
            ? momentFromIso
            : defaultMoment;
    }

    /**
     * @static
     * TODO: Finish locale work
     * @public @function
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the input TZ and if absent, preferred time zone.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     */
    static fromIsoWithTimeZoneId(isoDateTime = null, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        const momentFromIso = EzDateTime.fromIsoOrNull(
            isoDateTime,
            timeZoneId
            /*, ezLocale */);

        if (!EzDateTime.isValidMoment(momentFromIso)) {
            throw new EzBadParamException(
                'isoDateTime',
                EzDateTime,
                EzDateTime.fromIsoWithTimeZoneId);
        }

        return momentFromIso;
    }

    /**
     * @public @function
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the input TZ and if absent, preferred time zone.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     */
    ezFromIsoAndTimeZone = EzDateTime.fromIsoWithTimeZoneId;

    /**
     * TODO: Finish locale work
     * @static
     * @public @function
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * @param {undefined|null|string} isoDateTime
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezPreferredLocale
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @returns {null|moment}
     */
    static fromIsoOrNull(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezPreferredLocale*/) {
        // TODO: Finish locale work
        return EzDateTime.fromIsoOrDefault(
            isoDateTime,
            null,
            timeZoneId,
            /* ezLocale */);
    }

    ezFromIsoOrNull = EzDateTime.fromIsoOrNull;

    /**
     * @public @function
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     * @deprecated
     * Migrate to static method: EzDateTime.validateIso(proposedIsoDateTimeValue)
     */
    ezValidateIsIso8601(proposedValue = EzString.EMPTY) {
        return EzDateTime.validateIso(proposedValue);
    }

    /**
     * @public @function
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     * @deprecated
     * Migrate to static method: EzDateTime.validateIso(proposedIsoDateTimeValue)
     */
    ezValidateIsoString(proposedIsoDateTimeValue = EzString.EMPTY) {
        return EzDateTime.validateIso(proposedIsoDateTimeValue);
    }

    /**
     * @static
     * @public @function
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     */
    static validateIso(proposedIsoDateTimeValue = EzString.EMPTY) {
        /**
         * @function
         * Builds the validation failed result.
         * @returns {object}
         */
        const ezBuildValidationFailedResult = () => {
            // Proposed value considered a valid ISO 8601 value
            return {
                proposedIsoDateTimeValue,
                proposedValue: proposedIsoDateTimeValue,
                valid: false,
                iso8601Value: null,
                message: `The proposed value of ${proposedIsoDateTimeValue} is not a valid ISO 8601 value.`
            };
        };

        if (!EzString.hasText(proposedIsoDateTimeValue)) {
            return ezBuildValidationFailedResult();
        }

        if (!EzDateTime.ISO_VALIDATION_REGEX.test(proposedIsoDateTimeValue)) {
            // If the regex fails, fall back to creating a Javascript Date from the proposed value.
            try {
                // See if Javascript's Date method will parse the value:
                const jsDate = new Date(proposedIsoDateTimeValue);

                if (!EzObject.isValid(jsDate) || 'Invalid Date' === jsDate.toString()) {
                    // All validation checks failed
                    return ezBuildValidationFailedResult();
                }
            } catch (err) {
                EzLogger.debug(
                    EzString.em`
                        Failed to create a new Javascript Date instance from the proposedValue ${proposedIsoDateTimeValue}.
                        Error reported: ${err.message}`,
                    err);

                // All validation checks failed
                return ezBuildValidationFailedResult();
            }
        }

        // Proposed value considered a valid ISO 8601 value
        return {
            proposedIsoDateTimeValue,
            proposedValue: proposedIsoDateTimeValue,
            valid: true,
            iso8601Value: proposedIsoDateTimeValue,
            message: `The proposed value of ${proposedIsoDateTimeValue} is a valid ISO 8601 value.`
        };
    }

    /**
     * @public @function
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     * @deprecated
     * Migrate to static method: EzDateTime.validateIso(proposedIsoDateTimeValue)
     */
    ezValidateIso = EzDateTime.validateIso;

    /**
     * @static
     * @public @function
     * Determines if the provided isoDateTime is a valid ISO date time string.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: null
     * @returns {boolean}
     */
    static isValidIso(proposedIsoDateTimeValue = null) {
        return EzBoolean.isTrue(EzDateTime.validateIso(proposedIsoDateTimeValue).valid);
    }

    /**
     * @static
     * @public @function
     * Determines if the provided isoDateTime is a valid ISO date time string.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidIso
     */
    ezIsValidIso = EzDateTime.isValidIso;

    /**
     * @static
     * @public @function
     * Appends the time from the time picker to the provided moment instance.
     * @param {string} timePickerId
     * @param {moment} momentInstance
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static appendTimeFromTimePicker(timePickerId, momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime,
                EzDateTime.appendTimeFromTimePicker);
        }
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.appendTimeFromTimePicker);
        }

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        const timePickerElement = EzUx.$id(timePickerId);

        const defaultResponse = EzDateTime.withZone(
            momentInstance,
            timeZoneId);

        if (!EzObject.isValid(timePickerElement) || EzNumber.ZERO === timePickerElement?.length) {
            EzLogger.error(
                EzString.msg`
                    Failed to locate a time picker input HTML element with id="${timePickerId}".
                    Falling back to returning the default response (${EzDateTime.toIso(momentInstance)}) in time zone ${timeZoneId}.`);

            return defaultResponse;
        }

        const hour = timePickerElement?.timepicker('getHour');

        const min = timePickerElement?.timepicker('getMinute');

        if (!EzObject.isValid(hour) || !EzObject.isValid(min)) {
            EzLogger.error(
                EzString.msg`
                    Failed to obtain the time picker input HTML element with id="${timePickerId}"'s hour and/or minute values.
                    Falling back to returning the default response (${EzDateTime.toIso(momentInstance)}) in time zone ${timeZoneId}.`);

            return defaultResponse;
        }

        return EzDateTime.withZone(momentInstance, timeZoneId)
            .hours(hour)
            .minutes(min)
            .seconds(0)
            .milliseconds(0);
    }

    /**
     * @public @function
     * Appends the time from the time picker to the provided moment instance.
     * @param {string} timePickerId
     * @param {moment} momentInstance
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.appendTimeFromTimePicker(timePickerId, momentInstance, timeZoneId)
     */
    ezAppendTimeFromTimePicker = EzDateTime.appendTimeFromTimePicker;

    /**
     * @public @readonly @property
     * Returns the defaul options for time picker initialization.
     * @returns {object}
     * Result instance:
     *  {
     *      timeFormat: 'hh:mm tt',
     *      showPeriod: true,
     *      showLeadingZero: true
     *  }
     */
    static get defaultTimePickerOptions() {
        return {
            timeFormat: 'hh:mm tt',
            showPeriod: true,
            showLeadingZero: true
        };
    }

    /**
     * @public @function
     * Returns the defaul options for time picker initialization.
     * @returns {Object}
     * @deprecated
     * Migrate to static property: EzDateTime.defaultTimePickerOptions
     */
    ezGetDefaultTimePickerOptions() {
        return EzDateTime.defaultTimePickerOptions;
    }

    /**
     * @static
     * @public @function
     * Initializes a UX time picker with defaults. Applies any additional options or overrides to the default
     * options provided in timePickerOptionsOverrides.
     * @param {string} [timePickerId=EzString.EMPTY]
     * @param {undefiend|null|moment} [initialMoment=null]
     * Default: EzDateTime.now()
     * The provided momentInstance is converted to the active time zone before setting the time picker's values.
     * @param {undefined|null|object} [timePickerOptionsOverride=null]
     * Default: null
     * @returns {object}
     * Jquery referece to the time picker
     */
    static initTimePicker(timePickerId = EzString.EMPTY, initialMoment = null, timePickerOptionsOverrides = null) {
        if (!EzString.hasText(timePickerId)) {
            throw new EzBadParamException(
                { timePickerId },
                EzDateTime,
                EzDateTime.initTimePicker);
        }

        const timePickerElement = EzUx.$id(timePickerId);

        if (!EzObject.isValid(timePickerElement)) {
            throw new EzBadStateException(
                EzString.em`
                    Expected the provided timePickerId of "${timePickerId}" to be a valid HTML element id or jQuery selector
                    that is associated with a valid HTML element.`,
                EzString.em`
                    However, the provided timePickerId of ${timePickerId} is either
                    a valid HTML element or jQuery selector but not associated with a valid HTML elementnot
                    or is not valid HTML element id or jQuery selector.`,
                EzDateTime,
                EzDateTime.initTimePicker);
        }

        const timePickerOptions = EzDateTime.defaultTimePickerOptions;

        if (EzObject.isValid(timePickerOptionsOverrides)) {
            for (const prop in timePickerOptionsOverrides) {
                if (EzString.hasText(prop) && EzObject.isValid(timePickerOptionsOverrides?.[prop])) {
                    timePickerOptions[prop] = timePickerOptionsOverrides[prop];
                }
            }
        }

        // Apply the time picker options
        timePickerElement.timepicker(timePickerOptions);

        EzDateTime.setTimePickerValue(
            timePickerId,
            EzDateTime.dateTimeOrNow(initialMoment));

        return timePickerElement;
    }

    /**
     * @public @function
     * Initializes a UX time picker with defaults. Applies any additional options or overrides to the default
     * options provided in timePickerOptionsOverrides.
     * @param {string} timePickerId
     * @param {undefiend|null|moment} initialMoment
     * Default: EzDateTime.now()
     * @param {undefined|null|object} timePickerOptionsOverrides
     * Default: null
     * @returns {object}
     * Jquery referece to the time picker
     * @deprecated
     * Migrate to static method: EzDateTime.initTimePicker(timePickerId, initialMoment, timePickerOptionsOverrides)
     */
    ezInitTimePicker = EzDateTime.initTimePicker;

    /**
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     *
     * @static
     * @public @function
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * @param {string} datePickerId
     * @param {undefiend|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 2) (preferred) EzDateTime.fromDatePickerEndOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 3) (preferred) EzDateTime.fromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull)
     */
    static dateFromDatePicker(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.fromDatePickerStartOfDay(
            datePickerId,
            false,
            timeZoneId);
    }

    /**
     * @public @function
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * @param {string} datePickerId
     * @param {undefiend|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 2) (preferred) EzDateTime.fromDatePickerEndOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 3) (preferred) EzDateTime.fromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull)
     * 4) (deprecated) EzDateTime.dateFromDatePicker(datePickerId, timeZoneId)
     */
    ezDateFromDatePicker = EzDateTime.dateFromDatePicker;

    /**
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     *
     * @static
     * @public @function
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * The time is set to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * @param {string} datePickerId
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 1) (preferred) EzDateTime.fromDatePickerEndOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 4) (deprecated) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull, timeZoneId)
     * 5) (deprecated) EzDateTime.ezInstance.ezDateTimeFromDatePickerWithTimeAtStartOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    static momentFromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.ezInstance.fromDatePickerStartOfDay(
            datePickerId,
            EzBoolean.booleanOrOrFalse(returnNullInsteadOfTodayIfNull),
            EzDateTime.safeTimeZoneId(timeZoneId));
    }

    /**
     * @public @function
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * The time is set to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * @param {string} datePickerId
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     * 2) (deprecated) EzDateTime.momentFromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    ezMomentFromDatePicker = EzDateTime.fromDatePickerStartOfDay;

    /**
     * @static
     * @public @function
     * Creates a moment object from a date picker value with the specified timezone
     * @param {string} datePickerId - The ID of the date picker element
     * @param {string} [timeZoneId=EzDateTime.ezActiveTimeZoneId] - The timezone ID to use
     * @returns {moment.Moment} A moment object in the specified timezone
     * @deprecated
     * Migrate to one the following static method:
     * EzDateTime.fromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    static momentFromDatePickerWithTimeZone(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { datePickerId },
                this.ezInstance,
                this.momentFromDatePickerWithTimeZone);
        }

        return EzDateTime.fromDatePicker(
            datePickerId,
            true,
            timeZoneId);

        /* Original Implementation
         * return moment(EzUx.getInputValue(datePickerId)).tz(timeZoneId);
         */
    }

    /**
     * @static
     * @public @function
     * Creates a moment object from a date picker value with the specified timezone
     * @param {string} datePickerId - The ID of the date picker element
     * @param {string} [timeZoneId=EzDateTime.ezActiveTimeZoneId] - The timezone ID to use
     * @returns {moment.Moment} A moment object in the specified timezone
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     * 3) (deprecated) EzDateTime.momentFromDatePickerWithTimeZone(datePickerId, timeZoneId)
     */
    ezMomentFromDatePickerWithTimeZone = EzDateTime.momentFromDatePickerWithTimeZone;

    /**
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     *
     * @static
     * @public @function
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * @param {undefined|null|string} datePickerId
     * Default: null
     * @param {undefined|null|boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {null|moment}
     */
    static fromDatePicker(datePickerId = null, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.fromDatePicker);
        }

        const defaultMoment = EzBoolean.booleanOrFalse(returnNullInsteadOfTodayIfNull)
            ? EzDateTime.nowWithStartOfDay(timeZoneId)
            : null;

        const emDefaultResponseInfo = null == defaultMoment
            ? 'Will instead return the default response of null instead.'
            : `Will instead return the provided default date time of "${EzDateTime.nowWithStartOfDay(timeZoneId)}" instead.`;

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        const datePickerElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerElement?.[EzNumber.FIRST_INDEX])) {
            // Did not find a date picker input element for the provided id
            EzLogger.warn(
                EzString.em`
                    Failed to obtain the date and time value from a date picker input HTML element due to the following error:
                    No date picker input element is associated with the HTML element id="${datePickerId}.
                    ${emDefaultResponseInfo}`);

            return defaultMoment;
        }

        // Obtain the jsDate from the jQuery date picker
        const jsDateFromDatePicker = datePickerElement.datepicker('getDate');

        if (!EzObject.isValid(jsDateFromDatePicker)) {
            EzLogger.warn(
                EzString.em`
                    Failed to obtain a Javascript date instance from the jQuery date picker element with id="${datePickerId}"
                    due to the following error: call to datepicker('getdate') returned an undefined or null date instance.
                    ${emDefaultResponseInfo}`);

            return defaultMoment;
        }

        /*
         * TODO: Support locale date formats. Currently, the date picker input's value is expected to be in the format of MM/DD/YYYY since that's the format
         * that EzClocker sets on the date picker inputs. If the date pickers are configured to use a different date format, then this will need to be updated
         * to support that.
         */

        // Note: Since the date picker always stores the selected date in a Javascript Date instance AND the Javascript Date instance will only ever have the
        // date time in the browser's default time zone if we use the Javascript Date and change the time zone to a different configured time zone the actual
        // date will incorrectly change. We need the date in the picker but in the configured time zone, so the moment is created from the date pickers
        // raw input value.
        return moment.tz(
            datePickerElement?.[EzNumber.FIRST_INDEX]?.value || EzDateTime.safeGetInputValue(datePickerId),
            EzDateTime.DTFORMAT_MM_DD_YYYY,
            EzDateTime.safeTimeZoneId(timeZoneId));
    }

    /**
     * @static
     * @public @function
     * Gets the value of an HTML input associated with the provied inputElementId.
     * Uses EzUI or EzUx to obtain the value of the input element (which evern is available).
     * If the input element does not exist or the provided inputElementId is not valid, then the defaultValue is returned (default is empty string).
     * @param {string} inputElementId
     * The id of the input element.
     * @param {string} [defaultValue=EzString.EMPTY]
     * The default value to return if the input element is not found or invalid.
     * @returns {string} The value of the input element or the default value.
     */
    static safeGetInputValue(inputElementId, defaultValue = EzString.EMPTY) {
        defaultValue = EzObject.assignOrDefault(
            defaultValue,
            EzString.EMPTY);

        if (!EzString.hasText(inputElementId)) {
            globalThis?.console?.error(
                EzString.em`
                    A valid, non-null element id is required in call to EzDateTime.safeGetInputValue(..).
                    Returning the default value of "${defaultValue}" instead.`);

            return defaultValue;
        }

        const elementExists = EzUx.elementExists(inputElementId);

        if (!elementExists) {
            globalThis?.console?.error(
                EzString.em`
                    Input element id ${inputElementId} is not associated with an existing HTML intput.
                    Returning the default value of "${defaultValue}" instead.`);

            return defaultValue;
        }

        return EzUx.getInputValue(inputElementId);
    }

    /**
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     *
     * @static
     * @public @function
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * If a moment instance is returned the time is forced to the start of day.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * @param {undefined|null|string} datePickerId
     * Default: null
     * @param {undefined|null|boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static fromDatePickerStartOfDay(datePickerId = null, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { datePickerId },
                EzDateTime,
                EzDateTime.fromDatePickerStartOfDay);
        }

        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.fromDatePicker(
                datePickerId,
                EzBoolean.booleanOrFalse(returnNullInsteadOfTodayIfNull),
                EzDateTime.safeTimeZoneId(timeZoneId)));
    }

    /**
     * @public @function
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * If a moment instance is returned the time is forced to the start of day.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * @param {undefined|null|string} datePickerId
     * Default: null
     * @param {undefined|null|boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull, timeZoneId)
     */
    ezMomentFromDatePickerStartOfDay = EzDateTime.fromDatePickerStartOfDay;

    /**
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     *
     * @static
     * @public @function
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * If a moment instance is returned the time is forced to the end of day.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * @param {string} datePickerId
     * Default: null
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static fromDatePickerEndOfDay(datePickerId = null, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { datePickerId },
                EzDateTime,
                EzDateTime.fromDatePickerEndOfDay);
        }

        return EzDateTime.withTimeAtEndOfDay(
            EzDateTime.fromDatePicker(
                datePickerId,
                EzBoolean.booleanOrFalse(returnNullInsteadOfTodayIfNull),
                EzDateTime.safeTimeZoneId(timeZoneId)));
    }

    /**
     * @public @function
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * If a moment instance is returned the time is forced to the end of day.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * @param {string} datePickerId
     * Default: null
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    ezMomentFromDatePickerEndOfDay = EzDateTime.fromDatePickerEndOfDay;

    /**
     * @static
     * @public @function
     * Transforms the provided javascript date (jsDate) to an ISO date without time or timezone information.
     * @param {Date} jsDate
     * A valid Javascript Date instance
     * Default: null
     * @returns {string}
     */
    static jsDateToIsoDate(jsDate = null) {
        if (!EzObject.isValid(jsDate) || !EzFunction.isFunction(jsDate?.getFullYear)) {
            throw new EzBadParamException(
                'jsDate',
                EzDateTime,
                EzDateTime.ezJsDateToIsoDate);
        }

        const yearStr = EzNumber.asStringOrDefault(
            jsDate.getFullYear(),
            moment().year())
            .padStart(EzNumber.FOUR, EzChar.ZERO);

        const monthStr = EzNumber.asStringOrDefault(jsDate.getMonth() + EzNumber.ONE, '01')
            .padStart(EzNumber.TWO, EzChar.ZERO);

        const dayStr = EzNumber.asStringOrDefault(
            jsDate.getDate(),
            '01')
            .padStart(EzNumber.TWO, EzChar.ZERO);

        return `${yearStr}-${monthStr}-${dayStr}`;
    }

    /**
     *@public @function
     * Transforms the provided javascript date (jsDate) to an ISO date without time or timezone information.
     * @param {Date} jsDate
     * Default: null
     * A valid Javascript Date instance
     * @returns {string}
     * @deprecated:
     * Migrate to static method: EzDateTime.jsDateToIsoDate(jsDate)
     */
    ezJsDateToIsoDate = EzDateTime.jsDateToIsoDate;

    /**
     * @static
     * @public @function
     * Transforms the provided javascript date (jsDate) to an ISO date without time or timezone information.
     * @param {Date} jsDate
     * A valid Javascript Date instance
     * Default: null
     * @param {undefined|null|number} hours
     * Default: 0
     * @param {undefined|null|number} minutes
     * Default: 0
     * @param {undefined|null|number} seconds
     * Default: 0
     * @param {undefined|null|number} milliseconds
     * Default: 0
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     */
    static jsDateToIsoDateTime(jsDate = null, hours = EzNumber.ZERO, minutes = EzNumber.ZERO, seconds = EzNumber.ZERO, milliseconds = EzNumber.ZERO,
        timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzObject.isValid(jsDate) || !EzFunction.isFunction(jsDate?.getFullYear)) {
            throw new EzBadParamException(
                'jsDate',
                EzDateTime,
                EzDateTime.jsDateToIsoDateTime);
        }

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        return moment.tz(jsDate, timeZoneId)
            .hours(EzNumber.numberOrZero(hours))
            .minutes(EzNumber.numberOrZero(minutes))
            .seconds(EzNumber.numberOrZero(seconds))
            .milliseconds(EzNumber.numberOrZero(milliseconds))
            .format();
    }

    /**
     * @static
     * Transforms a JS Date to an ISO string with local time zone.
     * @param {Date} jsDate
     * @param {Object} options
     * Default: {
     *      year: true,
     *      month: true,
     *      date: true,
     *      hours: true,
     *      minutes: true,
     *      seconds: true,
     *      milliseconds: false,
     *      zone: true
     *  }
     * @returns {string}
     */
    static jsDateToIso(jsDate, options) {
        if (!EzObject.isValid(jsDate)) {
            throw new EzBadParamException(
                'jsDate',
                EzDateTime,
                EzDateTime.jsDateToIso);
        }

        if (!EzObject.isValid(options)) {
            options = {
                year: true,
                month: true,
                date: true,
                hours: true,
                minutes: true,
                seconds: true,
                milliseconds: false,
                zone: true
            };
        }

        let jsDateAsIso = EzString.EMPTY;

        let dateDelimiter = EzString.EMPTY;

        if (EzBoolean.booleanOrFalse(options?.year)) {
            jsDateAsIso = `${jsDate.getFullYear()}`;

            dateDelimiter = EzChar.DASH;
        }

        if (EzBoolean.booleanOrFalse(options?.month)) {
            jsDateAsIso = `${jsDateAsIso}${dateDelimiter}${EzNumber.toIsoMonthString(jsDate.getMonth() + EzNumber.ONE)}`;

            dateDelimiter = EzChar.DASH;
        }

        if (EzBoolean.booleanOrFalse(options?.date)) {
            jsDateAsIso = `${jsDateAsIso}${dateDelimiter}${EzNumber.toIsoDateString(jsDate.getDate())}`;
        }

        let jsTimeAsIso = EzString.EMPTY;

        let timeDelimiter = EzChar.T;

        if (EzBoolean.booleanOrFalse(options?.hours)) {
            jsTimeAsIso = `${timeDelimiter}${EzNumber.toIsoHourString(jsDate.getHours())}`;

            timeDelimiter = EzChar.COLON;
        }

        if (EzBoolean.booleanOrFalse(options?.minutes)) {
            jsTimeAsIso = `${jsTimeAsIso}${timeDelimiter}${EzNumber.toIsoMinuteString(jsDate.getMinutes())}`;

            timeDelimiter = EzChar.COLON;
        }

        if (EzBoolean.booleanOrFalse(options?.seconds)) {
            jsTimeAsIso = `${jsTimeAsIso}${timeDelimiter}${EzNumber.toIsoSecondString(jsDate.getSeconds())}`;

            timeDelimiter = EzChar.COLON;
        }

        if (EzBoolean.booleanOrFalse(options?.milliseconds)) {
            jsTimeAsIso = `${jsTimeAsIso}${timeDelimiter}${EzNumber.toIsoMillisecondString(jsDate.getMilliseconds())}`;
        }

        let jsTimeZoneAsIso = EzString.EMPTY;

        if (EzBoolean.booleanOrFalse(options?.zone)) {
            const timeZoneOffset = -jsDate.getTimezoneOffset();

            const hourOffset = Math.floor(Math.abs(timeZoneOffset) / EzNumber._60);

            const minuteOffset = Math.abs(timeZoneOffset) % EzNumber._60;

            jsTimeZoneAsIso = `${EzNumber.toIsoHoursString(hourOffset)}:${EzNumber.toIsoMinuteString(minuteOffset)}`;

            const timeZoneOffsetPrefix = EzNumber.ZERO <= timeZoneOffset
                ? EzChar.PLUS
                : EzChar.DASH;

            jsTimeZoneAsIso = '00:00' === jsTimeZoneAsIso
                ? EzChar.Z
                : `${timeZoneOffsetPrefix}${jsTimeZoneAsIso}`;
        }

        return `${jsDateAsIso}${jsTimeAsIso}${jsTimeZoneAsIso}`;
    }

    /**
     * Transforms a JS Date to an ISO string with local time zone.
     * @param {Date} jsDate
     * @param {Object} options
     * options = {
     *      year: true,
     *      month: true,
     *      date: true,
     *      hours: true,
     *      minutes: true,
     *      seconds: true,
     *      milliseconds: false,
     *      zone: true
     *  }
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.jsDateToIso(jsDate, options)
     */
    ezJsDateToIso = EzDateTime.jsDateToIso;

    /**
     * @static
     * @public @function
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * @param {string} timeZoneId
     * Optionally in the specified timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static dateTimeFromDatePickerWithTimeAtStartOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(datePickerId) || !EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.dateTimeFromDatePickerWithTimeAtStartOfDay);
        }

        const jsDate = EzUx.$id(datePickerId).datepicker('getDate');

        if (null == jsDate) {
            return null;
        }

        // Step 1: Create a moment with time at beginning of day in the users's selected time zone
        return EzDateTime.nowWithStartOfDay(
            EzDateTime.safeTimeZoneId(timeZoneId))
            // Step 2: Set only the year, month, and date from the JSDate (ignoring its timezone)
            .year(jsDate.getFullYear())
            .month(jsDate.getMonth())
            .date(jsDate.getDate());
    }

    /**
     * @public @function
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * @param {string} timeZoneId
     * Optionally in the specified timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.dateTimeFromDatePickerWithTimeAtStartOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    ezDateTimeFromDatePickerWithTimeAtStartOfDay = EzDateTime.dateTimeFromDatePickerWithTimeAtStartOfDay;

    /**
     * @static
     * @public @function
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the end of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {string} timeZoneId
     * Optionally return the date in the provided timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static dateTimeFromDatePickerWithTimeAtEndOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId) || !EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.dateTimeFromDatePickerWithTimeAtEndOfDay);
        }

        const jsDate = EzUx.$id(datePickerId).datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            throw new EzException(
                EzString.em`
                    The date picker (id=${datePickerId}') did not return a valid Javascript date instance from the call to
                    EzUx.$id(${datePickerId}).datepicker('getDate')`);
        }

        // Step 1: Create a moment with time at the end of day in the users's selected time zone
        return EzDateTime.nowWithEndOfDay(timeZoneId)
            // Step 2: Set only the year, month, and date from the JSDate (ignoring its timezone)
            .year(jsDate.getFullYear())
            .month(jsDate.getMonth())
            .date(jsDate.getDate());
    }

    /**
     * @public @function
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the end of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {string} timeZoneId
     * Optionally return the date in the provided timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.dateTimeFromDatePickerWithTimeAtEndOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    ezDateTimeFromDatePickerWithTimeAtEndOfDay = EzDateTime.dateTimeFromDatePickerWithTimeAtEndOfDay;

    /**
     * @public @function
     * Gets the date picker element's date as a string with a language-sensitive representation of the date portion
     * of the specified date in the user agent's timezone. In otherwords, returns a locale specific display date (which
     * may not be mm/dd/yyyy).
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @returns {string}
     */
    ezGetDatePickerLocaleDisplayDate(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetDatePickerLocaleDisplayDate);
        }

        if (!EzUx.elementExists(datePickerId)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        const jsDate = EzUx.$id(datePickerId).datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        return jsDate.toLocaleDateString();
    }

    /**
     * @public @function
     * Gets the date picker element's date as a string in ezClocker's default display date format.
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        ENGINEERING NOTES
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        JQuery UI date pickers store the value as a Javascript Date in the local time zone.
        EzClocker sets date pickers with the display date only (ignoring the time and zone)
        When the date is read from the date picker, the time and zone must get ignored to
        nake sure the date doesn't translate when converting it to the moment in the user's
        selected time zone.
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @returns {string}
     */
    ezGetDatePickerDisplayDate(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetDatePickerDisplayDate);
        }

        if (!EzUx.elementExists(datePickerId)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        const jsDate = EzUx.$id(datePickerId).datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        // Step 1: Create a moment with time at the end of day in the users's selected time zone
        const momentInstance = EzDateTime.nowWithEndOfDay();

        // Step 2: Set only the year, month, and date from the JSDate (ignoring its timezone)
        return EzDateTime.toDisplayDate(
            momentInstance.year(jsDate.getFullYear())
                .month(jsDate.getMonth())
                .date(jsDate.getDate()));
    }

    /**
     * @static
     * @public @function
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery Date Picker Formatting Values
     *     d - day of month (no leading zero)
     *     dd - day of month (two digit)
     *     o - day of the year (no leading zeros)
     *     oo - day of the year (three digit)
     *     D - day name short
     *     DD - day name long
     *     m - month of year (no leading zero)
     *     mm - month of year (two digit)
     *     M - month name short
     *     MM - month name long
     *     y - year (two digit)
     *     yy - year (four digit)
     *     @ - Unix timestamp (ms since 01/01/1970)
     *     ! - Windows ticks (100ns since 01/01/0001)
     *     '...' - literal text
     *     EzString.EMPTY - single quote
     * MomentJS Date Formatting Values
     *             TOKEN   EXAMPLE
     *     Month
     *             M       1 2 ... 11 12
     *             Mo      1st 2nd ... 11th 12th
     *             MM  01 02 ... 11 12
     *             MMM Jan Feb ... Nov Dec
     *             MMMM    January February ... November December
     *     Quarter
     *             Q       1 2 3 4
     *             Qo      1st 2nd 3rd 4th
     *     Day of Month
     *             D       1 2 ... 30 31
     *             Do      1st 2nd ... 30th 31st
     *             DD      01 02 ... 30 31
     *     Day of Year
     *             DDD     1 2 ... 364 365
     *             DDDo    1st 2nd ... 364th 365th
     *             DDDD    001 002 ... 364 365
     *     Day of Week
     *             d       0 1 ... 5 6
     *             do      0th 1st ... 5th 6th
     *             dd      Su Mo ... Fr Sa
     *             ddd     Sun Mon ... Fri Sat
     *             dddd    Sunday Monday ... Friday Saturday
     *     Day of Week (Locale)
     *             e       0 1 ... 5 6
     *     Day of Week (ISO)
     *             E       1 2 ... 6 7
     *     Week of Year
     *             w       1 2 ... 52 53
     *             wo      1st 2nd ... 52nd 53rd
     *             ww      01 02 ... 52 53
     *     Week of Year (ISO)
     *              W      1 2 ... 52 53
     *             Wo      1st 2nd ... 52nd 53rd
     *             WW      01 02 ... 52 53
     *     Year
     *             YY      70 71 ... 29 30
     *             YYYY    1970 1971 ... 2029 2030
     *             YYYYYY  -001970 -001971 ... +001907 +001971
     *                     Note: Expanded Years (Covering the full time value range of approximately 273,790 years forward or backward from 01 January, 1970)
     *             Y       1970 1971 ... 9999 +10000 +10001
     *                     Note: This complies with the ISO 8601 standard for dates past the year 9999
     *     Era Year
     *             y       1 2 ... 2020 ...
     *     Era
     *             N       BC AD
     *             NN      BC AD
     *             NNN     BC AD
     *                     Note: Abbr era name
     *             NNNN    Before Christ, Anno Domini
     *                     Note: Full era name
     *             NNNNN   BC AD
     *                     Note: Narrow era name
     *     Week Year
     *             gg      70 71 ... 29 30
     *             gggg    1970 1971 ... 2029 2030
     *     Week Year (ISO)
     *             GG      70 71 ... 29 30
     *             GGGG    1970 1971 ... 2029 2030
     *     AM/PM
     *             A       AM PM
     *             a       am pm
     *     Hour
     *             H       0 1 ... 22 23
     *             HH      00 01 ... 22 23
     *             h       1 2 ... 11 12
     *             hh      01 02 ... 11 12
     *             k       1 2 ... 23 24
     *             kk      01 02 ... 23 24
     *     Minute
     *             m       0 1 ... 58 59
     *             mm      00 01 ... 58 59
     *     Second
     *             s       0 1 ... 58 59
     *             ss      00 01 ... 58 59
     *     Fractional Second
     *             S       0 1 ... 8 9
     *             SS      00 01 ... 98 99
     *             SSS     000 001 ... 998 999
     *             SSSS... 000[0..] 001[0..] ... 998[0..] 999[0..]
     *     Time Zone
     *             z       EST CST ... MST PST
     *             zz      EST CST ... MST PST
     *                     Note: as of 1.6.0, the z/zz format tokens have been deprecated from plain moment objects.
     *                     However, they *do* work if you are using a specific time zone with the moment-timezone addon.
     *             Z       -07:00 -06:00 ... +06:00 +07:00
     *             ZZ      -0700 -0600 ... +0600 +0700
     *     Unix Timestamp
     *             X       1360013296
     *     Unix Millisecond Timestamp
     *             x       1360013296123
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
    static datePickerDateFormatToMomentDateFormat(datePickerDateFormat) {
        if (!EzObject.isValid(datePickerDateFormat)) {
            throw new EzBadParamException(
                'datePickerDateFormat',
                EzDateTime,
                EzDateTime.datePickerDateFormatToMomentDateFormat);
        }

        return datePickerDateFormat
            .replace(/DD/, 'dddd')
            .replace(/D/, 'ddd')
            .replace(/dd/, 'DD')
            .replace(/d/, EzChar.D)
            .replace(/MM/, 'MMMM')
            .replace(/M/, 'MMM')
            .replace(/mm/, 'MM')
            .replace(/m/, EzChar.M)
            .replace(/yy/, 'YYYY')
            .replace(/y/, 'YY')
            .replace(/o/, 'DDD')
            .replace(/oo/, 'DDDD')
            .replace(/@/, EzChar.X)
            .replace(/!/, EzChar.x);
    }

    /**
     * @public @function
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery Date Picker Formatting Values
     *     d - day of month (no leading zero)
     *     dd - day of month (two digit)
     *     o - day of the year (no leading zeros)
     *     oo - day of the year (three digit)
     *     D - day name short
     *     DD - day name long
     *     m - month of year (no leading zero)
     *     mm - month of year (two digit)
     *     M - month name short
     *     MM - month name long
     *     y - year (two digit)
     *     yy - year (four digit)
     *     @ - Unix timestamp (ms since 01/01/1970)
     *     ! - Windows ticks (100ns since 01/01/0001)
     *     '...' - literal text
     *     EzString.EMPTY - single quote
     * MomentJS Date Formatting Values
     *             TOKEN   EXAMPLE
     *     Month
     *             M       1 2 ... 11 12
     *             Mo      1st 2nd ... 11th 12th
     *             MM  01 02 ... 11 12
     *             MMM Jan Feb ... Nov Dec
     *             MMMM    January February ... November December
     *     Quarter
     *             Q       1 2 3 4
     *             Qo      1st 2nd 3rd 4th
     *     Day of Month
     *             D       1 2 ... 30 31
     *             Do      1st 2nd ... 30th 31st
     *             DD      01 02 ... 30 31
     *     Day of Year
     *             DDD     1 2 ... 364 365
     *             DDDo    1st 2nd ... 364th 365th
     *             DDDD    001 002 ... 364 365
     *     Day of Week
     *             d       0 1 ... 5 6
     *             do      0th 1st ... 5th 6th
     *             dd      Su Mo ... Fr Sa
     *             ddd     Sun Mon ... Fri Sat
     *             dddd    Sunday Monday ... Friday Saturday
     *     Day of Week (Locale)
     *             e       0 1 ... 5 6
     *     Day of Week (ISO)
     *             E       1 2 ... 6 7
     *     Week of Year
     *             w       1 2 ... 52 53
     *             wo      1st 2nd ... 52nd 53rd
     *             ww      01 02 ... 52 53
     *     Week of Year (ISO)
     *              W      1 2 ... 52 53
     *             Wo      1st 2nd ... 52nd 53rd
     *             WW      01 02 ... 52 53
     *     Year
     *             YY      70 71 ... 29 30
     *             YYYY    1970 1971 ... 2029 2030
     *             YYYYYY  -001970 -001971 ... +001907 +001971
     *                     Note: Expanded Years (Covering the full time value range of approximately 273,790 years forward or backward from 01 January, 1970)
     *             Y       1970 1971 ... 9999 +10000 +10001
     *                     Note: This complies with the ISO 8601 standard for dates past the year 9999
     *     Era Year
     *             y       1 2 ... 2020 ...
     *     Era
     *             N       BC AD
     *             NN      BC AD
     *             NNN     BC AD
     *                     Note: Abbr era name
     *             NNNN    Before Christ, Anno Domini
     *                     Note: Full era name
     *             NNNNN   BC AD
     *                     Note: Narrow era name
     *     Week Year
     *             gg      70 71 ... 29 30
     *             gggg    1970 1971 ... 2029 2030
     *     Week Year (ISO)
     *             GG      70 71 ... 29 30
     *             GGGG    1970 1971 ... 2029 2030
     *     AM/PM
     *             A       AM PM
     *             a       am pm
     *     Hour
     *             H       0 1 ... 22 23
     *             HH      00 01 ... 22 23
     *             h       1 2 ... 11 12
     *             hh      01 02 ... 11 12
     *             k       1 2 ... 23 24
     *             kk      01 02 ... 23 24
     *     Minute
     *             m       0 1 ... 58 59
     *             mm      00 01 ... 58 59
     *     Second
     *             s       0 1 ... 58 59
     *             ss      00 01 ... 58 59
     *     Fractional Second
     *             S       0 1 ... 8 9
     *             SS      00 01 ... 98 99
     *             SSS     000 001 ... 998 999
     *             SSSS... 000[0..] 001[0..] ... 998[0..] 999[0..]
     *     Time Zone
     *             z       EST CST ... MST PST
     *             zz      EST CST ... MST PST
     *                     Note: as of 1.6.0, the z/zz format tokens have been deprecated from plain moment objects.
     *                     However, they *do* work if you are using a specific time zone with the moment-timezone addon.
     *             Z       -07:00 -06:00 ... +06:00 +07:00
     *             ZZ      -0700 -0600 ... +0600 +0700
     *     Unix Timestamp
     *             X       1360013296
     *     Unix Millisecond Timestamp
     *             x       1360013296123
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @deprecated
     * Migrate to static method: EzDateTime.datePickerDateFormatToMomentDateFormat(datePickerDateFormat)
     */
    ezDatePickerDateFormatToMomentDateFormat = EzDateTime.datePickerDateFormatToMomentDateFormat;

    /**
     * @static
     * @public @function
     * Gets a JQuery UI date picker's date format for the provided datePickerId
     * @param {string} datePickerId
     * @returns {string}
     */
    static getDatePickerDateFormat(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.getDatePickerDateFormat);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            EzLogger.error(
                EzString.em`
                    Failed to obtain a date picker input HTML element's date format string for id="${datePickerId}"
                    due to the folowing error: Date picker HTML element with id="${datePickerId}" does not exist.
                    Returning the default date format string of ${EzDateTime.DEFAULT_MM_DD_YYYY} instead.`);
        }

        const datePickerDateFormatStr = datePickerHTMLElement.datepicker(
            'option',
            'dateFormat');

        return EzString.hasText(datePickerDateFormatStr)
            ? datePickerDateFormatStr
            : EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @public @function
     * Gets a JQuery UI date picker's date format for the provided datePickerId
     * @param {string} datePickerId
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.getDatePickerDateFormat(datePickerId)
     */
    ezGetDatePickerDateFormat = EzDateTime.getDatePickerDateFormat;

    /**
     * @static
     * @public @function
     * Gets a Moment date format string for the JQuery UI date picker's date format.
     * If the date picker does not have a date format value, then returns the default moment date format of EzDateTime.ezInstance.DEFAULT_MM_DD_YYYY
     * @param {string} datePickerId
     * @returns {string}
     */
    static getMomentJsDateFormatForDatePicker(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.getMomentJsDateFormatForDatePicker);
        }

        const datePickerDateFormat = EzDateTime.getDatePickerDateFormat(datePickerId);

        return EzString.hasLength(datePickerDateFormat)
            ? EzDateTime.datePickerDateFormatToMomentDateFormat(datePickerDateFormat)
            : EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @static
     * @public @function
     * Gets a Moment date format string for the JQuery UI date picker's date format.
     * If the date picker does not have a date format value, then returns the default moment date format of EzDateTime.ezInstance.DEFAULT_MM_DD_YYYY
     * @param {string} datePickerId
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.getDateFormatForDatePicker(datePickerId)
     */
    ezGetMomentDateFormatForDatePickerDateFormat = EzDateTime.getMomentJsDateFormatForDatePicker;

    /**
     * @static
     * @public @function
     * Sets a datepicker value from the provided moment.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Unless the provided momentInstance's time is needed, use one of the following instead of this method:
     *  1) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueStartOfDay(...)
     *  2) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueEndOfDay(...)
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    static setDatePickerValue(datePickerId, momentInstance) {
        if (!EzString.hasText(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValue);
        }
        if (!EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValue,
                `Date picker HTML element with id=${datePickerId} does not currently exist.`);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                EzDateTime.withTimeAtStartOfDay(momentInstance));
        }
    }

    /**
     * @public @function
     * Sets a datepicker value from the provided moment.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Unless the provided momentInstance's time is needed, use one of the following instead of this method:
     *  1) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueStartOfDay(...)
     *  2) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueEndOfDay(...)
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerValue(datePickerId, momentInstance)
     */
    ezSetDatePickerValue = EzDateTime.setDatePickerValue;

    /**
     * @static
     * @public @function
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    static setDatePickerDateWithTimeAtStartOfDay(datePickerId, momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(datePickerId) || !EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerDateWithTimeAtStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            // Set date picker value to null (clear out date)
            EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            const newDateTimeForDatePicker = EzDateTime.withTimeAtStartOfDay(
                momentInstance,
                timeZoneId);

            const currentPickerMoment = EzDateTime.dateTimeFromDatePickerWithTimeAtStartOfDay(
                datePickerId,
                timeZoneId);

            if (EzDateTime.equals(currentPickerMoment, newDateTimeForDatePicker)) {
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            } else {
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            }
        }
    }

    /**
     * @public @function
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    ezSetDatePickerDateWithTimeAtStartOfDay = EzDateTime.setDatePickerDateWithTimeAtStartOfDay;

    /**
     * @static
     * @public @function
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    static setDatePickerDateWithTimeAtEndOfDay(datePickerId, momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId) || !EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setDatePickerDateWithTimeAtEndOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            // Set date picker value to null (clear out date)
            EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            const newDateTimeForDatePicker = EzDateTime.withTimeAtEndOfDay(
                momentInstance,
                timeZoneId);

            let currentPickerMoment;

            try {
                currentPickerMoment = EzDateTime.dateTimeFromDatePickerWithTimeAtEndOfDay(
                    datePickerId,
                    timeZoneId);
            } catch (err) {
                const em = EzString.em`
                    Failed to obtain a moment at end of day for date pciker with id=
                    ${datePickerId} due to the following error: ${err.message}.
                    Defaulting currentPickerMoment to null instead.`;

                if (EzObject.isValid(globalThis?.ezApi?.ezclocker?.ezLogger)) {
                    globalThis.ezApi.ezclocker.ezLogger.debug(em);
                } else if (EzObject.isValid(globalThis?.console)) {
                    globalThis?.console?.debug(em);
                }

                currentPickerMoment = null;
            }

            if (EzDateTime.isValidMoment(currentPickerMoment) && EzDateTime.equals(currentPickerMoment, newDateTimeForDatePicker)) {
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            } else {
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            }
        }
    }

    /**
     * @public @function
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerDateWithTimeAtEndOfDay(datePickerId, momentInstance, timeZoneId)
     */
    ezSetDatePickerDateWithTimeAtEndOfDay = EzDateTime.setDatePickerDateWithTimeAtEndOfDay;

    /**
     * @static
     * @public @function
     * Sets the date picker's input value (but not the date picker's internal date value) equal to the provided
     * momentInstance formatted with the date picker control's dateFormat value. Nothing is set if the date picker's
     * input value is already equal to the formatted display date
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    static setDatePickerInputValueWithMomentDisplayDate(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate);
        }

        let momentAtStartOfDayWithPTZDisplayDate = EzString.EMPTY;

        if (EzDateTime.isValidMoment(momentInstance)) {
            momentAtStartOfDayWithPTZDisplayDate = EzDateTime.withTimeAtStartOfDay(momentInstance)
                .format(
                    EzDateTime.getMomentJsDateFormatForDatePicker(datePickerId));
        }

        // Set the date pickers input text box equal to the same value if it not already the same value
        if (momentAtStartOfDayWithPTZDisplayDate !== EzString.stringOrEmpty(EzUx.getInputValue(datePickerId))) {
            // Setting the input value will trigger the inputs onChange event.
            EzUx.setInputValue(
                datePickerId,
                momentAtStartOfDayWithPTZDisplayDate);
        }
    }

    /**
     * @static
     * @public @function
     * Sets the date picker's input value (but not the date picker's internal date value) equal to the provided
     * momentInstance formatted with the date picker control's dateFormat value. Nothing is set if the date picker's
     * input value is already equal to the formatted display date
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerInputValueWithMomentDisplayDate(datePickerId, momentInstance)
     */
    ezSetDatePickerInputValueWithMomentDisplayDate = EzDateTime.setDatePickerInputValueWithMomentDisplayDate;

    /**
     * @static
     * @public @function
     * Sets the date picker controls date and input value equal to the provided momentInstance formatted
     * with the date picker control's dateFormat value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {undefined|null|moment} momentInstance
     * When undefined or null, the date picker's selected date is cleared
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    static setDatePickerInputAndDateValueWithMomentDisplayDate(datePickerId, momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { datePickerId },
                EzDateTime,
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            throw new EzBadStateException(
                `Expected date picker id "${datePickerId}" to reference a valid date picker HTML element`,
                `Date picker HTML element with id="${datePickerId}" does not exist.`,
                EzDateTime,
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate,
                `Failed to set the date picker HTML element for id="${datePickerId}"'s input and date value.`);
        }

        // Set to null instead of empty string so the date date picker's selected date is cleard when the provided momentInstance is null or undefined
        const datePickerDisplayDate = EzDateTime.isValidMoment(momentInstance)
            ? EzDateTime.withZone(momentInstance, timeZoneId)
                .format(
                    EzDateTime.getMomentJsDateFormatForDatePicker(datePickerId))
            : EzString.EMPTY;

        // Set the actual date of the date picker using the datePickerDisplayDate value
        // Calling datepicker('setDate', datePickerDisplayDate) should not trigger a onChange event for the input
        datePickerHTMLElement.datepicker(
            'setDate',
            datePickerDisplayDate);

        // Set the date pickers input text value to the datePickerDisplayDate value (if not already equal that)
        if (EzString.stringOrEmpty(EzUx.getInputValue(datePickerId)) !== datePickerDisplayDate) {
            // Setting the input value will trigger the inputs onChange event.
            EzUx.setInputValue(
                datePickerId,
                datePickerDisplayDate);
        }
    }

    /**
     * @static
     * @public @function
     * Sets the date picker controls date and input value equal to the provided momentInstance formatted
     * with the date picker control's dateFormat value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {undefined|null|moment} momentInstance
     * When undefined or null, the date picker's selected date is cleared
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(datePickerId, momentInstance, timeZoneId)
     */
    ezSetDatePickerInputAndDateValueWithMomentDisplayDate = EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate;

    /**
     * @static
     * @public @function
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * Default: null
     */
    static setDatePickerValueStartOfDay(datePickerId, momentInstance = null) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValueStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                EzDateTime.withTimeAtStartOfDay(momentInstance));
        }
    }

    /**
     * @public @function
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerValueStartOfDay(datePickerId, momentInstance)
     */
    ezSetDatePickerValueStartOfDay = EzDateTime.setDatePickerValueStartOfDay;

    /**
     * @static
     * @public @function
     * Sets a datepicker value from the provided moment. Time is set to the end of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * Default: null
     */
    static setDatePickerValueEndOfDay(datePickerId, momentInstance = null) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValueEndOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            const currentPickerMoment = EzDateTime.fromDatePickerEndOfDay(datePickerId, true);

            if (EzDateTime.isValidMoment(currentPickerMoment) && EzDateTime.equals(momentInstance, currentPickerMoment)) {
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                    datePickerId,
                    EzDateTime.withTimeAtEndOfDay(momentInstance));
            } else {
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                    datePickerId,
                    EzDateTime.withTimeAtEndOfDay(momentInstance));
            }
        }
    }

    /**
     * @public @function
     * Sets a datepicker value from the provided moment. Time is set to the end of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * Default: null
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerValueEndOfDay(datePickerId, momentInstance)
     */
    ezSetDatePickerValueEndOfDay = EzDateTime.setDatePickerValueEndOfDay;

    /**
     * @public @function
     * Sets the date picker's minimum date with time at start of day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    ezSetDatePickerMinDateStartOfDay(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateStartOfDay);
        }

        if (!EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateStartOfDay);
        }

        EzUx.$id(datePickerId).datepicker(
            'option',
            'minDate',
            EzDateTime.toJsDate(
                EzDateTime.withTimeAtStartOfDay(momentInstance)));
    }

    /**
     * @public @function
     * Sets the date picker's minimum date with time at endof day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    ezSetDatePickerMinDateEndOfDay(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateEndOfDay);
        }
        if (!EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateEndOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateEndOfDay);
        }

        EzUx.$id(datePickerId).datepicker(
            'option',
            'minDate',
            EzDateTime.toDisplayDate(
                EzDateTime.withTimeAtEndOfDay(momentInstance)));
    }

    /**
     * @static
     * @public @function
     * Sets the date picker's maximum date with time at end of day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    static setDatePickerMaxDateEndOfDay(datePickerId, momentInstance) {
        if (!EzString.hasText(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerMaxDateEndOfDay);
        }
        if (!EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerMaxDateEndOfDay);
        }
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.setDatePickerMaxDateEndOfDay);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            throw new EzBadStateException(
                `Expected HTML element id "${datePickerId}" to reference a valid date picker HTML input element.`,
                `HTML element id "${datePickerId}" doe snot reference a valid HTML element.`,
                EzDateTime,
                EzDateTime.setDatePickerMaxDateEndOfDay,
                `Failed to set the date picker HTML input element with id=${datePickerId}'s maxDate property equal to ${EzDateTime.toIso(momentInstance)}.`);
        }

        datePickerHTMLElement.datepicker(
            'option',
            'maxDate',
            EzDateTime.toDisplayDate(
                EzDateTime.withTimeAtEndOfDay(momentInstance)));
    }

    ezSetDatePickerMaxDateEndOfDay = EzDateTime.setDatePickerMaxDateEndOfDay;

    /**
     * @static
     * @public @function
     * Converts the provided momentInstance to a JavaScript Date
     * @param {moment} momentInstance
     * Default: null
     * @returns {Date}
     */
    static toJsDate(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toJsDate);
        }

        return EzDateTime.momentWithPTZ(momentInstance).toDate();
    }

    /**
     * @public @function
     * Converts the provided momentInstance to a JavaScript Date
     * @param {moment} momentInstance
     * Default: null
     * @returns {Date}
     * @deprecated
     * Migrate to static method: EzDateTime.toJsDate(momentInstance)
     */
    ezToJsDate = EzDateTime.toJsDate;

    /**
     * @public @function
     * Creates a moment instance from a date picker's value and a time picker's value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {string} timePickerId
     * @returns {moment}
     */
    ezDateTimeFromPickers(datePickerId, timePickerId, timeZone) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDateTimeFromPickers);
        }

        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDateTimeFromPickers);
        }

        const datePickerMoment = EzDateTime.fromDatePickerStartOfDay(datePickerId, false, timeZone);

        let hour = EzUx.$id(timePickerId).timepicker('getHour');

        if (!EzNumber.isNumber(hour)) {
            hour = EzNumber.ZERO;
        }

        let min = EzUx.$id(timePickerId).timepicker('getMinute');

        if (!EzNumber.isNumber(min)) {
            min = EzNumber.ZERO;
        }

        return datePickerMoment.hour(hour)
            .minute(min)
            .seconds(EzNumber.ZERO)
            .millisecond(EzNumber.ZERO);
    }

    /**
     * @public @function
     * Creates a moment instance from a date picker's value and a time picker's value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {string} timePickerId
     * @returns {moment}
     */
    ezMomentFromPickers(datePickerId, timePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromPickers);
        }
        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromPickers);
        }

        const datePickerMoment = EzDateTime.fromDatePickerStartOfDay(datePickerId);

        let hour = EzUx.$id('edit_clockInTime').timepicker('getHour');

        if (!EzNumber.isNumber(hour)) {
            hour = EzNumber.ZERO;
        }

        let min = EzUx.$id('edit_clockInTime').timepicker('getMinute');

        if (!EzNumber.isNumber(min)) {
            min = EzNumber.ZERO;
        }

        return datePickerMoment
            .hour(hour)
            .minute(min)
            .seconds(EzNumber.ZERO)
            .millisecond(EzNumber.ZERO);
    }

    /**
     * @public @function
     * Returns the string formatted MM/DD/YYYY for the provided moment.js instance. If none provided,
     * returns the current date instead.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to  EzDateTime.toDisplayDate()
     */
    ezDateString = EzDateTime.toDisplayDate;

    static get NEVER_SET_MOMENT_UTC() {
        return moment()
            .tz(EzTimeZone.UTC)
            .year(EzNumber.ZERO)
            // Janurary (zero based month)
            .month(EzNumber.ZERO)
            .date(EzNumber.ONE)
            .hour(EzNumber.ZERO)
            .minute(EzNumber.ZERO)
            .seconds(EzNumber.ZERO)
            .millisecond(EzNumber.ZERO);
    }

    static get NEVER_SET_DATE_ISO() {
        return EzDateTime.toIsoDate(EzDateTime.NEVER_SET_MOMENT_UTC);
    }

    static get NEVER_SET_DATE_TIME_ISO() {
        return EzDateTime.toIso(EzDateTime.NEVER_SET_MOMENT_UTC);
    }

    static getNeverSetDateTimeIso(noZone, noMilliseconds, noMillisecondsOrSeconds, noTime) {
        if (EzBoolean.isTrue(noTime)) {
            return EzDateTime.NEVER_SET_DATE_ISO;
        }

        if (EzBoolean.isTrue(noMillisecondsOrSeconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_SET_DATE_ISO}T00:00`
                : `${EzDateTime.NEVER_SET_DATE_ISO}T00:00Z`;
        }

        if (EzBoolean.isTrue(noMilliseconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_SET_DATE_ISO}T00:00:00`
                : `${EzDateTime.NEVER_SET_DATE_ISO}T00:00:00Z`;
        }

        return EzDateTime.NEVER_SET_DATE_TIME_ISO;
    }

    static get NEVER_SET_DISPLAY_DATE() {
        return EzChar.DASH;
    }

    static get NEVER_EXPIRE_MOMENT_UTC() {
        return moment()
            .tz(EzTimeZone.UTC)
            .year(EzNumber._9999)
            // Janurary (zero based month)
            .month(EzNumber.ZERO)
            .date(EzNumber.ONE)
            .hour(EzNumber._23)
            .minute(EzNumber._59)
            .seconds(EzNumber._59)
            .millisecond(EzNumber._999);
    }

    static get NEVER_EXPIRE_DATE_ISO() {
        return EzDateTime.toIsoDate(EzDateTime.NEVER_EXPIRE_MOMENT_UTC);
    }

    static get NEVER_EXPIRE_DATE_TIME_ISO() {
        return EzDateTime.toIso(EzDateTime.NEVER_EXPIRE_MOMENT_UTC);
    }

    static getNeverExpireDateTimeIso(noZone, noTime, noMillisecondsOrSeconds, noMilliseconds) {
        if (EzBoolean.isTrue(noTime)) {
            return EzDateTime.NEVER_EXPIRE_DATE_ISO;
        }

        if (EzBoolean.isTrue(noMillisecondsOrSeconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00`
                : `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00Z`;
        }

        if (EzBoolean.isTrue(noMilliseconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00:00`
                : `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00:00Z`;
        }

        return EzDateTime.NEVER_SET_DATE_TIME_ISO;
    }

    static get NEVER_EXPIRE_DISPLAY_DATE() {
        return 'NEVER';
    }

    /**
     * @static
     * @public @function
     * Determines if the provided momentInstance (or possibly string) represents a never expire date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals EzChar.DASH
     *      starts with '9999/01/01'
     *      starts with '9999.01.01'
     *      starts with '01/01/9999'
     *      starts with '01-01-9999'
     *      starts with '01.01.9999'
     *      equals '9999-01-01T23:59:59:999Z'
     *      equals '9999-01-01T23:59:59:999'
     *      equals '9999-01-01T23:59:59Z'
     *      equals '9999-01-01T23:59:59'
     *      equals '9999-01-01T23:59Z'
     *      equals '9999-01-01T23:59'
     *      equals '9999-01-01'
     * @returns {boolean}
     */
    static isNeverExpire(momentInstanceOrString) {
        if (EzString.isString(momentInstanceOrString)) {
            momentInstanceOrString = momentInstanceOrString.toUpperCase();

            return !EzString.hasLength(momentInstanceOrString) ||
                EzDateTime.NEVER_EXPIRE_DISPLAY_DATE === momentInstanceOrString ||
                EzDateTime.NEVER_EXPIRE_DATE_ISO === momentInstanceOrString ||
                EzDateTime.NEVER_EXPIRE_DATE_TIME_ISO === momentInstanceOrString ||
                '01-01-9999' === momentInstanceOrString ||
                // No zone
                EzDateTime.getNeverExpireDateTimeIso(true, false, false, false) === momentInstanceOrString ||
                // No milliseconds
                EzDateTime.getNeverExpireDateTimeIso(false, false, false, true) === momentInstanceOrString ||
                // No zone, no milliseconds
                EzDateTime.getNeverExpireDateTimeIso(true, false, false, true) === momentInstanceOrString ||
                // No milliseconds, no seconds
                EzDateTime.getNeverExpireDateTimeIso(false, false, true, true) === momentInstanceOrString ||
                // No Zone, no milliseconds, no seconds
                EzDateTime.getNeverExpireDateTimeIso(true, false, true, true) === momentInstanceOrString ||
                momentInstanceOrString.startsWith('9999/01/01') ||
                momentInstanceOrString.startsWith('9999.01.01') ||
                momentInstanceOrString.startsWith('01/01/9999') ||
                momentInstanceOrString.startsWith('01-01-9999') ||
                momentInstanceOrString.startsWith('01.01.9999');
        }

        return EzDateTime.isValidMoment(momentInstanceOrString)
            ? EzDateTime.NEVER_EXPIRE_DATE_ISO === EzDateTime.toIsoDate(
                EzDateTime.toUTC(momentInstanceOrString))
            : false;
    }

    /**
     * @static
     * @public @function
     * Determines if the provided momentInstance (or possibly string) represents a never expire date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals EzChar.DASH
     *      starts with '9999/01/01'
     *      starts with '9999.01.01'
     *      starts with '01/01/9999'
     *      starts with '01-01-9999'
     *      starts with '01.01.9999'
     *      equals '9999-01-01T23:59:59:999Z'
     *      equals '9999-01-01T23:59:59:999'
     *      equals '9999-01-01T23:59:59Z'
     *      equals '9999-01-01T23:59:59'
     *      equals '9999-01-01T23:59Z'
     *      equals '9999-01-01T23:59'
     *      equals '9999-01-01'
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isNeverExpire(momentInstanceOrString)
     */
    ezIsNeverExpire = EzDateTime.isNeverExpire;

    /**
     * @static
     * @public @function
     * Determines if the provided momentInstance (or possibly string) represents a never set date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals 'NEVER'
     *      starts with '0000/01/01'
     *      starts with '0000.01.01'
     *      starts with '01/01/0000'
     *      starts with '01-01-0000'
     *      starts with '01.01.0000'
     *      equals '0000-01-01T00:00:00:000Z'
     *      equals '0000-01-01T00:00:00:000'
     *      equals '0000-01-01T00:00:00Z'
     *      equals '0000-01-01T00:00:00'
     *      equals '0000-01-01T00:00Z'
     *      equals '0000-01-01T00:00'
     *      equals '0000-01-01'
     * @returns {boolean}
     */
    static isNeverSet(momentInstanceOrString) {
        if (EzString.isString(momentInstanceOrString)) {
            momentInstanceOrString = momentInstanceOrString.toUpperCase();

            return !EzString.hasLength(momentInstanceOrString) ||
                EzDateTime.NEVER_SET_DISPLAY_DATE === momentInstanceOrString ||
                EzDateTime.NEVER_SET_DATE_ISO === momentInstanceOrString ||
                EzDateTime.NEVER_SET_DATE_TIME_ISO === momentInstanceOrString ||
                '01-01-0000' === momentInstanceOrString ||
                // No zone
                EzDateTime.getNeverSetDateTimeIso(true, false, false, false) === momentInstanceOrString ||
                // No milliseconds
                EzDateTime.getNeverSetDateTimeIso(false, false, false, true) === momentInstanceOrString ||
                // No zone, no milliseconds
                EzDateTime.getNeverSetDateTimeIso(true, false, false, true) === momentInstanceOrString ||
                // No milliseconds, no seconds
                EzDateTime.getNeverSetDateTimeIso(false, false, true, true) === momentInstanceOrString ||
                // No Zone, no milliseconds, no seconds
                EzDateTime.getNeverSetDateTimeIso(true, false, true, true) === momentInstanceOrString ||
                momentInstanceOrString.startsWith('0000/01/01') ||
                momentInstanceOrString.startsWith('0000.01.01') ||
                momentInstanceOrString.startsWith('01/01/0000') ||
                momentInstanceOrString.startsWith('01-01-0000') ||
                momentInstanceOrString.startsWith('01.01.0000');
        } else if (EzDateTime.isValidMoment(momentInstanceOrString)) {
            const isoDate = EzDateTime.toIsoDate(
                EzDateTime.toUTC(momentInstanceOrString));

            return EzDateTime.NEVER_SET_DATE_ISO === isoDate || '0000-01-01' === isoDate;
        }

        return false;
    }

    /**
     * @public @function
     * Determines if the provided momentInstance (or possibly string) represents a never set date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals 'NEVER'
     *      starts with '0000/01/01'
     *      starts with '0000.01.01'
     *      starts with '01/01/0000'
     *      starts with '01-01-0000'
     *      starts with '01.01.0000'
     *      equals '0000-01-01T00:00:00:000Z'
     *      equals '0000-01-01T00:00:00:000'
     *      equals '0000-01-01T00:00:00Z'
     *      equals '0000-01-01T00:00:00'
     *      equals '0000-01-01T00:00Z'
     *      equals '0000-01-01T00:00'
     *      equals '0000-01-01'
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isNeverSet(momentInstanceOrString)
     */
    ezIsNeverSet = EzDateTime.isNeverSet;

    /**
     * @static
     * @public @function
     * Transforms the provided isoDateTime to a displayable date
     * @param {undefined|null|string} isoDateTime
     * @returns {string}
     */
    static isoToDisplayDate(isoDateTime) {
        if (!EzString.hasText(isoDateTime)) {
            return EzString.EMPTY;
        }

        if (EzDateTime.isNeverExpire(isoDateTime)) {
            return EzDateTime.NEVER_EXPIRE_DISPLAY_DATE;
        }

        if (EzDateTime.isNeverSet(isoDateTime)) {
            return EzDateTime.NEVER_SET_DISPLAY_DATE;
        }

        try {
            const momentInstance = EzDateTime.fromIso(isoDateTime);

            return EzDateTime.toDisplayDate(momentInstance);
        } catch (err) {
            const em = EzString.em`
                Unable to transform iso date time '${isoDateTime} to a display date
                due to the following error: ${err.message}`;

            if (globalThis?.ezApi?.ezclocker?.ezLogger) {
                globalThis.ezApi.ezclocker.ezLogger.error(em);
            } else if (globalThis?.console) {
                globalThis.console.error(em);
            }

            return isoDateTime;
        }
    }

    /**
     * @public @function
     * Transforms the provided isoDateTime to a displayable date
     * @param {undefined|null|string} isoDateTime
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.isoToDisplayDate(isoDateTime)
     */
    ezIsoToDisplayDate = EzDateTime.isoToDisplayDate;

    /**
     * @static
     * @public @function
     * Transforms the provided isoDateTime to a displayable string in format: MM/YYYY
     * @param {undefined|null|string} isoDateTime
     * @returns {string}
     */
    static isoToDisplayMonthYear(isoDateTime) {
        if (!EzString.hasText(isoDateTime)) {
            return EzString.EMPTY;
        }

        try {
            const momentInstance = EzDateTime.fromIso(isoDateTime);

            if (EzDateTime.isNeverExpire(momentInstance)) {
                return EzDateTime.NEVER_EXPIRE_DISPLAY_DATE;
            }

            if (EzDateTime.isNeverSet(momentInstance)) {
                return EzDateTime.NEVER_SET_DISPLAY_DATE;
            }

            return EzDateTime.toDisplayMonthYear(momentInstance);
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Unable to transform iso date time '${isoDateTime} to a display month and year format
                    due to the following error: ${err.message}`);

            return isoDateTime;
        }
    }

    /**
     * @public @function
     * Transforms the provided isoDateTime to a displayable string in format: MM/YYYY
     * @param {undefined|null|string} isoDateTime
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.isoToDisplayMonthYear(isoDateTime)
     */
    ezIsoToDisplayMonthYear = EzDateTime.isoToDisplayMonthYear;

    /**
     * @static
     * @public @function
     * Transforms the provided momentInstance to a display string in formation: MM/YYYY
     * @param {moment} momentInstance
     * @returns {string}
     */
    static toDisplayMonthYear(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDisplayMonthYear);
        }

        if (EzDateTime.isNeverExpire(momentInstance)) {
            return EzDateTime.NEVER_EXPIRE_DATE_TIME_ISO;
        }

        if (EzDateTime.isNeverSet(momentInstance)) {
            return EzDateTime.NEVER_SET_DISPLAY_DATE;
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.DEFAULT_MONTH_YEAR_DISPLAY_FORMAT);
    }

    /**
     * @public @function
     * Transforms the provided momentInstance to a display string in formation: MM/YYYY
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayMonthYear(momentInstance)
     */
    ezToDisplayMonthYear = EzDateTime.ezToDisplayMonthYear;

    /**
     * @static
     * @public @function
     * Returns the string formatted using the ezPreferredTimeFormat (default = hh:mm) a for the provided moment.js instance.
     * @param {moment} momentInstance
     * Default: null
     * @returns {string}
     */
    static toDisplayTime(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDisplayDate);
        }

        if (EzDateTime.isNeverExpire(momentInstance)) {
            return '11:59 a';
        }

        if (EzDateTime.isNeverSet(momentInstance)) {
            return '12:00 a';
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.ezPreferredTimeFormat);
    }

    /**
     * @static
     * @public @function
     * Returns the string formatted ezPreferredDateFormat (default = MM/DD/YYYY) for the provided moment.js instance.
     * @param {moment} momentInstance
     * Default: null
     * @returns {string}
     */
    static toDisplayDate(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDisplayDate);
        }

        if (EzDateTime.isNeverExpire(momentInstance)) {
            return EzDateTime.NEVER_EXPIRE_DISPLAY_DATE;
        }

        if (EzDateTime.isNeverSet(momentInstance)) {
            return EzDateTime.NEVER_SET_DISPLAY_DATE;
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.ezPreferredDateFormat);
    }

    /**
     * @public @function
     * Returns the string formatted MM/DD/YYYY for the provided moment.js instance. If none provided,
     * returns the current date instead.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayDate(momentInstance)
     */
    ezToDisplayDate = EzDateTime.toDisplayDate;

    /**
     * @public @function
     * Get the name of the day
     * @returns {string}
     */
    static toDayNameDisplay(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDayNameDisplay);
        }

        return EzDateTime.momentWithPTZ(momentInstance).format('dddd');
    }

    /**
     * @public @function
     * Get the name of the day
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDayNameDisplay(momentInstance)
     */
    ezToDayNameDisplay = EzDateTime.toDayNameDisplay;

    /**
     * @static
     * @public @function
     * Returns the day name plus date string for display using the preferred moment day name and ate format value.
     * @param {moment} momentInstance
     * @returns {string}
     */
    static toDisplayDayNameAndDate(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDisplayDayNameAndDate);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.ezPreferredMomentDayNameAndDateFormat);
    }

    /**
     * @public @function
     * Returns the day name plus date string for display using the preferred moment day name and ate format value.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayDayNameAndDate(momentInstance)
     */
    ezToDisplayDayNameAndDate = EzDateTime.toDisplayDayNameAndDate;

    /**
     * @static
     * @public @function
     * Returns a date range in format: "{sDate} - {eDate}"
     * @param {moment} sMoment
     * @param {moment} eMoment
     * @returns {string}
     */
    static toDisplayDateRange(sMoment, eMoment) {
        if (!EzDateTime.isValidMoment(sMoment)) {
            throw new EzBadParamException(
                'sMoment',
                EzDateTime,
                EzDateTime.toDisplayDateRange);
        }

        if (!EzDateTime.isValidMoment(eMoment)) {
            throw new EzBadParamException(
                'eMoment',
                EzDateTime,
                EzDateTime.toDisplayDateRange);
        }

        const displayStartDate = EzDateTime.momentWithPTZ(sMoment)
            .format(EzDateTime.ezPreferredDateFormat);

        const displayEndDate = EzDateTime.momentWithPTZ(eMoment)
            .format(EzDateTime.ezPreferredDateFormat);

        return `${displayStartDate} - ${displayEndDate}`;
    }

    /**
     * @public @function
     * Returns a date range in format: "{sDate} - {eDate}"
     * @param {moment} sMoment
     * @param {moment} eMoment
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayDateRange(sMoment, eMoment)
     */
    ezToDisplayDateRange = EzDateTime.toDisplayDateRange;

    /**
     * @public @function
     * Returns the string hh:mm a format of the provided moment. If the moment is not valid, then the current time is used.
     * @param {moment} momentInstance
     * @returns {string}
     */
    static toTimeString(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toTimeString);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.DTFORMAT_hh_mm_a);
    }

    /**
     * @public @function
     * Returns the string hh:mm a format of the provided moment. If the moment is not valid, then the current time is used.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toTimeString(momentInstance)
     */
    ezTimeString = EzDateTime.toTimeString;

    /**
     * @static
     * @public @function
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string}
     */
    static toDisplayDateTime(momentInstance = null, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.toDisplayDateTime);
        }

        const dateTimeFormatToUse = EzString.stringWithTextOrDefault(
            optionalDateTimeFormat,
            EzDateTime.ezPreferredDateTimeFormat);

        try {
            return EzDateTime.momentWithPTZ(momentInstance).format(dateTimeFormatToUse);
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time due to the following error:
                    ${err.message}
                    [momentInstance: ${EzDateTime.toIso(momentInstance)}]
                    [Date time format string: ${dateTimeFormatToUse}]`);

            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.toDisplayDateTime);
        }
    }

    /**
     * @public @function
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayDateTime(momentInstance, optionalDateTimeFormat)
     */
    ezToDisplayDateTime = EzDateTime.toDisplayDateTime;

    /**
     * @public @function
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string}
     */
    ezToDisplayTimeStampDateTime(momentInstance = null, optionalDateTimeFormat = EzDateTime.DEFAULT_MOMENT_TIMESTAMP_DISPLAY_FORMAT) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time due to the following error:
                    ${err.message}`);

            return EzString.EMPTY;
        }
    }

    /**
     * @public @function
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {any} defaultValue
     * Default: EzString.EMPTY
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string|any}
     */
    ezToDisplayDateTimeOrDefault(momentInstance = null, defaultValue = EzString.EMPTY, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return defaultValue;
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time due to the following error:
                    ${err.message}`);

            return defaultValue;
        }
    }

    /**
     * @public @function
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     * @returns {string}
     */
    ezToDisplayDateTimeWithSeconds(momentInstance = null, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeWithSecondsFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateTimeWithSeconds);
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeWithSecondsFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time with seconds format due to the following error:
                    ${err.message}`);

            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateTimeWithSeconds);
        }
    }

    /**
     * @public @function
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {any} defaultValue
     * Default: EzString.EMPTY
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     * @returns {string|any}
     */
    ezToDisplayDateTimeWithSecondsOrDefault(momentInstance = null, defaultValue = EzString.EMPTY,
        optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeWithSecondsFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return defaultValue;
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeWithSecondsFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time with seconds format due to the following error:
                    ${err.message}`);

            return defaultValue;
        }
    }

    /**
     * @public @function
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     * @returns {string}
     */
    ezToDisplayDateTimeWithSecondsOrEmpty(momentInstance = null, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeWithSecondsFormat) {
        return EzDateTime.ezInstance.ezToDisplayDateTimeWithSecondsOrDefault(
            momentInstance,
            EzString.EMPTY,
            optionalDateTimeFormat);
    }

    /**
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toIso()
     */
    ezToIsoDateTime(momentInstance) {
        return EzDateTime.toIso(momentInstance);
    }

    /**
     * @static
     * @public @function
     * Converts the provided moment to a ISO date string in the format of YYYY-MM-DD
     * Template: "YYYY-MM-DD"
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {moment} momentInstance
     * Default: null
     * @returns {string}
     */
    static toIsoDate(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        momentInstance = EzDateTime.momentWithPTZ(momentInstance);

        return momentInstance.format('YYYY-MM-DD');
    }

    ezToIsoDate = EzDateTime.toIsoDate;

    /**
     * @static
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    static toIso(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIsoOrDefault(
            momentInstance,
            timeZoneId,
            EzString.EMPTY);
    }

    /**
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * @deprecated
     * Migrate to static method: EzDateTime.toIso(momentInstnace, timeZoneId)
     */
    ezToIso = EzDateTime.toIso;

    /**
     * @static
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} defaultIso
     * Default: EzDateTime.nowAsIso()
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    static toIsoOrDefault(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId, defaultValue = EzDateTime.nowAsIso()) {
        try {
            return !EzDateTime.isValidMoment(momentInstance)
                ? EzDateTime.isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(
                    EzString.stringWithLengthOrDefault(
                        defaultValue,
                        EzDateTime.nowAsIso(timeZoneId)))
                : EzDateTime.isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(
                    momentInstance.tz(EzDateTime.safeTimeZoneId(timeZoneId))
                        .format(EzDateTime.DEFAULT_ISO_DATE_TIME_FORMAT));
        } catch (err) {
            EzLogger.error(
                EzString.em`
                    Unable to convert the provided momentInstance to an ISO string due to the following error:
                    ${EzString.stringOrDefault(err?.message, 'Unnamed excception without message')}`,
                err);

            return EzDateTime.isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(
                EzString.stringWithLengthOrDefault(
                    defaultValue,
                    EzDateTime.nowAsIso(timeZoneId)));
        }
    }

    /**
     * @static
     * @public @function
     * Transforms the provided isoDateTimeWithUTCTimeZone based upon the following logic:
     * 1) If the provied isoDateTimeWithUTCTimeZone has text:
     * ====> 1a) If the provided isoDateTimeWithUTCTimeZone includes text '+00:00' then transform the isoDateTimeWithUTCTimeZone's '+00:00' text to EzChar.Z and return
     * the updated isoDateTimeWithUTCTimeZone value.
     * ====> 1b) If the provided isoDateTimeWithUTCTimeZone does not includes text '+00:00' then return isoDateTimeWithUTCTimeZone value as provided.
     * 2) If the provied isoDateTimeWithUTCTimeZone is undefined, null, or is only whitespace then eturn isoDateTimeWithUTCTimeZone value as provided.
     */
    static isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(isoDateTimeWithUTCTimeZone = null) {
        return EzString.hasText(isoDateTimeWithUTCTimeZone) && isoDateTimeWithUTCTimeZone.includes('+00:00')
            ? isoDateTimeWithUTCTimeZone.replace('+00:00', EzChar.Z)
            : isoDateTimeWithUTCTimeZone;
    }

    /**
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} defaultIso
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * @deprecated
     * Migrate to static method: EzDateTime.toIsoOrDefault(momentInstance, timeZoneId, defaultValue)
     */
    ezToIsoOrDefault = EzDateTime.toIsoOrDefault;

    /**
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    ezToIsoMaintainZone(momentInstance) {
        return EzDateTime.ezInstance.ezToIsoMaintainZoneOrDefault(
            momentInstance,
            EzString.EMPTY);
    }

    /**
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} defaultIso
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    ezToIsoMaintainZoneOrDefault(momentInstance, defaultValue = globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()) {
        let result;

        if (!EzDateTime.isValidMoment(momentInstance)) {
            result = EzString.stringWithLengthOrDefault(
                defaultValue,
                globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso());
        } else {
            try {
                result = momentInstance.format(EzDateTime.DEFAULT_ISO_DATE_TIME_FORMAT);
            } catch (err) {
                globalThis.ezApi.ezclocker.ezLogger.error(
                    `Unable to convert the provided momentInstance to an ISO string due to the following error: ${err.message}`,
                    err);

                result = EzString.stringWithLengthOrDefault(
                    defaultValue,
                    globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso());
            }
        }

        if (result.includes('+00:00')) {
            result = result.replace('+00:00', EzChar.Z);
        }

        return result;
    }

    /**
     * @public @function
     * Generates a default time iso from EzDateTime.now()
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: False
     * @param {boolean} noSeconds
     * Default: True
     * @param {boolean} noMilliseconds
     * Default: True
     * @returns {string}
     */
    static generateDefaultTimeIso(timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true) {
        const nowDateTime = EzDateTime.now();

        let defaultTimeIso;

        if (EzBoolean.booleanOrTrue(noSeconds)) {
            defaultTimeIso = EzBoolean.booleanOrFalse(noZone)
                ? nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mm')
                : nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mmZ');
        } else if (EzBoolean.booleanOrTrue(noMilliseconds)) {
            defaultTimeIso = EzBoolean.booleanOrFalse(noZone)
                ? nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mm:ss')
                : nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mm:ssZ');
        }

        return defaultTimeIso;
    }

    /**
     * @public @function
     * Generates a default time iso from EzDateTime.now()
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: False
     * @param {boolean} noSeconds
     * Default: True
     * @param {boolean} noMilliseconds
     * Default: True
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.generateDefaultTimeIso(timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true)
     */
    ezGenerateDefaultTimeIso = EzDateTime.generateDefaultTimeIso;

    /**
     * @static
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     */
    static toTimeIso(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true) {
        return EzDateTime.toTimeIsoOrDefault(
            momentInstance,
            timeZoneId,
            noZone,
            noSeconds,
            noMilliseconds,
            EzDateTime.generateDefaultTimeIso(
                timeZoneId,
                noZone,
                noSeconds,
                noMilliseconds));
    }

    /**
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     * @deprecated
     * Migrate to static method: EzDateTime.toTimeIso(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true)
     */
    ezToTimeIso = EzDateTime.toTimeIso;

    /**
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @param {undefined|null|string} defaultTimeIso
     * Default: Now time formatted as: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     */
    static toTimeIsoOrDefault(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true,
        defaultTimeIso = EzString.EMPTY) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.hasLength(defaultTimeIso)
                ? defaultTimeIso
                : EzDateTime.generateDefaultTimeIso(
                    timeZoneId,
                    noZone,
                    noSeconds,
                    noMilliseconds);
        }

        if (EzBoolean.booleanOrTrue(noSeconds)) {
            return EzBoolean.booleanOrFalse(noZone)
                ? momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mm')
                : momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mmZ');
        } else if (EzBoolean.booleanOrTrue(noMilliseconds)) {
            return EzBoolean.booleanOrFalse(noZone)
                ? momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mm:ss')
                : momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mm:ssZ');
        }

        return EzBoolean.booleanOrFalse(noZone)
            ? momentInstance.tz(
                EzDateTime.safeTimeZoneId(timeZoneId))
                .format('HH:mm:ss.SSS')
            : momentInstance.tz(
                EzDateTime.safeTimeZoneId(timeZoneId))
                .format('HH:mm:ss.SSSZ');
    }

    /**
     * @public @function
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @param {undefined|null|string} defaultTimeIso
     * Default: Now time formatted as: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     * @deprecated
     * Migrate to static method: EzDateTime.toTimeIsoOrDefault(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true,
     *         defaultTimeIso = EzString.EMPTY)
     */
    ezToTimeIsoOrDefault = EzDateTime.toTimeIsoOrDefault

    /**
     * @public @function
     * Returns the moment as a 'UNIX' MS timestamp.
     * Template: "x"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @returns {tsring}
     */
    ezToTimestamp(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return EzDateTime.momentWithPTZ(momentInstance).format(EzChar.x);
    }

    /**
     * @public @function
     * Returns the provided date as a version string in the formation: 'YYYYMMDDHHmmssSSS'
     * See https://momentjs.com/docs/#/displaying/ for formatting string details.
     * @param {moment} momentInstance
     * @returns {String}
     */
    ezToVersionStamp(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return EzDateTime.momentWithPTZ(momentInstance).format('YYYYMMDDHHmmssSSS');
    }

    /**
     * @public @readonly @property
     * Returns the current date and time as a version string in the formation: 'YYYYMMDDHHmmssSSS'
     * See https://momentjs.com/docs/#/displaying/ for formatting string details.
     * @returns {String}
     */
    get ezNowVersionStamp() {
        return EzDateTime.now().format('YYYYMMDDHHmmssSSS');
    }

    /**
     * @static
     * @public @function
     * Returns the moment.now() as a 'UNIX' MS timestamp.
     * Template: "x"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {string} [timeZoneId=EzDateTime.ezActiveTimeZoneId]
     * @returns {string}
     */
    static nowAsTimestamp(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.now(
            EzString.stringWithTextOrDefault(timeZoneId, EzDateTime.ezActiveTimeZoneId))
            .format(EzChar.x);
    }

    /**
     * @public @function
     * Returns the moment.now() as a 'UNIX' MS timestamp.
     * Template: "x"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {string} [timeZoneId=EzDateTime.ezActiveTimeZoneId]
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.nowAsTimestamp(timeZoneId)
     */
    ezNowAsTimestamp = EzDateTime.nowAsTimestamp;

    /**
     * @public @function
     * Returns the moment as a ISO String in UTC time zone
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDateTime.ezToIsoUTC(momentInstance)
     */
    ezToUTCIsoDateTime(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return EzDateTime.toIso(
            EzDateTime.withZoneUTC(momentInstance));
    }

    /**
     * @public @function
     * Returns the moment as a ISO String in UTC time zone
     * @param {moment} momentInstance
     * @returns {string}
     */
    static withZoneUTC(momentInstance) {
        return EzDateTime.withZone(momentInstance, EzDateTime.timeZoneIds.UTC);
    }

    /**
     * @public @function
     * Returns the moment as a ISO String in UTC time zone
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezToIsoUTC = EzDateTime.withZoneUTC;

    /**
     * @public @function
     * Returns a moment instance in the preferred timezone for the date, with time at the beginning of the date.
     * @param {number} aMonth
     * From 0 to 11
     * @param {number} aDay
     * From 1 to 31
     * @param {number} aYear
     * @returns {moment}
     */
    ezFromDate(aMonth, aDay, aYear) {
        if (!EzNumber.isNumber(aMonth)) {
            throw new EzBadParamException(
                'aMonth',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDate);
        }
        if (!EzNumber.isNumber(aDay)) {
            throw new EzBadParamException(
                'aDay',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDate);
        }
        if (!EzNumber.isNumber(aYear)) {
            throw new EzBadParamException(
                'aYear',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDate);
        }

        return EzDateTime.now()
            .set('year', aYear)
            .set('date', aDay)
            .set('month', aMonth)
            .set('hour', EzNumber.ZERO)
            .set('minute', EzNumber.ZERO)
            .set('second', EzNumber.ZERO)
            .set('millisecond', EzNumber.ZERO);
    }

    /**
     * @public @function
     * Returns the moment from the provided date string in format mm/dd/yyyy
     * @param {string} dateString
     * @returns {moment}
     */
    ezFromDateString(dateString) {
        if (!EzString.hasLength(dateString)) {
            throw new EzBadParamException(
                'dateString',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDateString);
        }

        const dateParts = dateString.split(EzChar.FORWARD_SLASH);

        // month is zero based
        return EzDateTime.ezInstance.ezFromDate(
            EzNumber.strToInt(dateParts[EzNumber.FIRST_INDEX]) - EzNumber.ONE,
            EzNumber.strToInt(dateParts[EzNumber.SECOND_INDEX]),
            EzNumber.strToInt(dateParts[EzNumber.THIRD_INDEX]));
    }

    /**
     * @static
     * @public @function
     * Creates a moment date time instance from the provided dateString that is expected to be in either the provided dateStringFormat OR
     * the EzDateTime.ezPreferredDateFormat.
     * Optionally sets the moment instance's time zone to the provided timeZoneId
     * @param {string} dateString
     * IMPORTANT: The provided dateString must match the provided format or EzDateTimeezPerferredDateForm string.
     * Default: null
     * @param {undefiend|null|string} dateFormatString
     * Default: EzDateTime.ezPreferredDateFormat
     * @param {undefiend|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static dateTimeFromDateStringInFormat(dateString = null, dateFormatString = EzDateTime.ezPreferredDateFormat, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(dateString)) {
            throw new EzBadParamException(
                { dateString },
                EzDateTime,
                EzDateTime.dateTimeFromDateStringInFormat);
        }

        if (!EzString.hasText(dateFormatString)) {
            dateFormatString = EzDateTime.ezPreferredDateFormat;
        }

        return moment.tz(
            EzDateTime.patchBadDecodedIso(dateString),
            EzString.stringWithTextOrDefault(
                dateFormatString,
                EzDateTime.ezPreferredDateFormat),
            EzDateTime.safeTimeZoneId(timeZoneId));
    }

    /**
     * @public @function
     * Creates a moment date time instance from the provided dateString that is expected to be in either the provided dateStringFormat OR
     * the EzDateTime.ezPreferredDateFormat.
     * Optionally sets the moment instance's time zone to the provided timeZoneId
     * @param {string} dateString
     * IMPORTANT: The provided dateString must match the provided format or EzDateTimeezPerferredDateForm string.
     * Default: null
     * @deprecated
     * Migrate to static method: EzDateTime.dateTimeFromDateStringInFormat(dateString, dateFormatString, timeZoneId)
     */
    ezMomentFromDateStringAllFormats = EzDateTime.dateTimeFromDateStringInFormat;

    /**
     * @public @function
     * Returns the moment from the provided date string.
     * String is expected to be in the format of the  EzDateTime.ezInstance.ezPreferredDateFormat result
     * @param {string} preferredDateString
     * @returns {moment}
     */
    ezMomentFromPreferredDateString(preferredDateString) {
        if (!EzString.hasLength(preferredDateString)) {
            throw new EzBadParamException(
                'preferredDateString',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromPreferredDateString);
        }

        const momentInstance = moment(
            preferredDateString,
            EzDateTime.ezPreferredDateFormat,
            true);

        if (!momentInstance.isValid()) {
            throw new EzException(
                EzString.em`
                    The provided preferredDateString value of "${preferredDateString}" did not translate to a momentjs instance.
                    Assumed formatting of ${EzDateTime.ezPreferredDateFormat}`);
        }

        return EzDateTime.momentWithPTZ(momentInstance);
    }

    /**
     * @public @function
     * Returns the preferred timezone moment instance if aValue is valid,
     * or the default timezone instance if aValue is not valid.
     * @param {string} aValue
     * @returns {moment}
     */
    ezCreateFromValue(aValue) {
        if (!EzString.hasLength(aValue)) {
            throw new EzBadParamException(
                'aValue',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromValue);
        }

        const momentInstance = !moment(aValue);

        return momentInstance.isValid()
            ? EzDateTime.momentWithPTZ(momentInstance)
            : EzDateTime.nowWithStartOfDay();
    }

    /**
     * @public @function
     * @param {string} dateTimeString
     * @param {string} expectedFormat
     * @returns {moment}
     */
    ezCreateFromValueInFormat(dateTimeString, expectedFormat) {
        if (!EzString.hasLength(dateTimeString) || !EzString.hasLength(expectedFormat)) {
            return null;
        }

        const momentInstance = moment(dateTimeString, expectedFormat);

        return momentInstance.isValid()
            ? EzDateTime.momentWithPTZ(momentInstance)
            : EzDateTime.nowWithStartOfDay();
    }

    /**
     * @static
     * @public @function
     * Returns the current date and time as an ISO string
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     */
    static nowAsIso(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIso(
            EzDateTime.now(
                EzDateTime.safeTimeZoneId(timeZoneId)));
    }

    /**
     * @public @function
     * Returns the current date and time as an ISO string
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     */
    ezNowAsIso = EzDateTime.nowAsIso;

    /**
     * @static
     * @public @function
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static now(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        try {
            return moment().tz(EzDateTime.safeTimeZoneId(timeZoneId));
        } catch (err) {
            if (!EzString.hasText(err?.message)) {
                err.message = `Unexpected error when creating a new date time instance (moment) with timeZoneId=${timeZoneId}.`;
            }

            globalThis?.console?.error(
                EzString.em`
                    Failed to get date time instance for now with timeZoneId=${timeZoneId}
                    due to the following error: ${err.message}`,
                err);

            // Re-try by using the active time zone id:
            try {
                return moment.tz(
                    EzDateTime.safeTimeZoneId(EzDateTime.ezActiveTimeZoneId));
            } catch (err2) {
                globalThis?.console?.error(
                    EzString.em`
                        Failed to get date time instance for now with timeZoneId=${timeZoneId}
                        due to the following error: ${err.message}`,
                    err2);

                // Last try using what ever the default time zone id:
                return moment();
            }
        }
    }

    /**
     * @public @function
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.now(timeZoneId)
     */
    ezNow = EzDateTime.now;

    /**
     * @static
     * @public @function
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     */
    static nowWithStartOfDay(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.now(timeZoneId));
    }

    /**
     * @public @function
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithStartOfDay(timeZoneId)
     */
    ezNowWithStartOfDay = EzDateTime.nowWithStartOfDay;

    /**
     * @static
     * @public @function
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     */
    static nowWithStartOfDayIso(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIso(
            EzDateTime.nowWithStartOfDay(timeZoneId));
    }

    /**
     * @public @function
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithStartOfDayIso(timeZoneId)
     */
    ezNowWithStartOfDayIso = EzDateTime.nowWithStartOfDayIso;

    /**
     * @static
     * @public @function
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 23:59:59.999.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     */
    static nowWithEndOfDay(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.now(timeZoneId).endOf('day');
    }

    /**
     * @public @function
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 23:59:59.999.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithEndOfDay(timeZoneId)
     */
    ezNowWithEndOfDay = EzDateTime.nowWithEndOfDay;

    /**
     * @static
     * @public @function
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     */
    static nowWithEndOfDayIso(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIso(
            EzDateTime.nowWithEndOfDay(timeZoneId));
    }

    /**
     * @public @function
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithStartOfDayIso(timeZoneId)
     */
    ezNowWithEndOfDayIso = EzDateTime.nowWithEndOfDayIso;

    /**
     * @static
     * @public @function
     * Returns a moment with the date set to the start of the month for the
     * provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    static momentAtStartOfMonth(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                momentInstance,
                EzDateTime,
                EzDateTime.momentAtStartOfMonth);
        }

        return EzDateTime.momentWithPTZ(momentInstance).startOf('month');
    }

    /**
     * @public @function
     * Returns a moment with the date set to the start of the month for the
     * provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.momentAtStartOfMonth(momentInstance)
     */
    ezMomentAtStartOfMonth = EzDateTime.momentAtStartOfMonth;

    /**
     * @public @function
     * Returns a moment with the date set to the start of the month for the
     * provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezMomentAtEndOfMonth(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                momentInstance,
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezStartOfMonthForMoment);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .endOf('month');
    }

    /**
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new temp moment instance from the provided momentInstance in the globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone.
     *  2) Creates a new moment instance from the temp instance with:
     *      a) The year from the provided momentInstance
     *      b) The month set as 1 (Janurary)
     *      c) The date in the month as 1
     *      d) The time at the beginning of the day (00:00:00.000)
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezSetMomentFirstDayOfYear(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetMomentFirstDayOfYear);
        }

        return EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId).date(1).day(1);
    }

    /**
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new temp moment instance from the provided momentInstance in the globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone.
     *  2) Creates a new moment instance from the temp instance with:
     *      a) The year from the provided momentInstance
     *      b) The month set as 1 (Janurary)
     *      c) The date in the month as 1
     *      d) The time at the end of the day (23:59:59.999)
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezSetMomentLastDayOfYear(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetMomentLastDayOfYear);
        }

        return EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId).date(12).day(31);
    }


    /**
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId)
     */
    ezSetMomentStartOfDay = EzDateTime.withTimeAtStartOfDay;

    /**
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Defualt: null
     * @param {undefiend|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId)
     */
    ezSetMomentEndOfDay = EzDateTime.withTimeAtEndOfDay;

    /**
     * @static
     * @public @function
     * Determines if the provided moment is a valid moment object.
     * @param {undefined|null|*|moment} momentInstance
     * Default: null
     * @returns {boolean}
     */
    static isValidMoment(momentInstance = null) {
        return EzObject.isValid(momentInstance) && momentInstance?._isAMomentObject && momentInstance?._isValid;
    }

    /**
     * @public @function
     * Determines if the provided moment is a valid moment object.
     * @param {undefined|null|*|moment} momentInstance
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to the static method: EzDateTime.isValidMoment(momentInstance = null)
     */
    ezIsValidMoment = EzDateTime.isValidMoment;

    /**
     * @static
     * @public @function
     * 1) If the provided momentInstance is a valid moment instance, then the momentInstance is returned transformed to the provided timeZoneId or
     * the default time zone id.
     * 2) If the provided momentInstance is not a valid moment instance, then the provided defaultMoment is returned based upon the following logic:
     *   2a) If the defaultMoment is a valid moment instance:
     *      a) If the timeZoneId is provided, then the defaultMoment is returned in the provided timeZoneId.
     *      b) If a timeZoneId is not provided, the the defaultMoment is returned as it was provided (does not apply the deafult timeZoneId)
     * 3) If the defaultMoment is not a valid moment instance, then the defaultMoment is returned as it was provided
     * @param {undefined|null|moment} [momentInstance=null]
     * @param {undefiend|null|moment|any} [defaultMoment=EzDateTime.now()]
     * @param {undefined|null|string} [timeZoneId=EzDateTime.ezActiveTimeZoneId]
     * @returns {undefined|null|moment|any}
     */
    static dateTimeOrDefault(momentInstance, defaultMoment, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.isValidMoment(momentInstance)
            ? EzDateTime.withZone(momentInstance, timeZoneId)
            : EzDateTime.isValidMoment(defaultMoment)
                ? EzString.hasText(timeZoneId)
                    ? EzDateTime.withZone(defaultMoment, timeZoneId)
                    : defaultMoment
                : defaultMoment;
    }

    /**
     * @static
     * @public @function
     * 1) If the provided momentInstance is a valid moment instance, then the momentInstance is returned transformed to the provided timeZoneId or
     * the default time zone id.
     * 2) If the provided momentInstance is not a valid moment instance, then null is returned.
     * @param {undefined|null|moment} [momentInstance=null]
     * @param {undefined|null|string} [timeZoneId=EzDateTime.ezActiveTimeZoneId]
     * @returns {null|moment}
     */
    static dateTimeOrNull(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.dateTimeOrDefault(
            momentInstance,
            null,
            timeZoneId);
    }

    /**
     * @static
     * @public @function
     * 1) If the provided momentInstance is a valid moment instance, then the momentInstance is returned transformed to the provided timeZoneId or
     * the default time zone id.
     * 2) If the provided momentInstance is not a valid moment instance, then EzDateTime.now() is returned transformed to the provided timeZoneId or
     * the EzDateTime.ezActiveTimeZoneId.
     * @param {undefined|null|moment} [momentInstance=null]
     * @param {undefined|null|string} [timeZoneId=EzDateTime.ezActiveTimeZoneId]
     * @returns {moment}
     */
    static dateTimeOrNow(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.dateTimeOrDefault(
            momentInstance,
            EzDateTime.now(timeZoneId));
    }

    /**
     * @public @function
     * Returns the provided momentInstance if it is a valid moment instance.
     * If the provided momentInstance is not a valid moment instance, then returns
     * a new moment instance with the current date and time.
     * @returns {moment}
     */
    ezDateTimeOrNow = EzDateTime.dateTimeOrNow;

    /**
     * @public @function
     * Returns the provided momentInstance if it is a valid moment instance.
     * If the provided momentInstance is not a valid moment instance, then returns
     * a new moment instance with the current date and time.
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.dateTimeOrNow(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    ezAssignMomentOrNow = EzDateTime.dateTimeOrNow;

    /**
     * @public @function
     * If the provided momentInstance is a vallid moment then the momentInstance is return with
     * it's time set to the start of the day.
     * If the provided momentInstance is not a valid moment, then now is returned
     * with it's time set to the start of the day.
     * @param {undefined|null|moment} momentInstance
     * @param {undefined|null|string} [timeZoneId=EzDateTime.ezActiveTimeZoneId]
     * @returns {moment}
     */
    ezDateTimeOrNowWithTimeAtStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.dateTimeOrNow(momentInstance, timeZoneId), timeZoneId);
    }

    /**
     * @public @function
     * If the provided momentInstance is a vallid moment then the momentInstance is return with
     * it's time set to the start of the day.
     * If the provided momentInstance is not a valid moment, then now is returned
     * with it's time set to the start of the day.
     * @param {undefined|null|moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * 1) From within the EzDateTime class:
     *      EzDateTime.ezInstance.ezDateTimeOrNowWithTimeAtStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     * 2) From outside the EzDateTime class:
     *      globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeOrNowWithTimeAtStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId);
     */
    ezAssignMomentOrNowThenSetTimeAtStartOfDay = this.ezDateTimeOrNowWithTimeAtStartOfDay;

    /**
     * @static
     * @public @function
     * Returns a time period object with:
     *  1) The start date as today's date with time at the beginning of the day in the active time zone.
     *  2) The end date as today's date plus 13 days with the time at the end of the day in the active time zone.
     * @returns {object}
     */
    static createDefaultSelectedPeriod(overrideStartMoment = null, overrideEndMoment = null) {
        const nowEzDateTimeWithTimeAtStartOfDayActive = EzDateTime.isValidMoment(overrideStartMoment)
            ? EzDateTime.withTimeAtStartOfDay(overrideStartMoment)
            : EzDateTime.nowWithStartOfDay();

        return EzDateTime.createSelectedPeriod(
            nowEzDateTimeWithTimeAtStartOfDayActive,
            EzDateTime.withTimeAtEndOfDay(
                EzDateTime.isValidMoment(overrideEndMoment)
                    ? overrideEndMoment
                    : EzDateTime.addDays(
                        nowEzDateTimeWithTimeAtStartOfDayActive,
                        EzNumber.THIRTEEN)));
    }

    /**
     * @public @function
     * Returns a time period object with:
     *  1) The start date as today's date with time at the beginning of the day in the active time zone.
     *  2) The end date as today's date plus 13 days with the time at the end of the day in the active time zone.
     * @returns {object}
     * @deprecated
     * Migreate to static method: EzDateTime.createDefaultSelectedPeriod()
     */
    ezCreateDefaultSelectedPeriod = EzDateTime.createDefaultSelectedPeriod;

    /**
     * @static
     * @public @function
     * Creates the default selected period object, sets the values as ISO strings, and converts to JSON
     * @returns {string}
     */
    static createDefaultSelectedPeriodJson() {
        const selectedPeriod = EzDateTime.createDefaultSelectedPeriod();

        selectedPeriod.ezPeriodStartMoment = EzDateTime.toIso(selectedPeriod.ezPeriodStartMoment);

        selectedPeriod.ezPeriodEndMoment = EzDateTime.toIso(selectedPeriod.ezPeriodEndMoment);

        return EzJson.toJson(selectedPeriod);
    }

    /**
     *  @public @function
     * Creates the default selected period object, sets the values as ISO strings, and converts to JSON
     * @returns {string}
     * @deprecated
     * Migreate to static method:  EzDateTime.createDefaultSelectedPeriodJso()
     */
    ezCreateDefaultSelectedPeriodJson = EzDateTime.createDefaultSelectedPeriodJson;

    /**
     * Transforms the provided momentInYear to the last date time of the year the
     * provided momentInYear is for.
     * @param {moment} momentInYear
     * @returns {moment}
     */
    ezToEndOfYear(momentInYear) {
        if (!EzDateTime.isValidMoment(momentInYear)) {
            throw new EzBadParamException(
                'momentInYear',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToEndOfYear);
        }

        return EzDateTime.momentWithPTZ(momentInYear)
            .endOf('year');
    }

    /**
     * @public @function
     * Returns the provied momentInstance as a display string formatted
     * for the date input controls.
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezToDatePickerDisplayDate(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDatePickerDisplayDate);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.DEFAULT_MM_DD_YYYY_FORMAT);
    }

    /**
     * @static
     * @public @function
     * Creates the default employee options
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     * @returns {object}
     * Returns time period object:
     *  {
     *      ezStartEzDateTimeActive: {moment},
     *      ezStartEzDateTimeUTC: {moment}
     *      ezStartIsoActive: {string},
     *      ezStartIsoUTC: {string},
     *      ezEndEzDateTimeActive: {moment},
     *      ezEndEzDateTimeUTC: {moment},
     *      ezEndIsoActive: {string},
     *      ezEndIsoUTC: {string},
     *      In addition, contains  additional Legacy properties: Please migrate to using of the properties above
     *      ezPeriodStartMoment: {moment},
     *      periodStartMoment: {moment},
     *      periodStartIso: {string},
     *      ezPeriodEndMoment: {moment},
     *      periodEndMoment: {moment},
     *      periodEndIso: {string}
     *  };
     */
    static createSelectedPeriod(periodStartMoment, periodEndMoment) {
        if (!EzDateTime.isValidMoment(periodStartMoment)) {
            throw new EzBadParamException(
                { periodStartMoment },
                EzDateTime,
                EzDateTime.createSelectedPeriod);
        }
        if (!EzDateTime.isValidMoment(periodEndMoment)) {
            throw new EzBadParamException(
                { periodEndMoment },
                EzDateTime,
                EzDateTime.createSelectedPeriod);
        }

        const startMomentActive = EzDateTime.withTimeAtStartOfDay(periodStartMoment);

        const endMomentActive = EzDateTime.withTimeAtEndOfDay(periodEndMoment);

        const startMomentUTC = EzDateTime.withTimeAtStartOfDay(
            periodStartMoment,
            EzTimeZone.UTC);

        const endMomentUTC = EzDateTime.withTimeAtEndOfDay(
            periodEndMoment,
            EzTimeZone.UTC);

        const startIsoActive = EzDateTime.toIso(startMomentActive);

        const endIsoActive = EzDateTime.toIso(endMomentActive);

        const startIsoUTC = EzDateTime.toIso(startMomentUTC);

        const endIsoUTC = EzDateTime.toIso(endMomentUTC);

        /* TODO: Use the new EzTimePeriod class instead of returning an object with the period values.
        return new EzTimePeriod(
            startMomentActive,
            endMomentActive,
            EzDateTime.ezActiveTimeZoneId);
         */

        return {
            /*
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             * Start Period Date Time Values
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             */

            /**
             * Preferred reference period start date time with primary/active time zone
             *
             * @type {moment}
             */
            ezPeriodStartMoment: startMomentActive,

            /**
             * Preferred reference of period start ISO with primary/active time zone
             *
             * @type {string}
             */
            ezStartIsoActive: startIsoActive,

            /**
             * Preferred reference of period start date time reference with UTC time zone
             *
             * @type {moment}
             */
            ezPeriodStartMomentUTC: startMomentUTC,

            /**
             * Preferred reference of period start ISO with UTC time zone
             *
             * @type {string}
             */
            ezStartIsoUTC: startIsoUTC,

            /*
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             * End Period Date Time Values
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             */

            /**
             * Preferred reference period end date time with primary/active time zone
             *
             * @Type {moment}
             */
            ezPeriodEndMoment: endMomentActive,

            /**
             * Preferred reference of period end ISO with primary/active time zone
             *
             * @Type {string}
             */
            ezEndIsoActive: endIsoActive,

            /**
             * Preferred reference of period end date time reference with UTC time zone
             *
             * @Type {moment}
             */
            ezPeriodEndMomentUTC: endMomentUTC,

            /**
             * Preferred reference of period end ISO with UTC time zone
             *
             * @Type {string}
             */
            ezEndIsoUTC: endIsoUTC,

            /*
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             * Legacy Start Period Date Time Values
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             */

            /**
             * Alias of property ezPeriodStartMoment
             *
             * @type {moment}
             *
             * @Deprecated
             * Stop all use and migrate to property ezPeriodStartMoment
             */
            ezStartEzDateTimeActive: startMomentActive,

            /**
             * Alias of property ezPeriodStartMomentUTC
             *
             * @type {moment}
             *
             * @Deprecated
             * Stop all use and migrate to property ezPeriodStartMomentUTC
             */
            ezStartEzDateTimeUTC: startMomentUTC,

            /**
             * Alias for property ezPeriodStartMoment
             *
             * @type {moment}
             *
             * @Deprecated
             * Stop all use and migrate to property ezPeriodStartMoment
             */
            periodStartMoment: startMomentActive,

            /**
             * Alias for property ezStartIsoActive
             *
             * @type {moment}
             *
             * @Deprecated
             * Stop all use and migrate to property ezStartIsoActive
             */
            periodStartIso: startIsoActive,

            /*
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             * Legacy End Period Date Time Values
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             */
            /**
             * Alias for property ezPeriodEndMoment
             *
             * @type {moment}
             *
             * @Deprecated
             * Stop all use and migrate to property ezStartIsoActive
             */
            ezEndEzDateTimeActive: endMomentActive,

            /**
             * Alias for property ezPeriodEndMomentUTC
             *
             * @type {moment}
             *
             * @Deprecated
             * Stop all use and migrate to property ezPeriodEndMomentUTC
             */
            ezEndEzDateTimeUTC: endMomentUTC,

            /**
             * Alias for property ezPeriodEndMoment
             *
             * @type {moment}
             *
             * @Deprecated
             * Stop all use and migrate to property ezPeriodEndMoment
             */
            periodEndMoment: endMomentActive,

            /**
             * Alias for property ezEndIsoActive
             *
             * @type {string}
             *
             * @Deprecated
             * Stop all use and migrate to property ezEndIsoActive
             */
            periodEndIso: endIsoActive
        };
    }

    /**
     * @public @function
     * Creates the default employee options
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     * @returns {object}
     * Returns time period object:
     *  {
     *      ezStartEzDateTimeActive: {moment},
     *      ezStartEzDateTimeUTC: {moment}
     *      ezStartIsoActive: {string},
     *      ezStartIsoUTC: {string},
     *      ezEndEzDateTimeActive: {moment},
     *      ezEndEzDateTimeUTC: {moment},
     *      ezEndIsoActive: {string},
     *      ezEndIsoUTC: {string},
     *      In addition, contains  additional Legacy properties: Please migrate to using of the properties above
     *      ezPeriodStartMoment: {moment},
     *      periodStartMoment: {moment},
     *      periodStartIso: {string},
     *      ezPeriodEndMoment: {moment},
     *      periodEndMoment: {moment},
     *      periodEndIso: {string}
     *  };
     * @deprecated
     * Migrate to static method: EzDateTime.createSelectedPeriod()
     */
    ezCreateSelectedPeriod = EzDateTime.createSelectedPeriod;

    /**
     * @public @function
     * Creates a selected period JSON Object from the provided periodStartMoment and periodEndMoment
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     * @returns {object}
     */
    ezCreateSelectedPeriodJSON(periodStartMoment, periodEndMoment) {
        if (!EzDateTime.isValidMoment(periodStartMoment)) {
            throw new EzBadParamException(
                'periodStartMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJSON);
        }
        if (!EzDateTime.isValidMoment(periodEndMoment)) {
            throw new EzBadParamException(
                'periodEndMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJSON);
        }

        const startMomentPTZ = EzDateTime.momentWithPTZ(periodStartMoment);

        const startMomentUTC = globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeWithTimeZone(
            periodStartMoment,
            EzTimeZone.UTC);

        const startIsoPTZ = EzDateTime.toIso(startMomentPTZ);

        const startIsoUTC = EzDateTime.toIso(startMomentUTC);

        const endMomentPTZ = EzDateTime.momentWithPTZ(periodEndMoment);

        const endMomentUTC = EzDateTime.momentWithPTZ(periodEndMoment);

        const endIsoPTC = EzDateTime.toIso(endMomentPTZ);

        const endIsoUTC = EzDateTime.toIso(endMomentUTC);

        // TODO: Create an EzTimePeriod JS class
        return {
            //ezStartEzDateTimeActive: startMomentActive,
            //ezStartEzDateTimeUTC: startMomentUTC,
            ezStartIsoActive: startIsoPTZ,
            ezStartIsoUTC: startIsoUTC,
            //ezEndEzDateTimeActive: endEzDateTimeActive,
            //ezEndEzDateTimeUTC: endEzDateTimeUTC,
            ezEndIsoActive: endIsoPTC,
            ezEndIsoUTC: endIsoUTC,
            // Legacy properties - migrate to one of the above
            //ezPeriodStartMoment: startMomentActive,
            //periodStartMoment: startMomentActive,
            //ezPeriodEndMoment: endEzDateTimeActive,
            //periodEndMoment: endEzDateTimeActive,
            periodStartIso: startIsoPTZ,
            periodEndIso: endIsoPTC
        };
    }

    /**
     * @public @function
     * Creates a selected period object from the provided moments, sets the values as ISO strings, converts to JSON
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     * @returns {string}
     */
    ezCreateSelectedPeriodJson(periodStartMoment, periodEndMoment) {
        if (!EzDateTime.isValidMoment(periodStartMoment)) {
            throw new EzBadParamException(
                periodStartMoment,
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJson);
        }
        if (!EzDateTime.isValidMoment(periodEndMoment)) {
            throw new EzBadParamException(
                periodEndMoment,
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJson);
        }

        return EzJson.toJson(
            EzDateTime.createSelectedPeriod(periodStartMoment, periodEndMoment));
    }

    /**
     * @static
     * @public @function
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * Documentation: https://momentjs.com/docs/#/manipulating/
     * Calls momentjs api method: moment.add(amountToAdd, fieldName)
     * @param {moment} momentInstance
     * @param {string} fieldName
     * Supported field names:
     *  |----------------|-----------|
     *  | Field Name     | Shorthand |
     *  |----------------|-----------|
     *  | years          | y         |
     *  | quarters       | Q         |
     *  | months         | M         |
     *  | weeks          | w         |
     *  | days           | d         |
     *  | hours          | h         |
     *  | minutes        | m         |
     *  | seconds        | s         |
     *  | milliseconds   | ms        |
     *  |----------------|-----------|
     * @param {number} amountToAdd
     * @returns {moment}
     */
    static add(momentInstance, fieldName, amountToAdd) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.add);
        }
        if (!EzString.hasText(fieldName)) {
            throw new EzBadParamException(
                'fieldName',
                EzDateTime,
                EzDateTime.add);
        }
        if (!EzNumber.isNumber(amountToAdd)) {
            throw new EzBadParamException(
                'amountToAdd',
                EzDateTime,
                EzDateTime.add);
        }

        momentInstance = EzDateTime.momentWithPTZ(momentInstance);

        return momentInstance.add(amountToAdd, fieldName);
    }

    /**
     * @static
     * @public @function
     * Adds the provided amountOfWeeksToAdd weeks to the provided momentInstance and returns the new moment instance.
     * @param {moment} momentInstance
     * @param {number} [amountOfWeeksToAdd=EzNumber.ZERO]
     * @returns {moment}
     */
    static addWeeks(momentInstance, amountOfWeeksToAdd = EzNumber.ZERO) {
        return EzDateTime.add(momentInstance, 'weeks', amountOfWeeksToAdd);
    }

    /**
     * @static
     * @public @function
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.add(momentInstance, fieldName, amountToAdd)
     */
    ezPlus = EzDateTime.add;

    /**
     * @public @function
     * Sets the momentInstance instance to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.add(momentInstance, fieldName, amountToAdd)
     */
    ezAdd = EzDateTime.add;

    /**
     * @static
     * @public @function
     * Sets the momentInstance instance to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {undefined|null|string} [aValueKey='minutes']
     * One of the following string values: 'years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'
     * or their shorthand values: EzChar.y, EzChar.M, EzChar.w, EzChar.d, EzChar.h, EzChar.m, EzChar.s, 'ms'
     * @param {undefined|null|number} [aAmountToAdd=EzNumber.ZERO]
     * @returns {moment}
     */
    static addUTC(momentInstance, aValueKey = 'minutes', aAmountToAdd = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezAddUTC);
        }

        return EzDateTime.add(
            EzDateTime.ezInstance.ezToUTC(momentInstance),
            EzString.stringWithTextOrDefault(aValueKey, 'minutes'),
            EzNumber.numberOrZero(aAmountToAdd));
    }

    /**
     * @public @function
     * Sets the momentInstance instance to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {undefined|null|string} [aValueKey='minutes']
     * One of the following string values: 'years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'
     * or their shorthand values: EzChar.y, EzChar.M, EzChar.w, EzChar.d, EzChar.h, EzChar.m, EzChar.s, 'ms'
     * @param {undefined|null|number} [aAmountToAdd=EzNumber.ZERO]
     * @returns {moment}
     */
    ezPlusUTC

    /**
     * @public @function
     * Sets the momentInstance instance to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     */
    ezAddUTC(momentInstance, aValueKey, aAmountToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(momentInstance, aValueKey, aAmountToAdd);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {yearsToAdd} number of years.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'years', yearsToAdd)
     * @param {moment} momentInstance
     * @param {number} yearsToAdd
     * @returns {moment}
     */
    ezPlusYears(momentInstance, yearsToAdd) {
        return EzDateTime.add(momentInstance, 'years', yearsToAdd);
    }

    /**
     * @static
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {yearsToAdd} number of years.
     * Calls:  EzDateTime.add(momentInstance, 'years', yearsToAdd);
     * @param {moment} momentInstance
     * @param {number} yearsToAdd
     * @returns {moment}
     */
    static addYears(momentInstance, yearsToAdd) {
        return EzDateTime.add(momentInstance, 'years', yearsToAdd);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {yearsToAdd} number of years.
     * Calls:  EzDateTime.add(momentInstance, 'years', yearsToAdd);
     * @param {moment} momentInstance
     * @param {number} yearsToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.addYears(momentInstance, yearsToAdd)
     */
    ezAddYears = EzDateTime.addYears;

    /**
     * @static
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {monthsToAdd} number of months.
     * Calls:  EzDateTime.add(momentInstance, 'months', monthsToAdd);
     * @param {moment} momentInstance
     * @param {number} monthsToAdd
     * @returns {moment}
     */
    static addMonths(momentInstance, monthsToAdd) {
        return EzDateTime.add(momentInstance, 'months', monthsToAdd);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {monthsToAdd} number of months.
     * Calls:  EzDateTime.add(momentInstance, 'months', monthsToAdd);
     * @param {moment} momentInstance
     * @param {number} monthsToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.addMonths(momentInstance, monthsToAdd)
     */
    ezAddMonths = EzDateTime.addMonths;

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {monthsToAdd} number of months.
     * Calls:  EzDateTime.add(momentInstance, 'months', monthsToAdd);
     * @param {moment} momentInstance
     * @param {number} monthsToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.addMonths(momentInstance, monthsToAdd)
     */
    ezPlusMonths = EzDateTime.addMonths;

    /**
     * @static
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'days', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     */
    static addDays(momentInstance, daysToAdd) {
        return EzDateTime.add(momentInstance, 'days', daysToAdd);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'days', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addDays(momentInstance, daysToAdd)
     */
    ezPlusDays = EzDateTime.addDays;

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'days', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addDays(momentInstance, daysToAdd)
     */
    ezAddDays = EzDateTime.addDays;

    /**
     * @static
     * @public @function
     * Returns a moment equal to the provided momentInstance plus the provided hoursToAdd.
     * @param {moment} momentInstance
     * @param {number} [hoursToAdd=EzNumber.ZERO]
     * @returns {moment}
     */
    static addHours(momentInstance, hoursToAdd = EzNumber.ZERO) {
        return EzDateTime.add(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToAdd));
    }

    /**
     * @static
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'hours', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} hoursToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addHours(momentInstance, hoursToAdd)
     */
    ezPlusHours = EzDateTime.addHours;

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'hours', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} hoursToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addHours(momentInstance, hoursToAdd)
     */
    ezAddHours = EzDateTime.addHours;

    /**
     * @static
     * @public @function
     * Returns a moment equal to the provided momentInstance plus the provided minutesToAdd.
     * @param {moment} momentInstance
     * @param {number} [minutesToAdd=EzNumber.ZERO]
     * @returns {moment}
     */
    static addMinutes(momentInstance, minutesToAdd = EzNumber.ZERO) {
        return EzDateTime.add(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToAdd));
    }

    /**
     * @static
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     * @deprecated
     * Migrate to static method: EzDateTime.addMinutes(momentInstance, minutesToAdd)
     */
    ezPlusMinutes = EzDateTime.addMinutes;

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     * @deprecated
     * Migrate to static method: EzDateTime.addMinutes(momentInstance, minutesToAdd)
     */
    ezAddMinutes = EzDateTime.addMinutes;

    /**
     * @static
     * @public @function
     * Returns a moment equal to the provided momentInstance plus the provided secondsToAdd.
     * @param {moment} [momentInstance=null]
     * @param {number} [secondsToAdd=EzNumber.ZERO]
     * @returns {moment}
     */
    static addSeconds(momentInstance, secondsToAdd = EzNumber.ZERO) {
        return EzDateTime.add(
            momentInstance,
            'seconds',
            EzNumber.numberOrZero(secondsToAdd));
    }

    /**
     * @static
     * @public @function
     * Returns a moment equal to the provided momentInstance plus the provided millisecondsToAdd.
     * @param {moment} [momentInstance=null]
     * @param {number} [millisecondsToAdd=EzNuymber.ZERO]
     * @returns {moment}
     */
    static addMilliseconds(momentInstance, millisecondsToAdd = EzNumber.ZERO) {
        return EzDateTime.add(
            momentInstance,
            'milliseconds',
            EzNumber.numberOrZero(millisecondsToAdd));
    }

    /**
     * @public @function
     * Converts the provided momentInstance to UTC time zone then adds the provided {daysToAdd} number of days.
     * Alias for: EzDateTime.ezInstance.ezAddDaysUTC(momentInstance, daysToAdd)
     * @param {moment} momentInstance
     * @param {number} [daysToAdd=EzNumber.ZERO]
     * @returns {moment}
     */
    ezPlusDaysUTC(momentInstance, daysToAdd = EzNumber.ZERO) {
        return EzDateTime.ezInstance.ezAddDaysUTC(momentInstance, daysToAdd);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to UTC time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'days', daysToAdd);
     * @param {moment} momentInstance
     * @param {number} [daysToAdd=EzNumber.ZERO]
     * @returns {moment}
     */
    ezAddDaysUTC(momentInstance, daysToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(
            momentInstance,
            'days',
            EzNumber.numberOrZero(daysToAdd));
    }

    /**
     * @public @function
     * Converts the provided momentInstance to UTC time zone then adds the provided {hoursToAdd} number of hours.
     * Alias for: EzDateTime.ezInstance.ezAddHoursUTC(momentInstance, hoursToAdd)
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     */
    ezPlusHoursUTC(momentInstance, hoursToAdd) {
        return EzDateTime.ezInstance.ezAddHoursUTC(momentInstance, hoursToAdd);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to UTC time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'hours', hoursToAdd);
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     */
    ezAddHoursUTC(momentInstance, hoursToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToAdd));
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone
     * then adds the provided {minutesToAdd} number of minutes.
     * Alias for: EzDateTime.ezInstance.ezAddMinutesUTC(momentInstance, minutesToAdd)
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     */
    ezPlusMinutesUTC(momentInstance, minutesToAdd) {
        return EzDateTime.ezInstance.ezAddMinutesUTC(momentInstance, minutesToAdd);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone
     * then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     */
    ezAddMinutesUTC(momentInstance, minutesToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToAdd));
    }

    /**
     * @static
     * @public @function
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * One of the following: 'years', 'days', 'weeks', 'months', 'hours', 'minutes', 'seconds', 'milliseconds' or
     * their shorthand values: EzChar.y, EzChar.d, EzChar.w, EzChar.M, EzChar.h, EzChar.m, EzChar.s, 'ms'
     * @param {number} [aAmountToSubtract=0]
     * @returns {moment}
     */
    static minus(momentInstance, aValueKey, aAmountToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.minus);
        }
        if (!EzString.hasText(aValueKey)) {
            throw new EzBadParamException(
                'aValueKey',
                EzDateTime,
                EzDateTime.minus);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .subtract(
                EzNumber.numberOrZero(aAmountToSubtract),
                aValueKey);
    }

    /**
     * @public @function
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToSubtract
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.minus(momentInstance, aValueKey, aAmountToSubtract)
     */
    ezMinus = EzDateTime.minus;

    /**
     * @public @function
     * Sets the momentInstance instnace to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     */
    ezMinusUTC(momentInstance, aValueKey, aAmountToAdd) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusUTC);
        }

        if (!EzString.hasLength(aValueKey)) {
            throw new EzBadParamException(
                'aValueKey',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusUTC);
        }

        if (!EzNumber.isNumber(aAmountToAdd)) {
            throw new EzBadParamException(
                'aAmountToAdd',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusUTC);
        }

        return EzDateTime.ezInstance.ezToUTC(momentInstance).subtract(aValueKey, aAmountToAdd);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided yearsToSubtract years.
     * Calls:  EzDateTime.minus(momentInstance, 'years', yearsToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} yearsToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     */
    ezMinusYears(momentInstance, yearsToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusYears);
        }
        if (!EzNumber.isNumber(yearsToSubtract)) {
            if (EzString.isString(yearsToSubtract)) {
                yearsToSubtract = EzNumber.strToIntDefault(yearsToSubtract, 0);

                if (EzNumber.isNaN(yearsToSubtract)) {
                    throw new EzBadParamException(
                        'yearsToSubtract',
                        EzDateTime.ezInstance,
                        EzDateTime.ezInstance.ezMinusYears);
                }
            } else {
                throw new EzBadParamException(
                    'yearsToSubtract',
                    EzDateTime.ezInstance,
                    EzDateTime.ezInstance.ezMinusYears);
            }
        }

        return EzDateTime.minus(
            momentInstance,
            'years',
            yearsToSubtract);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided monthsToSubtract months.
     * Calls:  EzDateTime.minus(momentInstance, 'months', monthsToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} monthsToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     */
    static minusMonths(momentInstance, monthsToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.minusMonths);
        }
        if (!EzNumber.isNumber(monthsToSubtract)) {
            if (EzString.isString(monthsToSubtract)) {
                monthsToSubtract = EzNumber.strToIntDefault(monthsToSubtract, 0);

                if (EzNumber.isNaN(monthsToSubtract)) {
                    throw new EzBadParamException(
                        'monthsToSubtract',
                        EzDateTime,
                        EzDateTime.minusMonths);
                }
            } else {
                throw new EzBadParamException(
                    'monthsToSubtract',
                    EzDateTime,
                    EzDateTime.minusMonths);
            }
        }

        return EzDateTime.minus(
            momentInstance,
            'months',
            monthsToSubtract);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided monthsToSubtract months.
     * Calls:  EzDateTime.minus(momentInstance, 'months', monthsToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} monthsToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.minusMonths(momentInstance, monthsToSubtract)
     */
    ezMinusMonths = EzDateTime.minusMonths;

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided weeksToSubtract weeks
     * Calls:  EzDateTime.minus(momentInstance, 'weeks', weeksToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} weeksToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     */
    ezMinusWeeks(momentInstance, weeksToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusWeeks);
        }
        if (!EzNumber.isNumber(weeksToSubtract)) {
            if (EzString.isString(weeksToSubtract)) {
                weeksToSubtract = EzNumber.strToIntDefault(weeksToSubtract, 0);

                if (EzNumber.isNaN(weeksToSubtract)) {
                    throw new EzBadParamException(
                        'weeksToSubtract',
                        EzDateTime.ezInstance,
                        EzDateTime.ezInstance.ezMinusWeeks);
                }
            } else {
                throw new EzBadParamException(
                    'weeksToSubtract',
                    EzDateTime.ezInstance,
                    EzDateTime.ezInstance.ezMinusWeeks);
            }
        }

        return EzDateTime.minus(
            momentInstance,
            'weeks',
            weeksToSubtract);
    }


    /**
     * @static
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided {daysToSubtract} number of days.
     * Calls:  EzDateTime.minus(momentInstance, 'days', daysToSubtract)
     * @param {undefined|null|moment} [momentInstance = null]
     * @param {string|number} [daysToSubtract=0]
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     */
    static minusDays(momentInstance = null, daysToSubtract = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.minusDays);
        }

        if (!EzNumber.isNumber(daysToSubtract)) {
            daysToSubtract = EzNumber.strToIntDefault(daysToSubtract, EzNumber.ZERO);
        }

        return EzDateTime.minus(
            momentInstance,
            'days',
            daysToSubtract);
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided {daysToSubtract} number of days.
     * Calls:  EzDateTime.minus(momentInstance, 'days', daysToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} daysToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.minusDays(momentInstance, daysToSubtract)
     */
    ezMinusDays = EzDateTime.minusDays;

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided hoursToSubtract number of hours.
     * Calls:  EzDateTime.minus(momentInstance, 'hours', hoursToSubtract)
     * @param {moment} momentInstance
     * @param {number} hoursToSubtract
     * @returns {moment}
     */
    ezMinusHours(momentInstance, hoursToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusHours);
        }

        return EzDateTime.minus(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToSubtract));
    }

    /**
     * @public @function
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided minutesToSubtract number of minutes.
     * Calls:  EzDateTime.minus(momentInstance, 'minutes', minutesToSubtract);
     * @param {moment} momentInstance
     * @param {number} minutesToSubtract
     * @returns {moment}
     */
    ezMinusMinutes(momentInstance, minutesToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusMinutes);
        }

        return EzDateTime.minus(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToSubtract));
    }

    /**
     * @public @function
     * Converts the provided momentInstance to UTC time zone then subtracts the provided daysToSubtract number of days.
     * Calls:  EzDateTime.ezInstance.ezMinusUTC(momentInstance, 'days', daysToSubtract);
     * @param {moment} momentInstance
     * @param {number} daysToSubtract
     * @returns {moment}
     */
    ezMinusDaysUTC(momentInstance, daysToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusDaysUTC);
        }

        return EzDateTime.ezInstance.ezMinusUTC(
            momentInstance,
            'days',
            EzNumber.numberOrZero(daysToSubtract));
    }

    /**
     * @public @function
     * Converts the provided momentInstance to UTC time zone then subtracts the provided {hoursToSubtract} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'hours', hoursToAdd);
     * @param {moment} momentInstance
     * @param {number} hoursToSubtract
     * @returns {moment}
     */
    ezMinusHoursUTC(momentInstance, hoursToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusHoursUTC);
        }

        return EzDateTime.ezInstance.ezMinusUTC(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToSubtract));
    }

    /**
     *@public @function
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToSubtract} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToSubtract
     */
    ezMinusMinutesUTC(momentInstance, minutesToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusMinutesUTC);
        }

        return EzDateTime.ezInstance.ezMinusUTC(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToSubtract));
    }

    /**
     * @public @function
     * Creates a moment from now with the UTC timezone.
     * @returns {moment}
     */
    ezNowUTC() {
        return moment().utc();
    }

    /**
     * @static
     * @public @function
     * Converts the moment to a UTC moment.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    static toUTC(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toUTC);
        }

        return moment.utc(momentInstance);
    }

    /**
     * @public @function
     * Converts the moment to a UTC moment.
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.toUtc(momentInstance)
     */
    ezToUtc = EzDateTime.toUTC;

    /**
     * @public @function
     * Returns the moment created from the provided jsDate, transformed to the preferred timezone
     * @param {Date} aJsDate
     * @returns {moment}
     */
    ezCreateFromJsDate(aJsDate) {
        if (!EzObject.isValid(aJsDate)) {
            throw new EzBadParamException(
                'aJsDate',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromJsDate);
        }

        // toISOString() is for the normal JS Date object ONLY
        return EzDateTime.momentWithPTZ(moment(aJsDate.toISOString()));
    }

    /**
     * @static
     * @public @function
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    static createFromMoment(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromMoment);
        }

        return EzDateTime.momentWithPTZ(momentInstance);
    }

    /**
     * @public @function
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.createFromMoment(momentInstance)
     */
    ezCreateFromMoment = EzDateTime.createFromMoment;

    /**
     * @public @function
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * Sets the time at the start of the day.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezCreateFromMomentWithTimeStartOfDay(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay);
        }

        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.momentWithPTZ(momentInstance));
    }

    /**
     * @public @function
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * Sets the time at the end of the day.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezCreateFromMomentWithTimeEndOfDay(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromMomentWithTimeEndOfDay);
        }

        return EzDateTime.withTimeAtEndOfDay(
            EzDateTime.momentWithPTZ(momentInstance));
    }

    /**
     * @public @function
     * Determines if the provided isoDateTimeString was valid or not.
     * @param {string} proposedDateTimeIso
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidIso(proposedDateTimeIso)
     */
    ezIsValidIsoDateTime(proposedDateTimeIso) {
        return EzDateTime.isValidIso(proposedDateTimeIso);
    }

    /**
     * @public @function
     * Determines if the provided isoDateTimeString was valid or not.
     * @param {string} proposedDateTimeIso
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidIso(proposedDateTimeIso)
     */
    ezIsValidIsoString(proposedDateTimeIso) {
        return EzDateTime.isValidIso(proposedDateTimeIso);
    }

    /**
     * @public @function
     * Converts the provided moment to an ISO string
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to EzDateTime.toIso()
     */
    ezMomentToIsoString(momentInstance) {
        return EzDateTime.toIso(momentInstance);
    }

    /**
     * @public @function
     * Transforms the provided moment to one with the preferred timezone
     * @param {moment} momentInstance
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Defaults to: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     */
    static momentWithPTZ(momentInstance = null/*, ezLocale */) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.momentWithPTZ);
        }

        /* TODO: Finish locale work
        if (!EzString.hasLength(ezLocale)) {
            ezLocale = EzDateTime.ezInstance.ezPreferredLocale;
        }
        */

        return moment(momentInstance).tz(EzDateTime.ezActiveTimeZoneId);

        // TODO: Remove below line once verified above line works fine :)
        /* Original implementation:
        return EzDateTime.fromIso(momentInstance.format());
        */
    }

    /**
     * @public @function
     * Transforms the provided moment to one with the preferred timezone
     * @param {moment} momentInstance
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Defaults to: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.momentWithPTZ(momentInstance, ezLocale)
     */
    ezMomentWithPTZ = EzDateTime.momentWithPTZ;

    /**
     * @static
     * @public @function
     * Creates a copy of the provided moment that has the Primary Time Zone. If the provided momentInstance is null or not
     * a valid moment instance null is returned.
     * @param {moment} momentInstance
     */
    static momentFromMoment(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.momentFromMoment);
        }

        return EzDateTime.momentWithPTZ(momentInstance);
    }

    /**
     * @public @function
     * Creates a copy of the provided moment that has the Primary Time Zone. If the provided momentInstance is null or not
     * a valid moment instance null is returned.
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.momentFromMoment(momentInstance)
     */
    ezMomentFromMoment = EzDateTime.momentFromMoment;

    /**
     * @static
     * @public @function
     * Gets the time zone id for the provided momentInstance.
     * If the provided momentInstance is undefined or null, then EzDateTime.ezActiveTimeZoneId is returned.
     * @param {undefined|null|moment} momentInstance
     * @returns {string}
     */
    static getTimeZoneId(momentInstance) {
        return EzDateTime.getTimeZoneIdOrDefault(
            momentInstance,
            EzDateTime.ezActiveTimeZoneId);
    }

    /**
     * @static
     * @public @function
     * Gets the time zone id for the provided momentInstance.
     * If the provided momentInstance is undefined or null, then the provided defaultTimeZoneId value is returned.
     * @param {undefined|null|moment} momentInstance
     * @returns {string}
     */
    static getTimeZoneIdOrDefault(momentInstance, defaultTimeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return defaultTimeZoneId;
        }

        return EzString.stringWithLengthOrDefault(
            momentInstance.zone().name,
            defaultTimeZoneId);
    }

    /**
     * @public @function
     * Returns a new moment instance created from the provided momentInstance then sets the time zone to the
     * provdied timeZoneId.
     * Uses globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone if the provided timeZoneId is not a valid time zone.
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.withZone(momentInstance)
     */
    ezDateTimeWithTimeZone = EzDateTime.withZone;

    /**
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to: EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId)
     */
    ezDateTimeWithTimeAtStartOfDay = EzDateTime.withTimeAtStartOfDay;

    /**
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId)
     */
    ezDateTimeWithTimeAtEndOfDay = EzDateTime.withTimeAtEndOfDay;

    /**
     * @static
     * @public @function
     * Creates a new moment with the provided timeZoneId
     * @param {moment} moment
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @return {moment}
     */
    static withZone(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.withZone);
        }

        return moment(momentInstance).tz(EzDateTime.safeTimeZoneId(timeZoneId));
    }


    /**
     * @public @function
     * Creates a new moment with the provided timeZoneId
     * @param {moment} moment
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @return {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withZone(moment, timeZoneId)
     */
    ezMomentWithZone = EzDateTime.withZone;

    /**
     * @static
     * @public @function
     * Returns the provided momentInstance in the provided timeZoneId with its time set to the provided hours, minutes, seconds, milliseconds.
     * Important: The time is set after the time-zone is set.
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|number} hours
     * Default: 0
     * @param {undefined|null|number} minutes
     * Default: 0
     * @param {undefined|null|number} seconds
     * Default: 0
     * @param {undefined|null|number} milliseconds
     * Default: 0
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.#ezActiveTimeZoneId
     */
    static withTime(momentInstance = null, hours = EzNumber.ZERO, minutes = EzNumber.ZERO, seconds = EzNumber.ZERO, milliseconds = EzNumber.ZERO,
        timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.withTime);
        }

        return EzDateTime.withZone(
            momentInstance,
            EzDateTime.safeTimeZoneId(timeZoneId))
            .hours(
                EzNumber.numberOrZero(hours))
            .minutes(
                EzNumber.numberOrZero(minutes))
            .seconds(
                EzNumber.numberOrZero(seconds))
            .milliseconds(
                EzNumber.numberOrZero(milliseconds));
    }

    /**
     * @static
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static withTimeAtStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.withTimeAtStartOfDay);
        }

        return EzDateTime.setTime(
            momentInstance,
            EzNumber.ZERO,
            EzNumber.ZERO,
            EzNumber.ZERO,
            EzNumber.ZERO,
            timeZoneId);
    }

    /**
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId)
     */
    ezMomentWithZoneAtStartOfDay = EzDateTime.withTimeAtStartOfDay;

    /**
     * @static
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Defualt: null
     * @param {undefiend|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneI
     * @returns {moment}
     */
    static withTimeAtEndOfDay(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.withTimeAtEndOfDay);
        }

        return EzDateTime.setTime(
            momentInstance,
            EzNumber.TWENTY_THREE,
            EzNumber.FIFTY_NINE,
            EzNumber.FIFTY_NINE,
            EzNumber.NINE_HUNDRED_NINETY_NINE,
            timeZoneId);
    }

    /**
     * @public @function
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Defualt: null
     * @param {undefiend|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneI
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId)
     */
    ezMomentWithZoneAtEndOfDay = EzDateTime.withTimeAtEndOfDay;

    /**
     * @public @function
     * Validates the provided timeZoneId and
     * 1) If valid, returns the provided timeZoneId
     * 2) Returns the EzDateTime.ezActiveTimeZoneId
     * @param {string} timeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDateTime.ezUserTimeZoneId(timeZoneId);
     */
    ezUseTimezoneIdOrActiveTimezoneId(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.safeTimeZoneId(timeZoneId);
    }

    /**
     * @static
     * @public @function
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then returns the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id, then returns EzDateTime.ezActiveTimeZoneId
     * 3) If EzDateTime.ezActiveTimeZoneId is not a valid date time zone id, then returns moment().tz()
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     */
    static safeTimeZoneId(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(timeZoneId)) {
            return EzDateTime.timeZoneIdOrDefault(
                EzDateTime.ezActiveTimeZoneId,
                moment().tz());
        }

        return EzDateTime.timeZoneIdOrDefault(
            timeZoneId,
            EzDateTime.ezActiveTimeZoneId);
    }

    /**
     * @public @function
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then returns the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id, then returns EzDateTime.ezActiveTimeZoneId
     * @param {string} timeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     * @deprecated
     * Migrate to static method: EzDateTime.safeTimeZoneId(timeZoneId)
     */
    ezUseTimeZoneId = EzDateTime.safeTimeZoneId;

    /**
     * @public @function
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then return the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id:
     * ====> 2a) If the defaultTimeZoneId is a valid date time zone id, then defaultTimeZoneId is returned
     * ====> 2b) If the defaultTimeZoneId is not a valid date time zone id, then moment().tz() is returned.
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @param {string} defaultTimeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     */
    static timeZoneIdOrDefault(timeZoneId = null, defaultTimeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (EzDateTime.DEFAULT_ACTIVE_TIMEZONE === timeZoneId.toUpperCase()) {
            timeZoneId = EzDateTime.ezGuessLocalTimeZone();
        }

        if (!EzString.hasText(defaultTimeZoneId) || !EzDateTime.isValidTimeZone(defaultTimeZoneId)) {
            defaultTimeZoneId = moment().tz();
        }

        return EzDateTime.isValidTimeZone(timeZoneId)
            ? timeZoneId
            : defaultTimeZoneId;
    }

    /**
     * @public @function
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then return the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id:
     * ====> 2a) If the defaultTimeZoneId is provided to the method, then return the provided defaultTimeZoneId
     * ====>2b) The defaultTimeZoneId is NOT provided to the method, then return the provided defaultTimeZoneId
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @param {string} defaultTimeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     * @deprecated
     * Migrate to static method: EzDateTime.timeZoneIdOrDefault(timeZoneId, defaultTimeZoneId)'
     */
    ezUseTimeZoneIdOrDefault = EzDateTime.timeZoneIdOrDefault;

    /**
     * @static
     * @public @property @getter
     * Gets the default active time zone display HTML element id
     * @returns {string}
     */
    static get defaultActiveTimeZoneDisplayElementId() {
        return '_EzActiveTimeZone';
    }

    /**
     * @static
     * @public @function
     * Displays the active timezone in the _EzActiveTimeZone element (or the element id specified)
     * @param {undefined|null|string} activeTimeZoneElementId
     * A valid HTML element id that can accept innterHTML
     * Default: EzDateTime.defaultActiveTimeZoneDisplayElementId (aka '_EzActiveTimeZone')
     */
    static displayActiveTimezone(displayActiveTimeZoneElementId = EzDateTime.defaultActiveTimeZoneDisplayElementId) {
        const htmlElement = globalThis?.document?.getElementById(
            EzString.stringWithTextOrDefault(
                displayActiveTimeZoneElementId,
                EzDateTime.defaultActiveTimeZoneDisplayElementId));

        if (EzObject.isValid(htmlElement)) {
            htmlElement.innerHtml = `Active Timezone: ${EzDateTime.ezActiveTimeZoneId}`;
        }
    }

    /**
     * @public @function
     * Displays the active timezone in the _EzActiveTimeZone element (or the element id specified)
     * @param {undefined|null|string} activeTimeZoneElementId
     * A valid HTML element id that can accept innterHTML
     * Default: '_EzActiveTimeZone'
     * @deprecated
     * Migrate to static method: EzDateTime.displayActiveTimezone(displayActiveTimeZoneElementId);
     */
    ezDisplayActiveTimezone(displayActiveTimeZoneElementId) {
        EzDateTime.displayActiveTimezone(displayActiveTimeZoneElementId);
    }

    /**
     * @static
     * @public @function
     * Gets the number of years between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static yearsBetween(startMomentInstance = null, endMomentInstance = null) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.yearsBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.yearsBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'years');
    }

    /**
     * @public @function
     * Gets the number of years between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.yearsBetween(startMomentInstance, endMomentInstance)
     */
    ezYearsBetween = EzDateTime.yearsBetween;

    /**
     * @static
     * @public @function
     * Gets the number of months between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static monthsBetween(startMomentInstance = null, endMomentInstance = null) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.monthsBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.monthsBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'months');
    }

    /**
     * @public @function
     * Gets the number of months between the provided startMomentInstance and endMomentInstance
     *@param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    ezMonthsBetween = EzDateTime.monthsBetween;

    /**
     * @static
     * @public @function
     * Gets the number of weeks between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static weeksBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.weeksBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.weeksBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'weeks');
    }

    /**
     * @public @function
     * Gets the number of weeks between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.weeksBetween(startMomentInstance, endMomentInstance)
     */
    ezWeeksBetween = EzDateTime.weeksBetween;

    /**
     * @static
     * @public @function
     * Gets the number of days between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static daysBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.daysBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.daysBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'days');
    }

    /**
     * @public @function
     * Gets the number of days between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.daysBetween(startMomentInstance, endMomentInstance)
     */
    ezDaysBetween = EzDateTime.daysBetween;

    /**
     * @static
     * @public @function
     * Gets the number of hours between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static hoursBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.hoursBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.hoursBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'hours');
    }

    /**
     * @public @function
     * Gets the number of hours between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.hoursBetween(startMomentInstance, endMomentInstance)
     */
    ezHoursBetween = EzDateTime.hoursBetween;

    /**
     * @static
     * @public @function
     * Gets the number of minutes between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static minutesBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.minutesBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.minutesBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'minutes');
    }

    /**
     * @public @function
     * Gets the number of minutes between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.minutesBetween(startMomentInstance, endMomentInstance)
     */
    ezMinutesBetween = EzDateTime.minutesBetween;

    /**
     * @public @function
     * Gets the number of seconds between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static secondsBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.secondsBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.secondsBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'seconds');
    }

    /**
     * @public @function
     * Gets the number of seconds between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.secondsBetween(startMomentInstance, endMomentInstance)
     */
    ezSecondsBetween = EzDateTime.secondsBetween;

    /**
     * @public @function
     * Generates a timestamp for the current date + time.
     * The active timezone id is used unless a timeZoneId value is provided during the call.
     * Example result: 1360013296
     * @param {undefiend|null|string} timeZoneId
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUnixMSTimeStamp(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.now(timeZoneId).format(EzChar.X);
    }

    /**
     * @public @function
     * Generates a timestamp for the current date + time in the UTC time zone
     * Example result: 1360013296123
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUTCUnixMSTimeStamp() {
        return EzDateTime.ezInstance.ezNowUTC().format(EzChar.x);
    }

    /**
     * @public @function
     * Generates a timestamp for the current date + time.
     * The active timezone id is used unless a timeZoneId value is provided during the call.
     * Example result: 1360013296
     * @param {undefiend|null|string} timeZoneId
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUnixTimeStamp(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzString.hasLength(timeZoneId)
            ? EzDateTime.now().format(EzChar.x)
            : EzDateTime.now().withZone(timeZoneId).format(EzChar.x);
    }

    /**
     * @public @function
     * Generates a timestamp for the current date + time in UTC time zone
     * Example result: 1360013296123
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUTCUnixTimeStamp() {
        return EzDateTime.ezInstance.ezNowUTC().format(EzChar.x);
    }

    /**
     * @public @function
     * Generates a timestamp for the provided momentInstance or NOW if momentInstance is not provided/null.
     * The active timezone id is used unless a timeZoneId value is provided during the call.
     * Example result: 1360013296
     * @param {undefiend|null|moment} momentInstance
     * @param {undefiend|null|string} timeZoneId
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetUnixTimeStamp(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetUnixTimeStamp);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .withZone(
                EzDateTime.timeZoneIdOrDefault(timeZoneId))
            .format(EzChar.x);
    }

    /**
     * @static
     * @public @function
     * Sets a time picker's values from the provided moment
     * @param {string} timePickerId
     * @param {moment} [momentInstance=null]
     * The provided momentInstance is converted to the active time zone before setting the time picker's values.
     */
    static setTimePickerValue(timePickerId, momentInstance = null) {
        if (!EzString.hasText(timePickerId)) {
            throw new EzBadParamException(
                { timePickerId },
                EzDateTime,
                EzDateTime.setTimePickerValue);
        }
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.setTimePickerValue);
        }

        const timePickerElement = EzUx.$id(timePickerId);

        if (!EzObject.isValid(timePickerElement)) {
            throw new EzBadStateException(
                EzString.em`
                    Expected the provided timePickerId of "${timePickerId}" to be a valid HTML element id or jQuery selector
                    that is associated with a valid HTML element.`,
                EzString.em`
                    However, the provided timePickerId of ${timePickerId} is either
                    a valid HTML element or jQuery selector but not associated with a valid HTML elementnot
                    or is not valid HTML element id or jQuery selector.`,
                EzDateTime,
                EzDateTime.setTimePickerValue);
        }

        timePickerElement.timepicker(
            'setTime',
            EzDateTime.momentWithPTZ(momentInstance)
                .format(EzDateTime.DTFORMAT_hh_mm_a));
    }

    /**
     * @public @function
     * Sets a time picker's values from the provided moment
     * @param {string} timePickerId
     * @param {moment} momentInstance
     * The provided momentInstance is converted to the active time zone before setting the time picker's values.
     * @deprecated
     * Migreate to static method: EzDateTime.setTimePickerValue(timePickerId, momentInstance)
     */
    ezSetTimePickerValue = EzDateTime.setTimePickerValue;

    /**
     * @public @function
     * Calculates the elapsed time since the provided startMoment to right now
     * @param {moment} stateMoment
     * @returns {string}
     */
    ezCalculateElapsedTimeToNow(startMoment) {
        return EzDateTime.isValidMoment(startMoment)
            ? EzDateTime.ezInstance.ezDurationToShiftTotal(
                moment.duration(
                    EzDateTime.now().diff(startMoment)))
            : '00:00';
    }

    /**
     * @public @function
     * Returns a duration as HH:MM string.
     * @param {moment.duration} duration
     * @returns {string}
     */
    ezDurationToShiftTotal(duration) {
        if (!EzObject.isValid(duration)) {
            return '00:00';
        }

        const hours = duration.hours();

        const min = duration.minutes();

        let response = EzNumber.TEN > hours
            ? `0${hours.toString()}`
            : hours.toString();

        response += EzNumber.TEN > min
            ? `:0${min.toString()}`
            : `:${min.toString()}`;

        return response;
    }

    /**
     * @public @function
     * Returns the day's name for the provided momentInstance
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezGetMomentDayName(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMomentDayName);
        }

        return EzDateTime.momentWithPTZ(momentInstance).format('dddd');
    }

    /**
     * Builds a shift total string from the provided values.
     * Null or zero values not displayed. Returns formats:
     *     D day HH:MM:SS.sss
     *     DD days HH:MM:SS.sss
     *     HH:MM:SS.sss
     *     HH:MM:SS
     *     HH:MM
     *     HH
     *     00:MM
     *     00:MM:SS
     *     00:MM:SS.sss
     *     00:00:SS
     *     00:00:SS.sss
     *     00:00:00.SSS
     * @param {boolean} includeDays
     * @param {number|null} days
     * @param {boolean} includeHours
     * @param {number|null} hours
     * @param {boolean} includeMinutes
     * @param {number|null} minutes
     * @param {boolean} includeSeconds
     * @param {number|null} seconds
     * @param {boolean} includeMilliseconds
     * @param {number|null} milliseconds
     * @returns {string}
     */
    ezBuildShiftTotalString(includeDays, days, includeHours, hours, includeMinutes, minutes, includeSeconds, seconds, includeMilliseconds, milliseconds) {
        let result = EzBoolean.isTrue(includeDays) && EzNumber.isNumber(days)
            ? EzDateTime.ezInstance.ezBuildShiftTotalDays(days, true)
            : EzString.EMPTY;

        if (EzBoolean.isTrue(includeHours)) {
            result = `${result}${EzDateTime.ezInstance.ezBuildShiftTotalHours(hours)}`;

            // Minutes (only if hours included)
            if (EzBoolean.isTrue(includeMinutes)) {
                result = `${result}:${EzDateTime.ezInstance.ezBuildShiftTotalMinutes(minutes, true)}`;

                // Seconds (only if minutes included)
                if (EzBoolean.isTrue(includeSeconds)) {
                    result = `${result}:${EzDateTime.ezInstance.ezBuildShiftTotalSeconds(seconds, true)}`;

                    // Milliseconds (only if seconds included)
                    if (EzBoolean.isTrue(includeMilliseconds)) {
                        result = `${result}.${EzDateTime.ezInstance.ezBuildShiftTotalMilliseconds(milliseconds, true)}`;
                    }
                }
            }
        }

        return result;
    }

    /**
     * @public @function
     * Builds a shift total display string for days using the provided days.
     * Appends a space if the appendSpace boolean is true.
     * @param {number} days
     * @returns {string}
     */
    ezBuildShiftTotalDays(days, appendSpace = false) {
        days = EzNumber.isNumber(days)
            ? days
            : EzNumber.ZERO;

        const daySuffix = 1 === days
            ? 'day'
            : 'days';

        return EzBoolean.isTrue(appendSpace)
            ? `${days} ${daySuffix}`
            : `${days}${daySuffix}`;
    }

    /**
     * @public @function
     * Builds a shift total display string for hours using the provided hours.
     * Prepends a space if prependSpace boolean is true.
     * @param {number|null} hours
     * @returns {string}
     */
    ezBuildShiftTotalHours(hours, prependSpace = false) {
        hours = EzNumber.isNumber(hours)
            ? EzDateTime.ezInstance.ezHoursToIso(hours)
            : EzNumber.ZERO;

        return EzBoolean.booleanOrFalse(prependSpace)
            ? ` ${hours}`
            : `${hours}`;
    }

    /**
     * @public @function
     * Builds a shift total in minutes. Prepends the : if prependColon boolean is true.
     * @param {number} hours
     * @param {boolean|null|undefined} prependColon
     * @returns {string}
     */
    ezBuildShiftTotalMinutes(minutes, prependColon = false) {
        minutes = EzNumber.isNumber(minutes)
            ? EzDateTime.ezInstance.ezMinutesToIso(minutes)
            : EzNumber.ZERO;

        return prependColon
            ? `:${minutes}`
            : `${minutes}`;
    }

    /**
     * @public @function
     * Builds a shift total display string for seconds using the provided seconds.
     * Prepends a colon to the result if prependColon boolean is true.
     * @param {number} hours
     * @param {boolean|null|undefined} prependColon
     * @returns {string}
     */
    ezBuildShiftTotalSeconds(seconds, prependColon) {
        seconds = EzNumber.isNumber(seconds)
            ? EzDateTime.ezInstance.ezSecondsToIso(seconds)
            : EzNumber.ZERO;

        return EzBoolean.booleanOrFalse(prependColon)
            ? `:${seconds}`
            : `${seconds}`;
    }

    /**
     * @public @function
     * Builds a shift total display string for milliseconds using the provided milliseconds.
     * Prepends a colon to the result if prependColon boolean is true.
     * @param {number} hours
     * @param {boolean|null|undefined} prependColon
     * @returns {string}
     */
    ezBuildShiftTotalMilliseconds(milliseconds, prependColon = false) {
        milliseconds = EzNumber.isNumber(milliseconds)
            ? EzDateTime.ezInstance.ezMillisecondsToIso(milliseconds)
            : EzNumber.ZERO;

        return EzBoolean.booleanOrFalse(prependColon)
            ? `:${milliseconds}`
            : `${milliseconds}`;
    }

    /**
     * @public @function
     * Returns the provided timeHours number as an ISO minutes string.
     * @param {number} hours
     * @returns {string}
     */
    ezHoursToIso(hours) {
        if (!EzNumber.isNumber(hours)) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezHoursToIso);
        }

        const hoursIso = EzNumber.ZERO >= hours && 10 <= hours
            ? hours.toString()
            : `0${hours.toString()}`;

        return EzNumber.ZERO >= hours
            ? hoursIso
            : '00';
    }

    /**
     * @public @function
     * Returns the provided timeMinutes number as an ISO minutes string of two digits.
     * @param {number} minutes
     * @returns {string}
     */
    ezMinutesToIso(minutes) {
        if (!EzNumber.isNumber(minutes)) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinutesToIso);
        }

        const minutesIso = EzNumber.ZERO >= minutes && 10 <= minutes
            ? minutes.toString()
            : `0${minutes.toString()}`;

        return EzNumber.ZERO >= minutes && 59 <= minutes
            ? minutesIso
            : '00';
    }

    /**
     * @public @function
     * Returns the provided seconds number as a ISO seconds string of two digits.
     * @param {number} seconds
     * @returns {string}
     */
    ezSecondsToIso(seconds) {
        if (!EzNumber.isNumber(seconds)) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSecondsToIso);
        }

        const secondsIso = EzNumber.ZERO >= seconds && 10 <= seconds
            ? seconds.toString()
            : `0${seconds.toString()}`;

        return EzNumber.ZERO >= seconds && 59 <= seconds
            ? secondsIso
            : '00';
    }

    /**
     * @public @function
     * Returns the provided timeMilliseconds number to an ISO milliseconds string.
     * @param {number} milliseconds
     * @returns {string}
     */
    ezMillisecondsToIso(milliseconds) {
        if (!EzNumber.isNumber(milliseconds)) {
            throw new EzBadParamException(
                'timeMilliseconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToIso);
        }

        if (0 < milliseconds && 999 <= milliseconds) {
            if (10 > milliseconds) {
                return `00${milliseconds}`;
            }

            if (100 > milliseconds) {
                return `0${milliseconds}`;
            }

            return `${milliseconds}`;
        }

        return '000';
    }

    /**
     * @public @function
     * Calculates the duration between two moments.
     * @param {moment} startMoment
     * @param {moment} endMoment
     * @returns {moment.duration}
     */
    ezCalculateDuration(startMoment, endMoment) {
        if (!EzObject.isValid(startMoment) || !EzObject.isValid(endMoment)) {
            return moment.duration(EzNumber.ZERO);
        }

        startMoment = EzDateTime.momentWithPTZ(startMoment);

        endMoment = EzDateTime.momentWithPTZ(endMoment);

        return moment.duration(endMoment.diff(startMoment));
    }

    /**
     * @pulic @function
     * Returns the milliseconds of the provided moment.
     * @returns {number}
     */
    ezGetMillisecond(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMillisecond);
        }

        return EzDateTime.momentWithPTZ(momentInstance).milliseconds();
    }

    /**
     * @pulic @function
     * Returns the seconds of the provided moment.
     * @returns {number}
     */
    ezGetSeconds(momentInstance) {
        if (!EzDateTime.ezInstance.ezIsValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetSeconds);
        }

        return EzDateTime.momentWithPTZ(momentInstance).seconds();
    }

    /**
     * @pulic @function
     * Returns the minutes of the provided moment.
     * @returns {number}
     */
    ezGetMinutes(momentInstance) {
        if (!EzDateTime.ezInstance.ezIsValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMinutes);
        }

        return EzDateTime.momentWithPTZ(momentInstance).minutes();
    }

    /**
     * @pulic @function
     * Returns the hours of the provided moment.
     * @returns {number}
     */
    ezGetHours(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetHours);
        }

        return EzDateTime.momentWithPTZ(momentInstance).hours();
    }

    /**
     * @pulic @function
     * Returns the month of the provided moment
     * Returned value is 0 through 11 where 0 = Janurary and 11 = December.
     * @returns {number}
     */
    ezGetMonth(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMonth);
        }

        return EzDateTime.momentWithPTZ(momentInstance).month();
    }

    /**
     * @pulic @function
     * Returns the day of the provided moment (a value from 1 to 31)
     * @returns {number}
     */
    ezGetDay(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetDay);
        }

        return EzDateTime.momentWithPTZ(momentInstance).date();
    }

    /**
     * @pulic @function
     * Returns the year of the provided moment
     * @param {moment} aMoment
     * @returns {number}
     */
    ezGetYear(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetYear);
        }

        return EzDateTime.momentWithPTZ(momentInstance).year();
    }

    /**
     * @public @function
     * Returns the provided hours as a decimal hours value.
     * @param {number} hours
     * @returns {number}
     */
    ezHoursToDecimalHours(hours) {
        if (!EzNumber.isNumber(hours)) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return hours;
    }

    /**
     * @public @function
     * Returns the provided minutes as a decimal hours value.
     * @param {number} minutes
     * @returns {number}
     */
    ezMinutesToDecimalHours(minutes) {
        if (!EzNumber.isNumber(minutes)) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return minutes / 60;
    }

    /**
     * @public @function
     * Returns the provided milliseconds as a decimal hours value.
     * @param {number} seconds
     * @returns {number}
     */
    ezSecondsToDecimalHours(seconds) {
        if (!EzNumber.isNumber(seconds)) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return seconds / 3600;
    }

    /**
     * @public @function
     * Returns the provided milliseconds as a decimal hour value.
     * @param {number} milliseconds
     * @returns {number}
     */
    ezMillisecondsToDecimalHours(milliseconds) {
        if (!EzNumber.isNumber(milliseconds)) {
            throw new EzBadParamException(
                'milliseconds',
                EzDateTime.ezInstance
                , EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return milliseconds / 3600000;
    }

    /**
     * @public @function
     * Returns the duration between the provided moment instances as decimal hours.
     * Provide the number of preferred deci+-malPoints. The default is 2.
     * @param {moment} momentInstance1
     * @param {moment} momentInstance2
     * @param {number} decimalPoints
     * @returns {number}
     */
    ezDurationBetweenAsDecimalHours(momentInstance1, momentInstance2, decimalPoints) {
        if (!EzNumber.isNumber(decimalPoints)) {
            decimalPoints = EzNumber.TWO;
        }

        let decimalHours = !EzObject.isValid(momentInstance1) || !EzObject.isValid(momentInstance2)
            ? EzNumber.ZERO
            : Number.parseFloat(
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours(
                    EzDateTime.ezInstance.ezMomentWithPTZ(momentInstance1)
                        .diff(EzDateTime.ezInstance.ezMomentWithPTZ(momentInstance2))));

        decimalHours = Math.round(decimalHours * (10 ^ decimalPoints)) / (10 ^ decimalPoints);

        return Number.parseFloat(decimalHours).toFixed(decimalPoints);
    }

    /**
     * @public @function
     * Returns the duration between the provided moment instances as decimal days.
     * @param {moment} aMoment1
     * @param {moment} aMoment2
     * @param {number} decimalPoints
     * @returns {number}
     */
    ezDurationBetweenAsDecimalDays(momentInstance1, momentInstance2, decimalPoints) {
        if (!EzNumber.isNumber(decimalPoints)) {
            decimalPoints = EzNumber.TWO;
        }

        const decimalHours = !EzObject.isValid(momentInstance1) || !EzObject.isValid(momentInstance2)
            ? EzNumber.ZERO
            : Number.parseFloat(
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours(
                    EzDateTime.momentWithPTZ(momentInstance1)
                        .diff(EzDateTime.momentWithPTZ(momentInstance2))));

        let decimalDays = decimalHours / EzNumber._24;

        decimalDays = Math.round(decimalDays * (EzNumber.TEN ^ decimalPoints)) / (EzNumber.TEN ^ decimalPoints);

        return Number.parseFloat(decimalDays).toFixed(decimalPoints);
    }

    /**
     * @public @function
     * Returns the momentjs duration instance for the provided momentjs moment instance.
     * @param {moment} aMoment
     * @returns {object}
     */
    ezDurationForMoment(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDurationForMoment);
        }

        return momentInstance.duration();
    }

    /**
     * @public @function
     * Returns the duration between the provided moment instances as decimal hours.
     * Provide the number of preferred decimalPoints. The default is 2.
     * @param {moment} momentInstance1
     * @param {moment} momentInstance2
     * @param {number} decimalPoints
     * @returns {number}
     */
    ezDurationBetweenAsHoursMinutes(momentInstance1, momentInstance2) {
        const millisecondsDuration = EzDateTime.momentWithPTZ(momentInstance1)
            .diff(EzDateTime.momentWithPTZ(momentInstance2));

        const hours = Math.trunc(millisecondsDuration / EzNumber.MILLISECONDS_IN_HOUR);

        const minutes = Math.round((millisecondsDuration - hours * EzNumber.MILLISECONDS_IN_HOUR) / EzNumber.MILLISECONDS_IN_MINUTE);

        const hoursStr = EzNumber.TEN > hours
            ? `0${hours}`
            : `${hours}`;

        return EzNumber.TEN > minutes
            ? `${hoursStr}h 0${minutes}m`
            : `${hoursStr}h ${minutes}m`;
    }

    /**
     * @static
     * @public @function
     * Determines if the provided momentInstanceA is the same as the provided momentInstanceB
     * Alias for: EzDateTime.isSame(momentInstanceA, momentInstanceB)
     * @param {moment} momentInstanceA
     * Will throw EzBadParamException if momentInstanceA is not a valid non-null moment instance
     * unless momentInstanceB is also null and in such a case true (meaning both are equally null) is returned.
     * Default: null
     * @param {undefined|null|moment|*} momentInstanceB
     * Can be undefined, null, a valid moment instance, or anything else.
     * Default: null
     * @returns {boolean}
     */
    static equals(momentInstanceA = null, momentInstanceB = null) {
        if (!EzObject.isValid(momentInstanceA)) {
            return !EzObject.isValid(momentInstanceB);
        }

        if (!EzObject.isValid(momentInstanceB)) {
            return false;
        }

        return EzDateTime.toIso(momentInstanceA) === EzDateTime.toIso(momentInstanceB);
    }

    /**
     * @public @function
     * Determines if the provided momentInstanceA is the same as the provided momentInstanceB
     * Alias for: EzDateTime.isSame(momentInstanceA, momentInstanceB)
     * @param {moment} momentInstanceA
     * Will throw EzBadParamException if momentInstanceA is not a valid non-null moment instance
     * unless momentInstanceB is also null and in such a case true (meaning both are equally null) is returned.
     * Default: null
     * @param {undefined|null|moment|*} momentInstanceB
     * Can be undefined, null, a valid moment instance, or anything else.
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.equals(momentInstanceA, momentInstanceB)
     */
    ezEquals = EzDateTime.equals;

    /**
     * @static
     * @public @function
     * Determines if the provided momentA is the same as the provided momentB
     * Alias for: EzDateTime.equals(momentInstanceA, momentInstanceB)
     * @param {moment} momentInstanceA
     * Will throw EzBadParamException if momentInstanceA is not a valid non-null moment instance
     * unless momentInstanceB is also null and in such a case true (meaning both are equally null) is returned.
     * Default: null
     * @param {undefined|null|moment|*} momentInstanceB
     * Can be undefined, null, a valid moment instance, or anything else.
     * Default: null
     * @returns {boolean}
     */
    static isSame(momentInstanceA = null, momentInstanceB = null) {
        return EzDateTime.equals(momentInstanceA, momentInstanceB);
    }

    /**
     * @static
     * @public @function
     * Determines if the provided momentInstanceToCheck is the same as or before the provided momentInstance
     * Will throw EzBadParamException if either momentInstanceToCheck or momentInstance is not a valid moment instance.
     * @param {moment} momentInstanceToCompare
     * @param {moment} compareToMomentInstance
     * @returns {boolean}
     */
    static isEqualOrBefore(momentInstanceToCompare, compareToMomentInstance) {
        if (!EzObject.isValid(momentInstanceToCompare)) {
            throw new EzBadParamException(
                'momentInstanceToCompare',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }
        if (!EzObject.isValid(compareToMomentInstance)) {
            throw new EzBadParamException(
                'compareToMomentInstance',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }

        return EzDateTime.equals(momentInstanceToCompare, compareToMomentInstance) ||
            momentInstanceToCompare.isBefore(compareToMomentInstance);
    }

    /**
     * @static
     * @public @function
     * Determines if the provided momentInstanceToCheck is before the provided momentInstance
     * Will throw EzBadParamException if either momentInstanceToCheck or momentInstance is not a valid moment instance.
     * @param {moment} momentInstanceToCompare
     * @param {moment} compareToMomentInstance
     * @returns {boolean}
     */
    static isBefore(momentInstanceToCompare, compareToMomentInstance) {
        if (!EzObject.isValid(momentInstanceToCompare)) {
            throw new EzBadParamException(
                'momentInstanceToCompare',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }
        if (!EzObject.isValid(compareToMomentInstance)) {
            throw new EzBadParamException(
                'compareToMomentInstance',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }

        return momentInstanceToCompare.isBefore(compareToMomentInstance);
    }

    /**
     * @static
     * @public @function
     * Determines if the provided momentInstanceToCheck is the same as or after the provided momentInstance
     * Will throw EzBadParamException if either momentInstanceToCheck or momentInstance is not a valid non-null moment instance.
     * @param {moment} momentInstanceToCompare
     * @param {moment} compareToMomentInstance
     * @returns {boolean}
     */
    static isEqualOrAfter(momentInstanceToCompare, compareToMomentInstance) {
        if (!EzObject.isValid(momentInstanceToCompare)) {
            throw new EzBadParamException(
                'momentInstanceToCompare',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }
        if (!EzObject.isValid(compareToMomentInstance)) {
            throw new EzBadParamException(
                'compareToMomentInstance',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }

        return EzDateTime.equals(momentInstanceToCompare, compareToMomentInstance) ||
            momentInstanceToCompare.isAfter(compareToMomentInstance);
    }

    /**
     * @static
     * @public @function
     * Determines if the provided momentInstanceToCheck is after the provided momentInstance
     * Will throw EzBadParamException if either momentInstanceToCheck or momentInstance is not a valid moment instance.
     * @param {moment} momentInstanceToCompare
     * @param {moment} compareToMomentInstance
     * @returns {boolean}
     */
    static isAfter(momentInstanceToCompare, compareToMomentInstance) {
        if (!EzObject.isValid(momentInstanceToCompare)) {
            throw new EzBadParamException(
                { momentInstanceToCompare },
                EzDateTime,
                EzDateTime.isAfter);
        }
        if (!EzObject.isValid(compareToMomentInstance)) {
            throw new EzBadParamException(
                { compareToMomentInstance },
                EzDateTime,
                EzDateTime.isAfter);
        }

        return momentInstanceToCompare.isAfter(compareToMomentInstance);
    }

    /**
     * @protected @function
     * Handles the onSelectedEmployerReady event from EzClockerContext
     */
    ezHandleOnSelectedEmployerReady() {
        if (EzBoolean.isFalse(EzDateTime.ezLoadedPreferredTimeZoneOption)) {
            EzDateTime.ezLoadPreferredTimezoneOption();
        }
    }

    /**
     * @protected @function
     * Handles the OnUserContextReady event from EzClockerContext
     */
    ezHandleOnUserContextReady() {
        EzDateTime.ezInstance.ezLoadAuthenticatedUserPreferredLocaleOption();
    }

    /**
     * @static
     * @public @function
     * Determines if the value of the HTML element with the provided id is a valid moment string that can be parsed to a moment instance.
     * Will return false if the element is not found or if the element's value is not a valid moment string.
     * @param {string} [elementId= EzString.EMPTY]
     * @returns {boolean}
     */
    static isValidMomentFromElementId(elementId = EzString.EMPTY) {
        const element = EzUx.$id(elementId);

        return EzObject.isValid(element) && moment(element.val()).isValid();
    }

    /**
     * @public @function
     * @param {string} elementId
     * @returns {boolean}
     */
    ezIsValidMomentFromElementId = EzDateTime.isValidMomentFromElementId;

    /**
     * @public @function
     * Returns a moment for the first day of week based upon what is indicated as the firstDayOfWeek
     * @param {moment} momentInWeek
     * @param {number} firstDayOfWeek
     * @returns {moment}
     */
    ezCalculateFirstDayOfWeekFromMoment(momentInWeek, firstDayOfWeek = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(momentInWeek)) {
            throw new EzBadParamException(
                'momentInWeek',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCalculateFirstDayOfWeekFromMoment);
        }
        if (!EzNumber.isNumber(firstDayOfWeek)) {
            throw new EzBadParamException(
                'firstDayOfWeek',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCalculateFirstDayOfWeekFromMoment);
        }

        const dayOfPeriodStart = momentInWeek.day();

        if (EzNumber.ZERO < dayOfPeriodStart - firstDayOfWeek) {
            return EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay(momentInWeek)
                .subtract(
                    dayOfPeriodStart - firstDayOfWeek,
                    'days');
        }

        if (EzNumber.ZERO === dayOfPeriodStart - firstDayOfWeek) {
            return EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay(momentInWeek);
        }

        return EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay(momentInWeek)
            .subtract(
                EzNumber.SEVEN + (dayOfPeriodStart - firstDayOfWeek),
                'days');
    }

    /**
     * @static
     * @public @function
     * Sets the hours of the provided aMoment value.
     * @param {moment} aMoment
     * @param {number} [hours=EzNumber.ZERO]
     * Positive number from 0 to 23.
     * @returns {moment}
     */
    static setHours(aMoment, hours = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setHours);
        }
        if (EzNumber.ZERO > hours || EzNumber._23 < hours) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setHours,
                'The hours param must be a positive number from 0 to 23.');
        }

        return EzDateTime.momentWithPTZ(aMoment)
            .hours(
                EzNumber.positiveNumberOrZero(hours));
    }

    /**
     * @static
     * @public @function
     * Sets the minutes of the provided aMoment value.
     * @param {moment} aMoment
     * @param {number} [minutes=EzNumber.ZERO]
     * Positive number from 0 to 59.
     * @returns {moment}
     */
    static setMinutes(aMoment, minutes = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMinutes);
        }
        if (EzNumber.ZERO > minutes || EzNumber._59 < minutes) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMinutes,
                'The minutes param must be a positive number from 0 to 59.');
        }

        return EzDateTime.momentWithPTZ(aMoment)
            .minutes(
                EzNumber.positiveNumberOrZero(minutes));
    }

    /**
     * @static
     * @public @function
     * Sets the seconds of the provided aMoment value.
     * @param {moment} aMoment
     * @param {number} [seconds=EzNumber.ZERO]
     * Positive number from 0 to 59.
     * @returns {moment}
     */
    static setSeconds(aMoment, seconds = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMinutes);
        }
        if (EzNumber.ZERO > minutes || EzNumber._59 < minutes) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setSeconds,
                'The seconds param must be a positive number from 0 to 59.');
        }

        return EzDateTime.momentWithPTZ(aMoment)
            .seconds(
                EzNumber.positiveNumberOrZero(seconds));
    }

    /**
     * @static
     * @public @function
     * Sets the milliseconds of the provided aMoment value.
     * @param {moment} aMoment
     * @param {number} [milliseconds=EzNumber.ZERO]
     * Positive number from 0 to 999.
     * @returns {moment}
     */
    static setMilliseconds(aMoment, milliseconds = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMilliseconds);
        }
        if (EzNumber.ZERO > milliseconds || EzNumber._999 < milliseconds) {
            throw new EzBadParamException(
                'milliseconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMilliseconds,
                'The milliseconds param must be a positive number from 0 to 999.');
        }

        return EzDateTime.momentWithPTZ(aMoment)
            .milliseconds(
                EzNumber.positiveNumberOrZero(milliseconds));
    }

    /**
     * @static
     * @public @function
     * Sets the time of the provided aMoment value using the provided hours, minutes, seconds and milliseconds values.
     * Will throw EzBadParamException if aMoment is not a valid moment instance or if any of the provided hours, minutes, seconds or milliseconds values are
     * out of their acceptable ranges.
     * @param {moment} aMoment
     * @param {number} [hours=EzNumber.ZERO]
     * Positive number from 0 to 23.
     * @param {number} [minutes=EzNumber.ZERO]
     * Positive number from 0 to 59.
     * @param {number} [seconds=EzNumber.ZERO]
     * Positive number from 0 to 59.
     * @param {number} [milliseconds=EzNumber.ZERO]
     * Positive number from 0 to 999.
     * @param {string} [timeZoneId=EzDateTime.ezActiveTimeZoneId]
     * @return {moment}
     */
    static setTime(aMoment, hours = EzNumber.ZERO, minutes = EzNumber.ZERO, seconds = EzNumber.ZERO, milliseconds = EzNumber.ZERO,
        timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMilliseconds);
        }
        if (EzNumber.ZERO > hours || EzNumber._23 < hours) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setHours,
                'The hours param must be a positive number from 0 to 23.');
        }
        if (EzNumber.ZERO > minutes || EzNumber._59 < minutes) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMinutes,
                'The minutes param must be a positive number from 0 to 59.');
        }
        if (EzNumber.ZERO > minutes || EzNumber._59 < minutes) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setSeconds,
                'The seconds param must be a positive number from 0 to 59.');
        }
        if (EzNumber.ZERO > milliseconds || EzNumber._999 < milliseconds) {
            throw new EzBadParamException(
                'milliseconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMilliseconds,
                'The milliseconds param must be a positive number from 0 to 999.');
        }

        return EzDateTime.withZone(aMoment, timeZoneId)
            .hours(
                EzNumber.positiveNumberOrZero(hours))
            .minutes(
                EzNumber.positiveNumberOrZero(minutes))
            .seconds(
                EzNumber.positiveNumberOrZero(seconds))
            .milliseconds(
                EzNumber.positiveNumberOrZero(milliseconds));
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! IMPORTANT: Enter ONLY class initialization or EzApi registration code in this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Returns the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezDateTime';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns an object of event names triggered by EzDateTime
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzDateTime_Ready',
            onActiveTimeZoneLoaded: 'ezOn_EzDateTime_ActiveTimeZone_Loaded',
            onActiveTimeZoneChanged: 'ezOn_EzDateTime_ActiveTimeZone_Changed',
            onSupportedTimeZonesReady: 'ezOn_EzDateTime_SupportedTimeZones_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the reference to EzDateTime's singleton instance registered with globalThis.ezApi.
     * @type {EzDateTime}
     */
    static #ezInstance = globalThis.ezApi?.ezclocker?.[EzDateTime.ezApiName]
        ? globalThis.ezApi.ezclocker[EzDateTime.ezApiName]
        : null;
    /**
     * @static
     * @public @getter @property
     * Returns the singleton instance of EzDateTime registered with EzApi (if available yet)
     * @returns {EzDateTime}
     */
    static get ezInstance() {
        return EzDateTime.#ezInstance;
    }
    /**
     * @static
     * @public @getter @property
     * Returns the singleton instance of EzDateTime registered with EzApi (if available yet)
     * @param {EzDateTime} instance
     */
    static set ezInstance(instance) {
        if (null != EzDateTime.#ezInstance) {
            throw new Error('EzDateTime\'s singleton instance is already reigstered with globalThis.ezApi.');
        }

        EzDateTime.#ezInstance = instance;

        if (null != EzDateTime.#ezInstance) {
            EzDateTime.ezApiRegistrationState = EzRegistrationState.REGISTERED;
        }
    }

    /**
     * @static
     * @private @field
     * @type {string}
     */
    static #ezApiRegistrationState = globalThis.ezApi?.ezclocker?.[EzDateTime.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Returns the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzDateTime.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzDateTime.#ezApiRegistrationState = ezApiRegistrationState;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets if all necessary dependences are ready and therefore it is ok for this class to
     * register it's singleton instance with globalThis.ezApi.
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzDateTime.ezApiRegistrationState && globalThis?.ezApi?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzDateTime.ezInstance && EzRegistrationState.REGISTERED === EzDateTime.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @function
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzDateTime.#ezCanRegister && !EzDateTime.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzDateTime, EzDateTime.ezApiName);
        }

        return EzDateTime.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzDateTime.ezApiName
     *     2) Property getter EzDateTime.ezEventNames
     *     3) Property getter EzDateTime.ezInstance
     *     4) Property setter EzDateTime.ezInstance
     *     5) Property getter EzDateTime.ezApiRegistrationState
     *     6) Property setter EzDateTime.ezApiRegistrationState
     *     7) Property getter EzDateTime.#ezCanRegister
     *     8) Property getter EzDateTime.#ezIsRegistered
     *     9) Method EzDateTime.#ezRegistrator()
     */
    static {
        if (!EzDateTime.#ezIsRegistered) {
            EzDateTime.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzDateTime.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzDateTime.ezOnEzApiReadyEventName,
                    EzDateTime.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzEventEngine.ezEventNames.onReady,
                    EzDateTime.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! IMPORTANT: Do NOT EMTER ANY non-initialization or non-EzApi registration code after this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}

export const ezDateTime = () => globalThis?.ezApi?.ezclocker?.ezDateTime;
